import React, { Component, useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Activity, Network, Camera, X, Package, Store, MapPin, Phone, Globe, Target, TrendingUp, BarChart3, Layers, Zap, Shield, Users, Map as MapIcon } from 'lucide-react';
import { GoogleGenAI, LiveServerMessage, Modality, Type } from '@google/genai';
import { SoundManager } from '../utils/SoundManager';
import { MemoryManager } from '../utils/MemoryManager';
import HologramDashboard from './HologramDashboard';
import WeeklyChartModal, { type TrendsData as ChartTrendsData } from './WeeklyChartModal';
import { GOOGLE_MAPS_API_KEY } from '../utils/mapConfig';
import { LunaFace } from './LunaFace';

// ─── Lazy-loaded map components — separate async chunk avoids Rollup scope hoisting conflicts ──
const LazyMiniMap = React.lazy(() => import('./LunaMapComponents').then(m => ({ default: m.MiniMap })));
const LazyMultiStoreMap = React.lazy(() => import('./LunaMapComponents').then(m => ({ default: m.MultiStoreMap })));


const SpeechBubble = ({ message, isVisible }: { message: string, isVisible: boolean }) => {
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 10, scale: 0.9, x: 20 }}
          animate={{ opacity: 1, y: 0, scale: 1, x: 0 }}
          exit={{ opacity: 0, y: 10, scale: 0.9, x: 20 }}
          transition={{ duration: 0.3, type: "spring", stiffness: 200, damping: 20 }}
          className="absolute -top-20 -right-16 z-30 max-w-[200px] pointer-events-none"
        >
          <div className="relative bg-black/40 backdrop-blur-xl border border-white/20 text-white text-xs font-bold tracking-wide px-5 py-3 rounded-2xl rounded-bl-none shadow-[0_8px_32px_rgba(0,0,0,0.5)]">
            {message}
            <svg className="absolute -bottom-3 left-4 w-5 h-5 text-white/20" viewBox="0 0 10 10">
              <path d="M0 0 L10 0 L0 10 Z" fill="currentColor" />
            </svg>
            <svg className="absolute -bottom-[10px] left-[17px] w-4 h-4 text-black/40 backdrop-blur-xl" viewBox="0 0 10 10">
              <path d="M0 0 L10 0 L0 10 Z" fill="currentColor" />
            </svg>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export { LunaFace } from './LunaFace';

// ─── Neural Action Button ────────────────────────────────────────────────────
// Reusable haptic action button with ripple burst on click.
const NeuralActionButton = ({ label, onClick }: { label: string; onClick: () => void }) => {
  const [burst, setBurst] = useState(false);

  const handleClick = () => {
    setBurst(true);
    setTimeout(() => setBurst(false), 600);
    SoundManager.play('ui_click');
    onClick();
  };

  return (
    <div className="relative mt-4 w-full">
      {/* Ripple burst overlay */}
      <AnimatePresence>
        {burst && (
          <motion.div
            key="burst"
            initial={{ opacity: 0.8, scale: 0.6 }}
            animate={{ opacity: 0, scale: 2.2 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.55, ease: "easeOut" }}
            className="absolute inset-0 rounded-lg border-2 border-cyan-400 pointer-events-none"
          />
        )}
      </AnimatePresence>
      <motion.button
        whileTap={{ scale: 0.95 }}
        whileHover={{ scale: 1.02 }}
        onClick={handleClick}
        className="relative w-full py-2.5 md:py-3 bg-gradient-to-r from-cyan-500 via-teal-500 to-emerald-400 text-slate-950 rounded-xl text-xs font-black uppercase tracking-wider transition-all shadow-[0_0_20px_rgba(6,182,212,0.4)] hover:shadow-[0_0_30px_rgba(6,182,212,0.8)] flex items-center justify-center gap-2 overflow-hidden cursor-pointer active:scale-95"
      >
        {/* Shimmer sweep */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 pointer-events-none"
          initial={{ x: '-100%' }}
          whileHover={{ x: '200%' }}
          transition={{ duration: 0.6, ease: "easeInOut" }}
        />
        <motion.span
          className="w-2 h-2 rounded-full bg-slate-950 shadow-[0_0_6px_rgba(0,0,0,0.8)]"
          animate={{ opacity: [1, 0.4, 1] }}
          transition={{ duration: 1.2, repeat: Infinity }}
        />
        <span>{label} →</span>
      </motion.button>
      {/* "Neural Link Activated" confirmation flash */}
      <AnimatePresence>
        {burst && (
          <motion.div
            key="nl-label"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.25 }}
            className="absolute -bottom-5 left-0 right-0 text-center text-[9px] font-mono text-cyan-400 tracking-widest uppercase pointer-events-none"
          >
            ◈ Neural Link Activated
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Product Card Component (extracted to isolate hooks per-item) ──────────
const ProductCard = ({ item, index, actionLabel, onActionClick, onHover }: {
  key?: number;
  item: any;
  index: number;
  actionLabel: string;
  onActionClick?: (item: any) => void;
  onHover?: (ctx: string | null) => void;
}) => {
  const [imgError, setImgError] = useState(false);

  const productName = item.name || item.productName || item.title || 'Product';
  const isPriceHidden = item.priceHidden === true || item.price === undefined || item.price === null;
  const productPrice = isPriceHidden ? null : (item.price || item.sellingPrice || item.cost || item.unitPrice || null);
  const productStock = item.stock || item.stockCount || item.quantity || item.qty || 'N/A';
  const imageUrl = item.imageUrl || item.image || item.productImage || item.photo || item.thumbnail || null;
  const storeName = item.Store?.name || item.storeName || item.store || 'Kangavena Network';
  const isAvailable = productStock !== 'N/A' && Number(productStock) > 0;
  const firstLetter = String(productName).charAt(0).toUpperCase();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}
      className="bg-gradient-to-br from-slate-900/80 to-black/60 border border-cyan-500/20 rounded-2xl overflow-hidden hover:border-cyan-500/50 hover:shadow-[0_0_30px_rgba(6,182,212,0.2)] hover:-translate-y-1 transition-all duration-300 group flex flex-col h-full"
      onMouseEnter={() => onHover?.(productName)}
      onMouseLeave={() => onHover?.(null)}
    >
      {/* Product Image or Glassmorphism Fallback */}
      <div className="relative w-full h-40 md:h-48 bg-gradient-to-br from-cyan-950/40 to-purple-950/30 overflow-hidden flex-shrink-0">
        {imageUrl && !imgError ? (
          <img
            src={imageUrl}
            alt={productName}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-cyan-500/10 via-purple-500/10 to-cyan-500/10 backdrop-blur-sm">
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-gradient-to-br from-cyan-400/20 via-fuchsia-400/20 to-cyan-400/20 border border-cyan-400/30 flex items-center justify-center shadow-[0_0_30px_rgba(6,182,212,0.3)] group-hover:shadow-[0_0_40px_rgba(6,182,212,0.5)] transition-shadow duration-300">
              <span className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-br from-cyan-300 via-fuchsia-300 to-cyan-300 drop-shadow-[0_0_10px_rgba(6,182,212,0.8)]">{firstLetter}</span>
            </div>
            <span className="mt-2 text-[10px] font-mono text-cyan-400/60 uppercase tracking-widest">{storeName}</span>
          </div>
        )}
        {/* Stock Badge Overlay */}
        <div className={`absolute top-3 right-3 px-2.5 py-1 rounded-full text-[10px] font-mono font-bold uppercase tracking-wider ${isAvailable ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
          {isAvailable ? `Stock: ${productStock}` : 'Out of Stock'}
        </div>
      </div>

      {/* Product Details */}
      <div className="flex-1 p-4 md:p-5 flex flex-col gap-3">
        <h3 className="text-base md:text-lg font-bold text-white group-hover:text-cyan-100 transition-colors leading-snug line-clamp-2">{productName}</h3>

        <div className="flex items-center justify-between gap-2">
          {isPriceHidden ? (
            <span className="text-sm md:text-base font-bold text-amber-400/80 flex items-center gap-1.5">
              🔒 Contact Store for Price
            </span>
          ) : (
            <span className="text-lg md:text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-fuchsia-400">
              Rs. {typeof productPrice === 'number' ? productPrice.toLocaleString() : productPrice}
            </span>
          )}
          <div className="flex items-center gap-1 text-[10px] text-slate-400">
            <Store size={10} className="text-fuchsia-400" />
            <span className="truncate max-w-[80px]">{storeName}</span>
          </div>
        </div>

        {onActionClick && (
          <div className="mt-auto pt-3">
            <NeuralActionButton
              label={isAvailable ? 'View Details' : 'Notify Me'}
              onClick={() => onActionClick(item)}
            />
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ─── Map Error Boundary — catches Leaflet minification crashes ──────────────
// Wraps each <MapContainer> individually so a Leaflet crash doesn't propagate
// to the LunaErrorBoundary and shut down the entire hologram.
interface MapErrorBoundaryProps { children: React.ReactNode; fallbackText?: string }
interface MapErrorBoundaryState { hasError: boolean }
class MapErrorBoundary extends Component<MapErrorBoundaryProps, MapErrorBoundaryState> {
  state: MapErrorBoundaryState = { hasError: false };
  constructor(props: MapErrorBoundaryProps) {
    super(props);
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) {
      return (
        <div className="w-full h-[150px] bg-slate-900/50 border border-red-500/20 rounded-xl flex items-center justify-center p-4">
          <p className="text-[10px] font-mono text-slate-400 text-center leading-relaxed">
            {(this as any).props.fallbackText || 'Luna cannot find location cause owner has not setup the location on map'}
          </p>
        </div>
      );
    }
    return (this as any).props.children;
  }
}

// ─── Product Detail View (O2O Deep Dive) ───────────────────────────────────
const ProductDetailView = ({
  product,
  allProducts,
  onBack,
  onActionClick,
  onHover
}: {
  key?: string;
  product: any;
  allProducts: any[];
  onBack: () => void;
  onActionClick?: (item: any) => void;
  onHover?: (ctx: string | null) => void;
}) => {
  const [imgError, setImgError] = useState(false);

  const productName = product.name || product.productName || product.title || 'Product';
  const isPriceHidden = product.priceHidden === true || product.price === undefined || product.price === null;
  const productPrice = isPriceHidden ? null : (product.price || product.sellingPrice || product.cost || product.unitPrice || null);
  const productStock = product.stock || product.stockCount || product.quantity || product.qty || 'N/A';
  const imageUrl = product.imageUrl || product.image || product.productImage || product.photo || product.thumbnail || null;
  const store = product.Store || {};
  const storeName = store.name || product.storeName || product.store || 'Kangavena Network';
  const storeAddress = store.address || store.location || 'Visit store for address';
  const storePhone = store.phone || store.contact || null;
  const storeLat = store.latitude;
  const storeLng = store.longitude;
  
  // Strict Null-Safety for Leaflet
  const validLat = Number(storeLat);
  const validLng = Number(storeLng);
  const hasValidLocation = storeLat != null && storeLng != null && isFinite(validLat) && isFinite(validLng);

  const isAvailable = productStock !== 'N/A' && Number(productStock) > 0;
  const firstLetter = String(productName).charAt(0).toUpperCase();

  // Get recommendations: other products from same store, excluding current
  const recommendations = allProducts
    .filter(p => p.id !== product.id && (p.Store?.id === store.id || p.storeId === store.id || p.store === storeName))
    .slice(0, 4);

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="w-full flex flex-col gap-5"
    >
      {/* ── Back Button ── */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors text-sm font-medium group"
      >
        <motion.span
          className="inline-block"
          initial={{ x: 0 }}
          whileHover={{ x: -4 }}
          transition={{ duration: 0.2 }}
        >
          ←
        </motion.span>
        <span className="uppercase tracking-wider text-[11px]">Back to Products</span>
      </button>

      {/* ── Main Product Hero ── */}
      <div className="bg-gradient-to-br from-slate-900/90 to-black/70 border border-cyan-500/30 rounded-2xl overflow-hidden">
        <div className="flex flex-col md:flex-row">
          {/* Product Image / Fallback */}
          <div className="relative w-full md:w-1/2 h-56 md:h-72 bg-gradient-to-br from-cyan-950/50 to-purple-950/40 flex-shrink-0">
            {imageUrl && !imgError ? (
              <img
                src={imageUrl}
                alt={productName}
                className="w-full h-full object-cover"
                onError={() => setImgError(true)}
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center">
                <div className="w-32 h-32 rounded-3xl bg-gradient-to-br from-cyan-400/20 via-fuchsia-400/20 to-cyan-400/20 border border-cyan-400/40 flex items-center justify-center shadow-[0_0_50px_rgba(6,182,212,0.4)]">
                  <span className="text-6xl font-black text-transparent bg-clip-text bg-gradient-to-br from-cyan-300 via-fuchsia-300 to-cyan-300 drop-shadow-[0_0_15px_rgba(6,182,212,1)]">
                    {firstLetter}
                  </span>
                </div>
                <span className="mt-4 text-[11px] font-mono text-cyan-400/70 uppercase tracking-widest">{storeName}</span>
              </div>
            )}
            {/* Stock Badge */}
            <div className={`absolute top-4 right-4 px-3 py-1.5 rounded-full text-[11px] font-mono font-bold uppercase tracking-wider ${isAvailable ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' : 'bg-red-500/20 text-red-400 border border-red-500/40'}`}>
              {isAvailable ? `In Stock: ${productStock}` : 'Out of Stock'}
            </div>
          </div>

          {/* Product Info & O2O Actions */}
          <div className="flex-1 p-5 md:p-6 flex flex-col">
            <h2 className="text-2xl md:text-3xl font-black text-white leading-tight mb-2">{productName}</h2>
            {isPriceHidden ? (
              <p className="text-xl font-bold text-amber-400/80 mb-6 flex items-center gap-2">
                🔒 Contact Store for Pricing
              </p>
            ) : (
              <p className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-fuchsia-400 mb-6">
                Rs. {typeof productPrice === 'number' ? productPrice.toLocaleString() : productPrice}
              </p>
            )}

            {/* Store Details */}
            <div className="space-y-3 mb-6">
              <div className="flex items-start gap-3">
                <Store size={18} className="text-fuchsia-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-cyan-100">{storeName}</p>
                  <p className="text-xs text-slate-400 mt-0.5 flex items-start gap-1.5">
                    <MapPin size={12} className="mt-0.5 shrink-0 text-slate-500" />
                    {storeAddress}
                  </p>
                </div>
              </div>
              {storePhone && (
                <div className="flex items-center gap-3">
                  <Phone size={18} className="text-emerald-400 shrink-0" />
                  <span className="text-sm text-cyan-100">{storePhone}</span>
                </div>
              )}
            </div>

            {/* O2O CTA Buttons */}
            <div className="mt-auto flex flex-col sm:flex-row gap-3">
              {storePhone && (
                <a
                  href={`tel:${storePhone}`}
                  className="flex-1 py-3 px-4 bg-gradient-to-r from-emerald-500/20 to-emerald-600/10 border border-emerald-500/50 rounded-xl text-emerald-400 font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-emerald-500/30 hover:shadow-[0_0_20px_rgba(16,185,129,0.3)] transition-all"
                >
                  <Phone size={16} />
                  Call Store
                </a>
              )}
              {storeLat && storeLng && (
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${storeLat},${storeLng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 py-3 px-4 bg-gradient-to-r from-cyan-500/20 to-fuchsia-500/10 border border-cyan-500/50 rounded-xl text-cyan-400 font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-cyan-500/30 hover:shadow-[0_0_20px_rgba(6,182,212,0.3)] transition-all"
                >
                  <MapPin size={16} />
                  Get Directions
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Embedded Mini Map ── */}
      {hasValidLocation ? (
        <div className="w-full">
          <p className="text-[11px] font-mono text-cyan-400/60 uppercase tracking-widest mb-2 flex items-center gap-2">
            <MapPin size={12} />
            Store Location
          </p>
          <div className="w-full h-[180px] rounded-xl overflow-hidden border border-cyan-500/30 shadow-[0_0_20px_rgba(6,182,212,0.15)]">
            <MapErrorBoundary>
              <React.Suspense fallback={<div className="w-full h-full bg-slate-900/50 rounded-xl" />}>
                <LazyMiniMap lat={validLat} lng={validLng} storeName={storeName} />
              </React.Suspense>
            </MapErrorBoundary>
          </div>
        </div>
      ) : (
        <div className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl p-4 flex items-center justify-center">
          <p className="text-[10px] font-mono text-slate-400 text-center leading-relaxed">
            Luna cannot find location cause owner has not setup the location on map
          </p>
        </div>
      )}

      {/* ── Recommendations Loop ── */}
      {recommendations.length > 0 && (
        <div className="mt-2">
          <p className="text-[11px] font-mono text-fuchsia-400/80 uppercase tracking-widest mb-3 flex items-center gap-2">
            <Activity size={12} />
            More from {storeName}
          </p>
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-fuchsia-500/30 scrollbar-track-transparent">
            {recommendations.map((rec, idx) => {
              const recImage = rec.imageUrl || rec.image || rec.productImage || rec.photo || rec.thumbnail || null;
              const recLetter = String(rec.name || 'P').charAt(0).toUpperCase();
              const recPrice = rec.price || rec.sellingPrice || 'N/A';
              return (
                <motion.button
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  onClick={() => onActionClick?.({ ...rec, action: 'SELECT_PRODUCT' })}
                  onMouseEnter={() => onHover?.(rec.name)}
                  onMouseLeave={() => onHover?.(null)}
                  className="flex-shrink-0 w-28 bg-slate-900/60 border border-fuchsia-500/20 rounded-xl overflow-hidden hover:border-fuchsia-500/50 hover:shadow-[0_0_20px_rgba(192,38,211,0.2)] transition-all text-left group"
                >
                  {/* Mini Image */}
                  <div className="relative w-full h-20 bg-gradient-to-br from-fuchsia-950/30 to-cyan-950/30">
                    {recImage ? (
                      <img src={recImage} alt={rec.name} className="w-full h-full object-cover" onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-br from-fuchsia-300 to-cyan-300">{recLetter}</span>
                      </div>
                    )}
                  </div>
                  <div className="p-2.5">
                    <p className="text-[10px] font-medium text-cyan-100 truncate">{rec.name}</p>
                    <p className="text-[9px] text-fuchsia-400 font-mono mt-0.5">Rs.{recPrice}</p>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      )}
    </motion.div>
  );
};

// ─── Action label helper ──────────────────────────────────────────────────────
const getActionLabel = (action: string): string => {
  if (action === 'SHOW_COURSES') return 'Select Course';
  if (action === 'SHOW_PRODUCTS') return 'Visit Store';
  if (action === 'SHOW_FEATURES') return 'Explore Feature';
  if (action === 'SHOW_MAP') return 'Get Directions';
  return 'Select';
};

const HologramDisplay = ({ action, data, role, onClose, onBack, onActionClick, onHover }: { action: string, data: any[], role: string, onClose: () => void, onBack?: () => void, onActionClick?: (item: any) => void, onHover?: (ctx: string | null) => void }) => {
  const isGodModeStats = role === 'admin' && action === 'SHOW_STATS';
  const actionLabel = getActionLabel(action);

  // ── Grid ↔ Detail View State ──────────────────────────────────────────────
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null);

  // Handle product selection (grid → detail) and recommendation clicks.
  // Deliberately does NOT call onActionClick — Luna must not speak on passive product browsing.
  // Premium audio feedback via SoundManager replaces verbal AI chatter.
  const handleSelectProduct = (item: any) => {
    setSelectedProduct(item);
    SoundManager.play('product_select');
  };

  // Reset detail view when data changes
  useEffect(() => {
    setSelectedProduct(null);
  }, [data, action]);

  // Fix grey tiles: force map to recalculate after hologram expands
  useEffect(() => {
    if (action === 'SHOW_MAP') {
      const timer = setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [action]);

  // SHOW_ANALYTICS derived state — handles both flat [{title,value,trend}] and legacy {metrics,chartData}
  const analyticsKPIs: { title: string; value: string; trend?: string }[] = (() => {
    if (action !== 'SHOW_ANALYTICS') return [];
    // Flat KPI array (preferred schema) — exclude chartData rows (have label/revenue/sales but no title/value)
    if (Array.isArray(data) && data.length > 0 && data[0]?.title !== undefined) {
      return data
        .filter((item: any) => item?.title !== undefined && item?.value !== undefined)
        .map((item: any) => ({ ...item, title: String(item.title ?? ''), value: String(item.value ?? '') })) as { title: string; value: string; trend?: string }[];
    }
    // Legacy {metrics: {...}} shape — convert to flat array
    const metrics = data[0]?.metrics || {};
    return Object.entries(metrics).map(([k, v]) => ({ title: k, value: String(v), trend: undefined }));
  })();
  // Find chartData — may be inside any item, or be a top-level array with label/month keys
  const analyticsChart: { month: string; sales: number; revenue: number }[] = (() => {
    if (action !== 'SHOW_ANALYTICS') return [];
    // Look for chartData nested in any data item
    for (const item of data) {
      if (Array.isArray(item?.chartData) && item.chartData.length > 0) {
        return item.chartData.map((d: any) => ({
          month: d.month || d.label || d.name || '?',
          revenue: Number(d.revenue ?? d.value ?? 0),
          sales: Number(d.sales ?? d.count ?? 0),
        }));
      }
    }
    // chartData sent as separate array items alongside KPIs (label field)
    const chartItems = data.filter((d: any) => d.label !== undefined && (d.revenue !== undefined || d.sales !== undefined));
    if (chartItems.length > 0) {
      return chartItems.map((d: any) => ({
        month: d.label || d.month || '?',
        revenue: Number(d.revenue ?? 0),
        sales: Number(d.sales ?? 0),
      }));
    }
    return [];
  })();
  const CHART_H_PX = 128; // fixed pixel height of chart area
  const maxRevenue = Math.max(...analyticsChart.map((d: any) => d.revenue), 1);
  const maxSales = Math.max(...analyticsChart.map((d: any) => d.sales), 1);
  const analyticsKPIIcon = (title: string) => {
    const k = (title ?? '').toLowerCase();
    if (k.includes('revenue') || k.includes('income')) return '💰';
    if (k.includes('tenant')) return '🏢';
    if (k.includes('store')) return '🏪';
    if (k.includes('sale') || k.includes('order')) return '🛒';
    if (k.includes('health') || k.includes('uptime')) return '💚';
    if (k.includes('user') || k.includes('customer')) return '👥';
    return '📊';
  };
  const analyticsTrendColor = (trend?: string) => {
    if (!trend) return 'text-slate-400';
    const t = (trend ?? '').toLowerCase();
    if (t.startsWith('+') || t.includes('up') || t.includes('growth')) return 'text-emerald-400';
    if (t.startsWith('-') || t.includes('down') || t.includes('drop')) return 'text-red-400';
    return 'text-slate-400';
  };
  // Deterministic mock monthly bars (7 bars) used when LLM sends no chartData
  const mockBars = [55, 70, 45, 85, 60, 90, 75];
  const mockMonths = ['Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr'];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ type: "spring", stiffness: 200, damping: 20 }}
        className="w-full max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 box-border overflow-x-hidden z-10 flex flex-col items-center justify-center pointer-events-auto"
      >
        <div className="w-full max-h-[92dvh] h-[92dvh] bg-[#050B15]/90 backdrop-blur-3xl border border-cyan-500/40 rounded-3xl shadow-[0_0_120px_rgba(6,182,212,0.25)] flex flex-col overflow-hidden relative box-border">
          
          {/* ── Sci-Fi Year 2100 Laser Corner Brackets ── */}
          <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-cyan-400 z-30 pointer-events-none shadow-[0_0_15px_rgba(6,182,212,1)]" />
          <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-cyan-400 z-30 pointer-events-none shadow-[0_0_15px_rgba(6,182,212,1)]" />
          <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-cyan-400 z-30 pointer-events-none shadow-[0_0_15px_rgba(6,182,212,1)]" />
          <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-cyan-400 z-30 pointer-events-none shadow-[0_0_15px_rgba(6,182,212,1)]" />

          {/* Holographic Matrix & Scanline Grid Overlay */}
          <div className="absolute inset-0 pointer-events-none z-0">
             <div className="w-full h-full bg-[radial-gradient(circle_at_center,rgba(6,182,212,0.14)_0%,transparent_75%)]" />
             <div className="w-full h-full bg-[linear-gradient(to_bottom,transparent_0%,rgba(6,182,212,0.04)_50%,transparent_100%)] bg-[length:100%_8px]" />
             <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-cyan-500/40 to-transparent absolute top-[25%]" />
             <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-cyan-500/40 to-transparent absolute top-[75%]" />
          </div>

          {/* ── Year 2100 Hologram Telemetry Header ── */}
          <div className="flex justify-between items-center px-4 md:px-7 py-4 border-b border-cyan-500/30 bg-gradient-to-r from-[#070E1A]/95 via-[#0B182E]/90 to-[#070E1A]/95 flex-wrap gap-4 shrink-0 backdrop-blur-2xl relative z-20">
            <div className="flex items-center gap-3.5">
              <span className="relative flex h-3.5 w-3.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-cyan-500 shadow-[0_0_12px_rgba(6,182,212,1)]"></span>
              </span>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono text-cyan-300 font-bold uppercase tracking-widest bg-cyan-500/15 border border-cyan-400/40 px-2.5 py-0.5 rounded-md shadow-[0_0_10px_rgba(6,182,212,0.2)]">QUANTUM NEURAL HUD v2100</span>
                  <span className="text-[10px] font-mono text-emerald-400 font-bold uppercase tracking-widest hidden sm:inline-block">NEURAL LINK: OPTIMAL</span>
                </div>
                <h3 className="text-lg md:text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-teal-200 to-purple-300 uppercase tracking-widest flex items-center gap-2 mt-0.5">
                  <Activity className="text-cyan-400 animate-pulse w-5 h-5 flex-shrink-0" />
                  <span>{isGodModeStats ? "GLOBAL NETWORK TELEMETRY" : action.replace('SHOW_', '').replace('_', ' ')}</span>
                </h3>
              </div>
            </div>
            <div className="flex gap-3 items-center flex-shrink-0">
              <span className="text-[10px] text-cyan-400/70 font-mono tracking-widest uppercase border border-cyan-500/30 px-3 py-1.5 rounded-lg bg-cyan-500/10 hidden md:block">MODE: {role.toUpperCase()}</span>
              <button onClick={onClose} className="text-cyan-300 hover:text-white transition-all text-xs font-mono font-bold border border-cyan-400/50 px-5 py-2 rounded-xl bg-cyan-500/15 hover:bg-cyan-500/30 hover:border-cyan-300 shadow-[0_0_20px_rgba(6,182,212,0.3)] hover:shadow-[0_0_35px_rgba(6,182,212,0.7)] active:scale-95 flex items-center gap-2 cursor-pointer">
                <span>DISENGAGE HUD</span>
                <span className="text-cyan-400 font-bold">[X]</span>
              </button>
            </div>
          </div>

          <div className="p-3 sm:p-4 md:p-6 overflow-y-auto overflow-x-hidden custom-scrollbar flex-1 relative z-10 w-full box-border pb-24">
            {isGodModeStats ? (
              /* GOD MODE STATS DASHBOARD */
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
                {data.map((item, i) => Object.entries(item).map(([key, val]) => (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.1 }} key={key}
                    className="bg-gradient-to-b from-cyan-900/20 to-black/40 border border-cyan-500/30 rounded-2xl p-4 md:p-6 flex flex-col items-center justify-center relative overflow-hidden group min-h-[120px]"
                  >
                    <div className="absolute inset-0 bg-cyan-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <span className="text-[10px] md:text-xs uppercase font-mono text-cyan-400/70 mb-2 tracking-widest text-center w-full px-2 break-words whitespace-normal">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                    <span className="text-3xl md:text-5xl font-light text-white drop-shadow-[0_0_15px_rgba(255,255,255,0.5)] max-w-full px-2 break-words whitespace-normal">
                      {typeof val === 'number' ? val.toLocaleString() : String(val)}
                    </span>
                    <div className="absolute bottom-0 left-0 w-full h-1 bg-cyan-500/20">
                      <motion.div 
                        initial={{ width: 0 }} animate={{ width: '100%' }} transition={{ duration: 1.5, ease: "easeOut" }}
                        className="h-full bg-cyan-400"
                      />
                    </div>
                    {onActionClick && (
                      <div className="w-full pt-3 pb-1">
                        <NeuralActionButton label="Inquire" onClick={() => onActionClick({ [key]: val })} />
                      </div>
                    )}
                  </motion.div>
                )))}
              </div>
            ) : action === 'SHOW_ANALYTICS' ? (
              /* ── GOD MODE EXECUTIVE ANALYTICS DASHBOARD ── */
              <div className="flex flex-col gap-4 w-full">

                {/* KPI Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {analyticsKPIs.length > 0 ? analyticsKPIs.map((kpi, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 20, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ delay: i * 0.07, type: 'spring', stiffness: 180, damping: 18 }}
                      className="relative bg-gradient-to-br from-cyan-950/60 to-black/70 border border-cyan-500/20 rounded-2xl p-4 sm:p-5 flex flex-col gap-2 overflow-hidden group"
                    >
                      {/* Glow hover overlay */}
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-cyan-400/5 rounded-2xl" />
                      {/* Top row: icon + trend */}
                      <div className="flex items-center justify-between w-full">
                        <span className="text-2xl">{analyticsKPIIcon(kpi.title)}</span>
                        {kpi.trend && (
                          <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded-full bg-white/5 ${analyticsTrendColor(kpi.trend)}`}>
                            {kpi.trend}
                          </span>
                        )}
                      </div>
                      {/* Value */}
                      <p className="text-xl sm:text-2xl font-bold text-white drop-shadow-[0_0_12px_rgba(34,211,238,0.6)] leading-none break-all">
                        {kpi.value}
                      </p>
                      {/* Title */}
                      <p className="text-[9px] uppercase font-mono text-cyan-500/60 tracking-widest leading-tight">
                        {kpi.title}
                      </p>
                      {/* Bottom accent bar */}
                      <div className="absolute bottom-0 left-0 w-full h-[2px] bg-gradient-to-r from-cyan-500/60 via-purple-500/40 to-transparent" />
                    </motion.div>
                  )) : (
                    /* Skeleton placeholders while data loads */
                    [0,1,2,3].map(i => (
                      <div key={i} className="bg-cyan-950/30 border border-cyan-500/10 rounded-2xl p-5 h-[110px] animate-pulse" />
                    ))
                  )}
                </div>

                {/* Monthly Activity Chart */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
                  className="w-full bg-black/40 backdrop-blur border border-cyan-500/15 rounded-2xl p-4 sm:p-5"
                >
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <p className="text-[10px] sm:text-xs uppercase font-mono text-cyan-400/70 tracking-widest">Monthly Activity</p>
                    <div className="flex items-center gap-3 text-[9px] font-mono text-slate-500">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-cyan-500/80 inline-block" />Revenue</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-purple-500/80 inline-block" />Sales</span>
                    </div>
                  </div>
                  {/* Fixed pixel height container — % heights on children resolve reliably */}
                  <div className="flex items-end justify-between gap-1.5 w-full" style={{ height: `${CHART_H_PX}px` }}>
                    {(analyticsChart.length > 0
                      ? analyticsChart
                      : mockBars.map((v, i) => ({ month: mockMonths[i], revenue: v, sales: Math.round(v * 0.6) }))
                    ).map((d: any, i: number) => {
                      const maxR = analyticsChart.length > 0 ? maxRevenue : 100;
                      const maxS = analyticsChart.length > 0 ? maxSales : 100;
                      const rPx = Math.max(10, Math.round((Number(d.revenue) / maxR) * CHART_H_PX));
                      const sPx = Math.max(10, Math.round((Number(d.sales) / maxS) * CHART_H_PX));
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1 min-w-0" style={{ height: `${CHART_H_PX}px` }}>
                          <div className="flex items-end gap-[3px] w-full justify-center" style={{ height: `${CHART_H_PX - 16}px` }}>
                            {/* Revenue bar — cyan */}
                            <motion.div
                              className="rounded-t-sm bg-gradient-to-t from-cyan-700 to-cyan-400 shadow-[0_0_8px_rgba(6,182,212,0.7)] min-w-[5px] flex-1 max-w-[18px]"
                              initial={{ height: 0 }}
                              animate={{ height: rPx }}
                              transition={{ delay: 0.45 + i * 0.06, duration: 0.5, ease: 'easeOut' }}
                              style={{ minHeight: 10 }}
                            />
                            {/* Sales bar — purple */}
                            <motion.div
                              className="rounded-t-sm bg-gradient-to-t from-purple-700 to-purple-400 shadow-[0_0_8px_rgba(139,92,246,0.7)] min-w-[5px] flex-1 max-w-[18px]"
                              initial={{ height: 0 }}
                              animate={{ height: sPx }}
                              transition={{ delay: 0.5 + i * 0.06, duration: 0.5, ease: 'easeOut' }}
                              style={{ minHeight: 10 }}
                            />
                          </div>
                          <span className="text-[7px] sm:text-[9px] font-mono text-slate-500 truncate w-full text-center mt-1">{d.month || d.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>

                {/* Gradient status strip */}
                <motion.div
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
                  className="w-full flex items-center gap-3 bg-black/30 border border-white/5 rounded-xl px-4 py-3"
                >
                  <div className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)] animate-pulse flex-shrink-0" />
                  <p className="text-[10px] font-mono text-slate-400 tracking-wide">All systems operational &mdash; Kangavena Cloud Network</p>
                  <div className="ml-auto text-[9px] font-mono text-slate-600">{new Date().toLocaleTimeString()}</div>
                </motion.div>
              </div>
            ) : role === 'public' && action === 'SHOW_MAP' ? (
              /* HOLOGRAPHIC STORE LOCATOR MAP */
              (() => {
                // Group products by store to prevent overlapping markers
                const storeMap = new Map();
                data.forEach((item: any) => {
                  const rawLat = item.Store?.latitude;
                  const rawLng = item.Store?.longitude;
                  const lat = Number(rawLat);
                  const lng = Number(rawLng);
                  
                  // Strict Null-Safety Check
                  if (rawLat == null || rawLng == null || !isFinite(lat) || !isFinite(lng)) return;
                  
                  const key = `${lat},${lng}`;
                  if (!storeMap.has(key)) {
                    storeMap.set(key, { store: item.Store, products: [] });
                  }
                  storeMap.get(key).products.push(item);
                });

                const validItem = data.find((d: any) => d.Store?.latitude != null && d.Store?.longitude != null && isFinite(Number(d.Store.latitude)) && isFinite(Number(d.Store.longitude)));
                const mapCenter = validItem
                  ? [Number(validItem.Store.latitude), Number(validItem.Store.longitude)]
                  : [7.8731, 80.7718];

                const storeGroups = Array.from(storeMap.values());
                const primaryStore = storeGroups[0]; // Get the first available store to highlight

                return (
                  <div className="w-full flex flex-col gap-4">
                    {onBack && (
                      <button
                        onClick={onBack}
                        className="self-start flex items-center gap-1.5 text-[11px] text-cyan-400/70 hover:text-cyan-300 font-mono tracking-widest transition-colors mb-1"
                      >
                        ← Back to Products
                      </button>
                    )}
                    {/* HIGHLIGHTED INFO CARD - No clicking required */}
                    {primaryStore && (
                      <div className="w-full bg-slate-800/80 border border-cyan-500/40 rounded-xl p-4 shadow-[0_0_15px_rgba(6,182,212,0.15)] backdrop-blur-md">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="text-cyan-400 font-extrabold text-lg flex items-center gap-2">
                              <Store size={18} />
                              {primaryStore.store.name}
                            </h3>
                            <p className="text-slate-300 text-xs mt-1.5 flex items-center gap-1.5">
                              <MapPin size={14} className="text-slate-400" />
                              {primaryStore.store.address || primaryStore.store.location}
                            </p>
                          </div>
                          <div className="flex flex-col gap-2 flex-shrink-0">
                            {primaryStore.store.latitude && primaryStore.store.longitude && (
                              <a
                                href={`https://www.google.com/maps/dir/?api=1&destination=${primaryStore.store.latitude},${primaryStore.store.longitude}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 bg-cyan-500/10 text-cyan-300 px-4 py-2 rounded-lg text-xs font-bold border border-cyan-500/40 hover:bg-cyan-500/20 transition-all"
                              >
                                <MapPin size={14} /> Get Directions
                              </a>
                            )}
                            {primaryStore.store.phone && (
                              <a href={`tel:${primaryStore.store.phone}`} className="flex items-center gap-2 bg-green-500/10 text-green-400 px-4 py-2 rounded-lg text-xs font-bold border border-green-500/30 hover:bg-green-500/20 transition-all">
                                <Phone size={14} /> Call Store
                              </a>
                            )}
                          </div>
                        </div>
                        {/* PRODUCTS WITH IMAGES */}
                        <div className="mt-4 pt-3 border-t border-slate-700/50">
                          <p className="text-[10px] font-mono text-cyan-400/60 uppercase tracking-widest mb-2">Available Products</p>
                          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-cyan-500/30 scrollbar-track-transparent">
                            {primaryStore.products.map((p: any, idx: number) => {
                              const imageUrl = p.imageUrl || p.image || p.productImage || p.photo || p.thumbnail || null;
                              const firstLetter = String(p.name || 'P').charAt(0).toUpperCase();
                              return (
                                <div key={idx} className="flex-shrink-0 w-24 bg-slate-900/60 border border-cyan-500/20 rounded-lg overflow-hidden group/product">
                                  {/* Product Image or Fallback */}
                                  <div className="relative w-full h-16 bg-gradient-to-br from-cyan-950/40 to-purple-950/30">
                                    {imageUrl ? (
                                      <img
                                        src={imageUrl}
                                        alt={p.name}
                                        className="w-full h-full object-cover"
                                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-cyan-500/10 to-purple-500/10">
                                        <span className="text-xl font-black text-transparent bg-clip-text bg-gradient-to-br from-cyan-300 to-fuchsia-300">{firstLetter}</span>
                                      </div>
                                    )}
                                  </div>
                                  {/* Product Info */}
                                  <div className="p-2">
                                    <p className="text-[10px] font-medium text-cyan-100 truncate">{p.name}</p>
                                    <p className="text-[9px] text-cyan-400 font-mono">Rs.{p.price}</p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* MAP CONTAINER OR FALLBACK */}
                    {storeGroups.length > 0 ? (
                      <div className="w-full h-[280px] rounded-xl overflow-hidden border border-slate-700/50 relative z-0">
                        <MapErrorBoundary>
                          <React.Suspense fallback={<div className="w-full h-full bg-slate-900/50 rounded-xl" />}>
                            <LazyMultiStoreMap center={mapCenter as [number, number]} storeGroups={storeGroups} />
                          </React.Suspense>
                        </MapErrorBoundary>
                      </div>
                    ) : (
                      <div className="w-full h-[150px] bg-slate-900/50 border border-slate-700/50 rounded-xl p-4 flex items-center justify-center text-center">
                        <p className="text-[11px] font-mono text-slate-400 leading-relaxed">
                          Luna cannot find location cause owner has not setup the location on map
                        </p>
                      </div>
                    )}
                  </div>
                );
              })()
            ) : action === 'SHOW_FEATURES' ? (
              /* FEATURES/CAPABILITIES LIST */
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
                {data.map((item, i) => {
                  const vals = Object.values(item).filter(v => typeof v === 'string' && v.trim());
                  const cardTitle = item.title || item.name || item.feature || item.heading || (vals[0] as string) || 'Feature';
                  const cardDesc = item.description || item.details || item.summary || item.content || (vals[1] as string) || '';
                  const cardContext = String(cardTitle);
                  return (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} key={i}
                      className="bg-gradient-to-br from-cyan-900/30 to-purple-900/20 border border-cyan-500/40 rounded-2xl p-6 hover:shadow-[0_0_30px_rgba(6,182,212,0.3)] transition-all duration-300 group flex items-start gap-4 overflow-hidden cursor-default"
                      onMouseEnter={() => onHover?.(cardContext)}
                      onMouseLeave={() => onHover?.(null)}
                    >
                      <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center border border-cyan-400 shrink-0">
                        <Activity className="text-cyan-400 w-6 h-6" />
                      </div>
                      <div className="flex-1 min-w-0 overflow-hidden flex flex-col h-full">
                        <div className="flex-1">
                          <h3 className="text-base md:text-lg font-bold text-white mb-2 break-words leading-snug">{cardTitle}</h3>
                          {cardDesc && <p className="text-xs md:text-sm text-cyan-100/70 leading-relaxed break-words whitespace-normal">{cardDesc}</p>}
                        </div>
                        {onActionClick && (
                          <NeuralActionButton label={actionLabel} onClick={() => onActionClick(item)} />
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ) : action === 'SHOW_FORM' ? (
              /* REGISTRATION FORM — Year 2100 Cyberpunk Intake Form */
              <div className="flex flex-col items-center justify-start w-full min-h-[400px] px-0 sm:px-4 py-4">
                <motion.div
                  initial={{ opacity: 0, y: 24, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ type: 'spring', stiffness: 180, damping: 22 }}
                  className="w-full max-w-lg mx-auto bg-gradient-to-br from-[#0A1224]/95 via-[#0F1C36]/90 to-[#060B18]/95 border border-cyan-500/40 rounded-3xl p-6 sm:p-8 shadow-[0_0_80px_rgba(6,182,212,0.35)] flex flex-col gap-5 backdrop-blur-2xl relative"
                >
                  <div className="flex items-center gap-3.5 border-b border-cyan-500/25 pb-4">
                    <div className="w-12 h-12 rounded-2xl bg-cyan-500/20 border border-cyan-400 flex items-center justify-center shrink-0 shadow-[0_0_20px_rgba(6,182,212,0.35)]">
                      <Activity className="text-cyan-300 w-6 h-6 animate-pulse" />
                    </div>
                    <div>
                      <p className="text-[10px] font-mono font-bold text-cyan-300 uppercase tracking-widest bg-cyan-500/15 border border-cyan-400/30 px-2.5 py-0.5 rounded shadow-[0_0_10px_rgba(6,182,212,0.2)] inline-block">NEURAL INTAKE FORM v2100</p>
                      <h4 className="text-lg font-black text-white tracking-wide mt-1">Program Registration & Lead Capture</h4>
                    </div>
                  </div>

                  {(() => {
                    // DEFENSIVE ARRAY CASTING: Ensures data.map NEVER throws "TypeError: t.map is not a function"
                    let fieldsList: any[] = [];
                    if (Array.isArray(data)) {
                      fieldsList = data;
                    } else if (data && typeof data === 'object') {
                      fieldsList = Array.isArray((data as any).fields)
                        ? (data as any).fields
                        : (Array.isArray((data as any).formFields)
                            ? (data as any).formFields
                            : (Object.keys(data).length > 0 ? [data] : []));
                    }
                    
                    if (fieldsList.length === 0 || (fieldsList.length === 1 && !fieldsList[0]?.label && !fieldsList[0]?.field && !fieldsList[0]?.TITLE && !fieldsList[0]?.title)) {
                      fieldsList = [
                        { label: 'Full Name', placeholder: 'Enter your full name', type: 'text' },
                        { label: 'Email / Mobile Phone', placeholder: 'Enter email address or contact number', type: 'text' },
                        { label: 'Selected Program / Inquiry', placeholder: 'Specify course or special requirements', type: 'textarea' }
                      ];
                    }

                    return fieldsList.map((field: any, i: number) => {
                      const label = field.label || field.LABEL || field.field || field.FIELD || field.title || field.name || `Field ${i + 1}`;
                      const placeholder = field.placeholder || field.PLACEHOLDER || field.hint || `Enter ${label}`;
                      const isTextarea = (field.type || '').toLowerCase() === 'textarea' || (label ?? '').toLowerCase().includes('message') || (label ?? '').toLowerCase().includes('note') || (label ?? '').toLowerCase().includes('inquiry') || (label ?? '').toLowerCase().includes('qualification');
                      return (
                        <motion.div key={i} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.07 }} className="flex flex-col gap-2 font-sans">
                          <label className="text-xs uppercase font-mono font-extrabold text-cyan-300 tracking-wider flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-cyan-400 shadow-[0_0_8px_rgba(6,182,212,1)] inline-block" />
                            <span>{label}</span>
                          </label>
                          {isTextarea ? (
                            <textarea
                              rows={3}
                              placeholder={placeholder}
                              className="w-full bg-[#050A14]/80 border border-cyan-500/35 focus:border-cyan-300 rounded-2xl px-4 py-3 text-sm text-slate-100 placeholder-slate-500 outline-none resize-none transition-all focus:shadow-[0_0_25px_rgba(6,182,212,0.4)]"
                            />
                          ) : (
                            <input
                              type={field.type || 'text'}
                              placeholder={placeholder}
                              className="w-full bg-[#050A14]/80 border border-cyan-500/35 focus:border-cyan-300 rounded-2xl px-4 py-3 text-sm text-slate-100 placeholder-slate-500 outline-none transition-all focus:shadow-[0_0_25px_rgba(6,182,212,0.4)]"
                            />
                          )}
                        </motion.div>
                      );
                    });
                  })()}

                  <div className="mt-2">
                    <NeuralActionButton label="Submit Admission Request" onClick={() => onActionClick && onActionClick({ action: 'FORM_SUBMITTED' })} />
                  </div>
                </motion.div>
              </div>
            ) : action === 'SHOW_PRODUCTS' ? (
              /* ── PRODUCT GRID / DETAIL VIEW ── */
              <div className="w-full">
                <AnimatePresence mode="wait">
                  {(!data || data.length === 0) ? (
                    <motion.div
                      key="empty"
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      className="flex flex-col items-center justify-center py-16 opacity-70"
                    >
                      <Package size={40} className="text-cyan-500/50 mb-4" />
                      <p className="text-sm font-mono text-cyan-200">No products found in the active inventory.</p>
                    </motion.div>
                  ) : selectedProduct ? (
                    <ProductDetailView
                      key="detail"
                      product={selectedProduct}
                      allProducts={data}
                      onBack={() => setSelectedProduct(null)}
                      onActionClick={handleSelectProduct}
                      onHover={onHover}
                    />
                  ) : (
                    <motion.div
                      key="grid"
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.2 }}
                      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 w-full"
                    >
                      {data.map((item, i) => (
                        <ProductCard
                          key={i}
                          item={item}
                          index={i}
                          actionLabel={actionLabel}
                          onActionClick={handleSelectProduct}
                          onHover={onHover}
                        />
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              /* STANDARD LIST/GRID FOR TENANTS & POS (Courses, Products, etc.) */
              <div className="w-full">
                {(!data || data.length === 0) ? (
                  <div className="flex flex-col items-center justify-center py-16 opacity-70">
                    <Package size={40} className="text-cyan-500/50 mb-4" />
                    <p className="text-sm font-mono text-cyan-200">No items found in the active inventory.</p>
                  </div>
                ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 w-full">
                {data.map((item, i) => {
                  // Intelligently extract title and description — never show raw keys
                  const strVals = Object.entries(item)
                    .filter(([k]) => !String(k).toLowerCase().includes('image') && !String(k).toLowerCase().includes('url'))
                    .filter(([, v]) => typeof v === 'string' && (v as string).trim());
                  const cardTitle = item.title || item.name || item.course || item.product ||
                    item.heading || item.label || item.Title || item.Name ||
                    (strVals[0]?.[1] as string) || 'Item';
                  const cardDesc = item.description || item.details || item.summary ||
                    item.content || item.about || item.info ||
                    (strVals[1]?.[1] as string) || '';
                  
                  // Extract category or auto-derive
                  const categoryName = item.category || item.department || item.faculty || 
                    (cardTitle.toLowerCase().includes('software') || cardTitle.toLowerCase().includes('it') ? 'FACULTY OF COMPUTING & IT' :
                     cardTitle.toLowerCase().includes('ai') || cardTitle.toLowerCase().includes('data') ? 'FACULTY OF ARTIFICIAL INTELLIGENCE' :
                     cardTitle.toLowerCase().includes('mba') || cardTitle.toLowerCase().includes('business') ? 'FACULTY OF MANAGEMENT & MBA' :
                     cardTitle.toLowerCase().includes('design') || cardTitle.toLowerCase().includes('ui') ? 'FACULTY OF DIGITAL MEDIA & DESIGN' :
                     cardTitle.toLowerCase().includes('cyber') || cardTitle.toLowerCase().includes('hacking') ? 'FACULTY OF CYBERSECURITY' : 'ACADEMIC PROGRAM');

                  const metaFields = strVals.filter(([, v]) =>
                    v !== cardTitle && v !== cardDesc
                  ).slice(0, 3);
                  const cardContext = String(cardTitle);
                  const hasImage = Object.entries(item).some(([k]) => String(k).toLowerCase().includes('image') || String(k).toLowerCase() === 'url');
                  
                  return (
                    <motion.div
                      initial={{ opacity: 0, y: 24, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ delay: i * 0.06, type: 'spring', stiffness: 180, damping: 20 }}
                      key={i}
                      className="bg-gradient-to-br from-[#0A1224]/95 via-[#0F1C36]/90 to-[#060B18]/95 border border-cyan-500/40 hover:border-cyan-300 rounded-3xl p-6 hover:shadow-[0_0_55px_rgba(6,182,212,0.45)] hover:-translate-y-1.5 transition-all duration-300 group shadow-2xl flex flex-col h-full overflow-hidden box-border cursor-default backdrop-blur-2xl relative"
                      onMouseEnter={() => onHover?.(cardContext)}
                      onMouseLeave={() => onHover?.(null)}
                    >
                      {/* Top Laser Accent Sweep Line */}
                      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-cyan-400 to-transparent opacity-80 group-hover:opacity-100 transition-opacity" />

                      {/* Sci-Fi Corner HUD Accent Dots */}
                      <div className="absolute top-2 left-2 w-1.5 h-1.5 rounded-full bg-cyan-400/40 group-hover:bg-cyan-400 transition-colors" />
                      <div className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-cyan-400/40 group-hover:bg-cyan-400 transition-colors" />

                      {/* Category Tag Header */}
                      <div className="mb-2">
                        <span className="text-[9px] font-mono font-bold text-cyan-300 uppercase tracking-widest bg-cyan-500/15 border border-cyan-400/30 px-3 py-1 rounded-full shadow-[0_0_12px_rgba(6,182,212,0.2)] inline-block">
                          ⚡ {categoryName}
                        </span>
                      </div>

                      {hasImage && (
                        <div className="w-full h-32 md:h-40 bg-gradient-to-br from-cyan-950/40 via-slate-900/90 to-purple-950/40 rounded-2xl mb-4 border border-cyan-500/30 flex items-center justify-center overflow-hidden flex-shrink-0 shadow-inner group-hover:border-cyan-400/60 transition-colors">
                          <Activity className="text-cyan-400 w-12 h-12 animate-pulse" />
                        </div>
                      )}

                      <div className="flex-1 min-w-0 w-full overflow-hidden flex flex-col gap-3">
                        {/* Title — Prominent, futuristic typography */}
                        <h3 className="text-lg md:text-xl font-black text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-cyan-300 group-hover:via-teal-200 group-hover:to-fuchsia-300 transition-all leading-snug break-words">
                          {cardTitle}
                        </h3>

                        {/* Description */}
                        {cardDesc && <p className="text-xs text-slate-300/85 leading-relaxed break-words flex-1 font-sans font-normal">{cardDesc}</p>}

                        {/* Metadata Badges (Fees in Emerald Neon, Duration in Cyan Neon) */}
                        {metaFields.length > 0 && (
                          <div className="flex flex-wrap gap-2.5 mt-2">
                            {metaFields.map(([k, v]) => {
                              const valStr = String(v);
                              const isPrice = valStr.toLowerCase().includes('lkr') || valStr.toLowerCase().includes('rs') || !isNaN(Number(valStr.replace(/[^0-9.]/g, '')));
                              return (
                                <span
                                  key={k}
                                  className={`text-xs font-mono font-extrabold px-4 py-1.5 rounded-xl border break-words flex items-center gap-1.5 transition-all ${
                                    isPrice
                                      ? 'bg-gradient-to-r from-emerald-500/25 via-teal-500/20 to-emerald-600/15 text-emerald-300 border-emerald-400/60 shadow-[0_0_15px_rgba(16,185,129,0.3)]'
                                      : 'bg-cyan-500/15 text-cyan-300 border-cyan-400/40 shadow-[0_0_10px_rgba(6,182,212,0.15)]'
                                  }`}
                                >
                                  {isPrice ? <span>💎</span> : <span>⏳</span>}
                                  <span>{valStr}</span>
                                </span>
                              );
                            })}
                          </div>
                        )}

                        {/* Store Details Section */}
                        {(item.Store?.name || item.storeName || item.Store?.location || item.location) && (
                          <div className="mt-3 pt-3 border-t border-slate-800 flex flex-col gap-1.5 font-sans">
                            <div className="flex items-center gap-2 text-xs text-slate-200">
                              <Store size={14} className="text-purple-400 shrink-0" />
                              <span className="truncate font-semibold">{item.Store?.name || item.storeName || 'Kangavena Network'}</span>
                            </div>
                            <div className="flex items-center gap-2 text-[11px] text-slate-400">
                              <MapPin size={12} className="text-cyan-400 shrink-0" />
                              <span className="truncate">{item.Store?.address || item.Store?.location || 'Local Branch'}</span>
                            </div>
                          </div>
                        )}

                        {onActionClick && (
                          <div className="mt-3">
                            <NeuralActionButton label={actionLabel} onClick={() => onActionClick(item)} />
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
                )}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// ─── Error Boundary — contains Luna crashes so POS never goes down ──────────
type LunaErrorBoundaryProps = { children: React.ReactNode };
type LunaErrorBoundaryState = { hasError: boolean; errorMsg: string };
class LunaErrorBoundary extends Component<LunaErrorBoundaryProps, LunaErrorBoundaryState> {
  state: LunaErrorBoundaryState = { hasError: false, errorMsg: '' };
  constructor(props: LunaErrorBoundaryProps) {
    super(props);
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, errorMsg: error?.message || 'Unknown error' };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[Luna Error Boundary]', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center text-white gap-4">
          <div className="text-cyan-400 text-4xl">⚠</div>
          <div className="text-sm font-mono text-slate-400 text-center px-6">
            Luna encountered an error and has been safely isolated.
            <br />
            <span className="text-red-400 text-xs">{this.state.errorMsg}</span>
          </div>
          <button
            onClick={() => (this as any).setState({ hasError: false, errorMsg: '' })}
            className="px-4 py-2 rounded-lg bg-cyan-500/20 border border-cyan-500/40 text-cyan-300 text-xs font-bold uppercase tracking-widest hover:bg-cyan-500/30 transition-all"
          >
            Restart Luna
          </button>
        </div>
      );
    }
    return (this as any).props.children;
  }
}

// ── Strategic Consultant HUD ──────────────────────────────────────────────────

const SCAN_STEPS = [
  'ගනුදෙනු ඉතිහාසය විශ්ලේෂණය කරමින්...',
  'වඩාත් විකිණෙන භාණ්ඩ හඳුනා ගනිමින්...',
  'ප්‍රාදේශීය වෙළෙඳ ජනතාව ඇගයිමින්...',
  'අසල්වැසි තරඟකරුවන් සොයා බලමින්...',
  'PESTEL සාධක සංශ්ලේෂණය කරමින්...',
  'Ansoff වර්ධන මාදිලිය සිතියම් ගත කරමින්...',
  'උපාය මාර්ගික රාමුව සකස් කරමින්...',
];

interface StrategicPlanData {
  topCategories: string[];
  topItems: string[];
  storeName: string;
  location?: string;
  isReplay?: boolean;
}

function StrategicConsultantHUD({ data, onClose }: { data: StrategicPlanData; onClose: () => void }) {
  const [phase, setPhase]         = useState<'scanning' | 'report'>(data.isReplay ? 'report' : 'scanning');
  const [scanStep, setScanStep]   = useState(0);
  const [activeTab, setActiveTab] = useState<'digital' | 'local' | 'international'>('digital');
  const [saved, setSaved]         = useState(false);

  useEffect(() => {
    if (phase !== 'scanning') return;
    if (scanStep < SCAN_STEPS.length - 1) {
      const t = setTimeout(() => setScanStep(s => s + 1), 1400);
      return () => clearTimeout(t);
    } else {
      const t = setTimeout(() => setPhase('report'), 1200);
      return () => clearTimeout(t);
    }
  }, [phase, scanStep]);

  const top3     = data.topCategories.slice(0, 3).join(', ') || 'සාමාන්‍ය සිල්ලර';
  const topItems = data.topItems.slice(0, 3).join(', ')      || 'ඔබේ ප්‍රධාන භාණ්ඩ';

  const handleSave = () => {
    window.dispatchEvent(new CustomEvent('kangavena:save-strategic-plan'));
    setSaved(true);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9990] bg-slate-950/95 backdrop-blur-xl flex flex-col items-center justify-center p-4 overflow-hidden"
    >
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-6 py-4 border-b border-cyan-500/15">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
            <Target size={15} className="text-cyan-400" />
          </div>
          <div>
            <p className="text-[13px] font-black text-white tracking-wide">උපාය මාර්ගික උපදේශක</p>
            <p className="text-[9px] text-cyan-400/70 font-mono uppercase tracking-widest">{data.storeName}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {phase === 'report' && (
            <button
              onClick={handleSave}
              disabled={saved}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[9px] font-bold border transition-all ${saved ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400 cursor-default' : 'bg-cyan-500/10 border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/20'}`}
              title="Luna, save this plan"
            >
              <Zap size={9} />
              {saved ? 'සුරකින ලදී ✓' : 'සැලැස්ම සුරකිමු'}
            </button>
          )}
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:border-white/30 transition-all">
            <X size={14} />
          </button>
        </div>
      </div>

      {phase === 'scanning' ? (
        /* ── Scanning Animation ───────────────────────────────────── */
        <div className="flex flex-col items-center gap-8 mt-12">
          <div className="relative w-40 h-40 flex items-center justify-center">
            <div className="absolute inset-0 rounded-full border border-cyan-500/30 animate-[spin_4s_linear_infinite]" />
            <div className="absolute inset-3 rounded-full border border-dashed border-cyan-400/20 animate-[spin_6s_linear_infinite_reverse]" />
            <div className="absolute inset-6 rounded-full border border-cyan-500/40 animate-[spin_3s_linear_infinite]" />
            <div className="w-20 h-20 rounded-full bg-cyan-500/10 border border-cyan-500/40 flex items-center justify-center">
              <BarChart3 size={28} className="text-cyan-400 animate-pulse" />
            </div>
            <motion.div
              className="absolute w-2.5 h-2.5 rounded-full bg-cyan-400 shadow-[0_0_10px_rgba(34,211,238,0.8)]"
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              style={{ transformOrigin: '0 80px', top: '50%', left: '50%', marginTop: '-5px', marginLeft: '-5px' }}
            />
          </div>
          <div className="flex flex-col items-center gap-3">
            <p className="text-[10px] text-cyan-400/60 font-mono uppercase tracking-[0.3em]">Luna AI පර්යේෂණය</p>
            <AnimatePresence mode="wait">
              <motion.p
                key={scanStep}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.35 }}
                className="text-[18px] font-bold text-white text-center max-w-xs"
              >
                {SCAN_STEPS[scanStep]}
              </motion.p>
            </AnimatePresence>
            <div className="w-64 h-0.5 bg-white/5 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-cyan-400 rounded-full"
                animate={{ width: `${((scanStep + 1) / SCAN_STEPS.length) * 100}%` }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              />
            </div>
            <div className="flex gap-1.5 mt-1">
              {SCAN_STEPS.map((_, i) => (
                <div key={i} className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${i <= scanStep ? 'bg-cyan-400' : 'bg-white/10'}`} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        /* ── Strategic Report (Sinhala) ───────────────────────────── */
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="w-full max-w-4xl mt-16 flex flex-col gap-4"
        >
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 self-start">
            <Zap size={10} className="text-cyan-400" />
            <span className="text-[10px] font-bold text-cyan-300 uppercase tracking-widest">Luna AI — වර්ධන රාමුව</span>
          </div>
          <p className="text-[13px] text-slate-400">ඔබේ ප්‍රධාන කාණ්ඩ: <span className="text-white font-bold">{top3}</span></p>

          {/* Tab selector */}
          <div className="flex gap-2 flex-wrap">
            {([
              ['digital',       Globe,  'ඩිජිටල් අලෙවිය'],
              ['local',         MapIcon,    'ප්‍රාදේශීය අලෙවිය'],
              ['international', Users,  'ජාත්‍යන්තර'],
            ] as const).map(([id, Icon, label]) => (
              <button key={id} onClick={() => setActiveTab(id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-bold border transition-all ${
                  activeTab === id
                    ? 'bg-cyan-500/20 border-cyan-500/40 text-cyan-300'
                    : 'bg-white/[0.03] border-white/10 text-slate-500 hover:text-slate-300 hover:border-white/20'
                }`}
              >
                <Icon size={11} /> {label}
              </button>
            ))}
          </div>

          {/* Report panel */}
          <div className="rounded-2xl bg-white/[0.03] border border-white/[0.07] overflow-y-auto max-h-[55vh] p-5">
            <AnimatePresence mode="wait">
              {activeTab === 'digital' && (
                <motion.div key="digital" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col gap-5">
                  <StratSection icon={<Shield size={13} />} title="SWOT විශ්ලේෂණය" color="text-emerald-400" border="border-emerald-500/20" bg="bg-emerald-500/5">
                    <p className="text-[11px] text-slate-300 leading-relaxed"><strong>ශක්තිය:</strong> <strong>{top3}</strong> විකිණීමේ ශක්තිය. <strong>අවස්ථාව:</strong> ඩිජිටල් දෘශ්‍යතාව පුළුල් කිරීම. <strong>දුර්වලතාව:</strong> පාගමන් ගනුදෙනු මත රඳා පැවතීම. <strong>තර්ජනය:</strong> ප්‍රාදේශීය e-commerce ව්‍යාපාර මිල ගණන් අඩු කිරීම.</p>
                  </StratSection>
                  <StratSection icon={<Layers size={13} />} title="Keller ගේ වෙළෙඳ නාම ත්‍රිකෝණය" color="text-violet-400" border="border-violet-500/20" bg="bg-violet-500/5">
                    <p className="text-[11px] text-slate-300 leading-relaxed"><strong>අනන්‍යතාව:</strong> විශ්වාසනීය, ප්‍රාදේශීය, ස්ථාවර. <strong>අර්ථය:</strong> ප්‍රජා පහසුව. <strong>ප්‍රතිචාරය:</strong> <strong>{topItems}</strong> වෙනුවෙන් සමාලෝචන හා සමාජ සාක්ෂි ගොඩනැගීම. <strong>සංසරණය:</strong> Kangavena CRM හරහා පක්ෂපාතී වැඩසටහන සක්‍රිය කිරීම.</p>
                  </StratSection>
                  <StratSection icon={<Globe size={13} />} title="PESTEL රාමුව" color="text-sky-400" border="border-sky-500/20" bg="bg-sky-500/5">
                    <p className="text-[11px] text-slate-300 leading-relaxed"><strong>දේශපාලනික:</strong> ක්ෂුද්‍ර ව්‍යවසාය සහාය ක්‍රමවේද ප්‍රයෝජනයට ගන්න. <strong>ආර්ථික:</strong> ඉහළ සංඛ්‍යාත SKU සඳහා උද්ධමන-සංවේදී මිල ගණන්. <strong>සමාජ:</strong> දේශීය භාෂා ප්‍රවර්ධන. <strong>තාක්ෂණ:</strong> QR-පදනම් පක්ෂපාතී ලකුණු. <strong>පරිසර:</strong> හරිත ඇසුරුම් වෙනස්කම. <strong>නීතිමය:</strong> ඩිජිටල් ඉන්වොයිස් VAT අනුකූලතාව.</p>
                  </StratSection>
                  <StratSection icon={<Target size={13} />} title="7D ආකෘතිය (ඩිජිටල් ස්පර්ශ ලක්ෂ්‍ය)" color="text-amber-400" border="border-amber-500/20" bg="bg-amber-500/5">
                    <p className="text-[11px] text-slate-300 leading-relaxed"><strong>උපාංගය</strong> (ජංගම-ප්‍රථම), <strong>සොයාගැනීම</strong> (Google Business Profile), <strong>ගමනාන්තය</strong> (WhatsApp නාමාවලිය), <strong>සංවාදය</strong> (Luna AI), <strong>දත්ත</strong> (ගනුදෙනු විශ්ලේෂණ), <strong>වට්ටම්</strong> (පක්ෂපාතී ත්‍යාග), <strong>බෙදාහැරීම</strong> (දිනය-ම ප්‍රාදේශීය).</p>
                  </StratSection>
                  <StratSection icon={<BarChart3 size={13} />} title="PESO මාධ්‍ය මිශ්‍රණය" color="text-rose-400" border="border-rose-500/20" bg="bg-rose-500/5">
                    <p className="text-[11px] text-slate-300 leading-relaxed"><strong>ගෙවූ:</strong> <strong>{top3}</strong> සඳහා 5km භූ-ඉලක්ක Google/Meta දැන්වීම්. <strong>ඉපැයූ:</strong> Google Maps ගනුදෙනුකරුවන්ගේ ශ්‍රේණිගත කිරීම්. <strong>හවුල්:</strong> WhatsApp state + ප්‍රජා කණ්ඩායම්. <strong>සතු:</strong> POS විකිණුම් දත්ත තොගසේවා කතාසාහිත්‍ය සඳහා.</p>
                  </StratSection>
                  <StratSection icon={<TrendingUp size={13} />} title="Value Proposition Canvas" color="text-cyan-400" border="border-cyan-500/20" bg="bg-cyan-500/5">
                    <p className="text-[11px] text-slate-300 leading-relaxed"><strong>ගනුදෙනුකරු කාර්යය:</strong> නිවස අසල <strong>{topItems}</strong> ලබාගැනීම. <strong>වේදනාව:</strong> දිගු පෝළිම්, ගබඩා හිගය, මිල අපැහැදිලිකම. <strong>ලාභ:</strong> පක්ෂපාතී ලකුණු, ක්ෂණික රිසිට්පත, Luna AI හඬ-ගෙවීම. <strong>වේදනා නිවාරකය:</strong> තොග දෘශ්‍යතාව. <strong>ලාභ නිර්මාතෘ:</strong> පෞද්ගලීකෘත ප්‍රවර්ධන.</p>
                  </StratSection>
                </motion.div>
              )}
              {activeTab === 'local' && (
                <motion.div key="local" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col gap-5">
                  <StratSection icon={<Target size={13} />} title="STP උපාය මාර්ගය" color="text-emerald-400" border="border-emerald-500/20" bg="bg-emerald-500/5">
                    <p className="text-[11px] text-slate-300 leading-relaxed"><strong>ඛණ්ඩනය:</strong> 3km ඇතුළත නිවාස, දිනපතා-අවශ්‍ය ගැනුම්කරුවන්, <strong>{top3}</strong> ගන්නා කුඩා ආපනශාලා. <strong>ඉලක්කය:</strong> ඉහළ-සංඛ්‍යාත ගැනුම්කරුවන් (සතිය 3+ වතාවක්). <strong>ස්ථානගතකිරීම:</strong> "ඔබේ අසල්වැසි වෙළඳසැල — ශ්‍රේෂ්ඨ ලෙස."</p>
                  </StratSection>
                  <StratSection icon={<Layers size={13} />} title="4Ps අලෙවිකරණ මිශ්‍රණය" color="text-violet-400" border="border-violet-500/20" bg="bg-violet-500/5">
                    <p className="text-[11px] text-slate-300 leading-relaxed"><strong>නිෂ්පාදිත:</strong> ජනප්‍රිය භාණ්ඩ පහසු පැකේජ ලෙස. <strong>මිල:</strong> <strong>{topItems}</strong> නිශ්චිත මිලින්. <strong>ස්ථානය:</strong> ආවේගශීලී ගැනුම් සඳහා හැඩගැසූ ගබඩා සැලැස්ම. <strong>ප්‍රවර්ධනය:</strong> හොඳම විකිණුම් දිනයට සම්බන්ධ සතිය-අවසාන ෆ්ලෑෂ් බිකිණිය.</p>
                  </StratSection>
                  <StratSection icon={<BarChart3 size={13} />} title="Ansoff වර්ධන ව්‍යූහය" color="text-sky-400" border="border-sky-500/20" bg="bg-sky-500/5">
                    <p className="text-[11px] text-slate-300 leading-relaxed"><strong>වෙළෙඳපොළ විනිවිද යාම:</strong> පක්ෂපාතී ඇණවුම් හරහා ගෙවීම් ඉහළ නැංවීම. <strong>වෙළෙඳපොළ සංවර්ධනය:</strong> බෙදාහැරීමෙන් නව ප්‍රදේශ ඉලක්ක. <strong>නිෂ්පාදන සංවර්ධනය:</strong> <strong>{top3}</strong> සඳහා ෆ්‍රැන්චයිස් ඇසුරුම්. <strong>විවිධකරණය:</strong> ගෙවීම්, ජංගම ක්‍රෙඩිට් සේවා.</p>
                  </StratSection>
                  <StratSection icon={<Shield size={13} />} title="Porter ගේ බල පංච රාශිය" color="text-amber-400" border="border-amber-500/20" bg="bg-amber-500/5">
                    <p className="text-[11px] text-slate-300 leading-relaxed"><strong>තරඟය:</strong> ඉහළ — සේවා වෙනස්කම් මගින් යුද්ධය. <strong>නව ඇතුළු:</strong> මධ්‍යම — ස්ථාන ආරක්ෂාව ඔබේ වාසිය. <strong>ආදේශකයන්:</strong> සුපිරි වෙළඳසැල් — පෙරට පෞද්ගලික සේවාව. <strong>ගැනුම්කරු බලය:</strong> ඉහළ — ස්විච් කිරීම් පක්ෂපාතිත්ව වැඩසටහනෙන් අඩු. <strong>සැපයුම්කරු:</strong> ශ්‍රේණිය 2+ සමාගම් සාකච්ඡා.</p>
                  </StratSection>
                  <StratSection icon={<Globe size={13} />} title="නිල් මහා සාගර උපාය" color="text-rose-400" border="border-rose-500/20" bg="bg-rose-500/5">
                    <p className="text-[11px] text-slate-300 leading-relaxed"><strong>ඉවත් කරන්න:</strong> අතින් තොග ගණන් කිරීම. <strong>අඩු කරන්න:</strong> මිල ගෙවීමේ පෝළිම. <strong>ඉහළ නැංවීම:</strong> AI-ආශ්‍රිත නැවත ඇණවුම් ඇඟවීම්. <strong>නිර්මාණය:</strong> Luna AI හඬ-ගෙවීම — රටේ ඒකෛකම හඬ-AI ගබඩාව.</p>
                  </StratSection>
                  <StratSection icon={<MapIcon size={13} />} title="ප්‍රාදේශීය SEO + ළඟාම තරඟකරු" color="text-cyan-400" border="border-cyan-500/20" bg="bg-cyan-500/5">
                    <p className="text-[11px] text-slate-300 leading-relaxed">Google Business Profile ලියාපදිංචි + ප්‍රශස්ත. <strong>{topItems}</strong> නිෂ්පාදනවල ඡායාරූප එකතු කිරීම. සතිපතා පළ කිරීම. ගෙවීමේ දී සෑම ගනුදෙනුකරුවෙකුගෙන්ම Google ශ්‍රේණිගතකිරීමක් ඉල්ලීම. ළඟාම 5 තරඟකරුවන්ගේ මිල ගණන් සතිපතා නිරීක්ෂණය.</p>
                  </StratSection>
                </motion.div>
              )}
              {activeTab === 'international' && (
                <motion.div key="international" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col gap-5">
                  <StratSection icon={<Globe size={13} />} title="CAGE දුර රාමුව" color="text-emerald-400" border="border-emerald-500/20" bg="bg-emerald-500/5">
                    <p className="text-[11px] text-slate-300 leading-relaxed"><strong>සංස්කෘතික:</strong> UAE, UK, කැනඩාවේ ශ්‍රී ලාංකික ව්‍යාප්ත ජනතාව — <strong>{top3}</strong> සඳහා ශක්තිමත් ඉල්ලුම. <strong>පරිපාලන:</strong> ඇසුරු කළ භාණ්ඩ සඳහා අපනයන බලපත්‍ර. <strong>භූගෝලීය:</strong> ශීතල-දාමය නොමැතිව ප්‍රවාහනය (දින 3–7). <strong>ආර්ථික:</strong> ව්‍යාප්ත ජනතාව අව්‍යාජ බව ගෙවන නිසා ඉහළ මිල ගනිව්.</p>
                  </StratSection>
                  <StratSection icon={<Layers size={13} />} title="EPRG රාමුව (ගෝලීකරණ මනෝභාවය)" color="text-violet-400" border="border-violet-500/20" bg="bg-violet-500/5">
                    <p className="text-[11px] text-slate-300 leading-relaxed"><strong>වත්මන් අදියර:</strong> Ethnocentric (ස්වදේශ වෙළෙඳපොළ). <strong>නිර්දේශිත:</strong> Polycentric — ව්‍යාප්ත-විශේෂ WhatsApp නාමාවලිය, AED/GBP/CAD වලින් මිල ලැයිස්තු. <strong>අදියර 3:</strong> Geocentric — කේන්ද්‍රගත සැපයුම, ප්‍රාදේශීය ව්‍යාපාරික ලාංඡනය.</p>
                  </StratSection>
                  <StratSection icon={<Shield size={13} />} title="රට අවදානම් ඇගයීම" color="text-sky-400" border="border-sky-500/20" bg="bg-sky-500/5">
                    <p className="text-[11px] text-slate-300 leading-relaxed"><strong>ඉලක්ක වෙළෙඳපොළ (අඩු අවදානම):</strong> UAE (බොහෝ ආහාර සඳහා ආනයන බදු ශූන්‍ය), UK (ස්ථාවර ශ්‍රී ලාංකික ප්‍රජාව), කැනඩාව (ස්ථාවර නීති). <strong>ආරක්ෂාව:</strong> <strong>{topItems}</strong> ශීතල-දාමය නොඅවශ්‍ය ඇසුරු නිෂ්පාදනවලින් ආරම්භ.</p>
                  </StratSection>
                  <StratSection icon={<Users size={13} />} title="Hofstede ගේ සංස්කෘතික මානයන්" color="text-amber-400" border="border-amber-500/20" bg="bg-amber-500/5">
                    <p className="text-[11px] text-slate-300 leading-relaxed"><strong>බලතල දුර:</strong> ව්‍යාප්ත ජනතාව විශ්වාසනීය ස්වදේශ ලාංඡනවලට ගරු කරයි. <strong>සාමූහිකත්වය:</strong> ශ්‍රී ලාංකික සංගම් හරහා ප්‍රජා කණ්ඩායම් ගෙවීම. <strong>අවිනිශ්චිතතා වළකිනු:</strong> ඉහළ — ජාත්‍යන්තර ඇණවුම් සඳහා ස්පෂ්ට ආපසු ගැනීමේ ප්‍රතිපත්තිය. <strong>දීර්ඝ-කාල:</strong> අවුරුදු 2–3 කාලය තුළ ශ්‍රේෂ්ඨ ලාංඡනය.</p>
                  </StratSection>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}

function StratSection({ icon, title, color, border, bg, children }: { icon: React.ReactNode; title: string; color: string; border: string; bg: string; children: React.ReactNode }) {
  return (
    <div className={`rounded-xl border ${border} ${bg} p-4`}>
      <div className={`flex items-center gap-2 mb-2 ${color}`}>
        {icon}
        <span className="text-[11px] font-black uppercase tracking-wider">{title}</span>
      </div>
      {children}
    </div>
  );
}

export interface LunaVoiceModelHandle {
  /** Surgically kills mic stream, audio context, WebSocket session, and intervals —
   *  without triggering the onDisconnect callback. Use when a parent modal closes
   *  and Luna must be force-stopped, NOT when the user intentionally minimizes. */
  forceKill(): void;
}

interface LunaVoiceModelProps {
  apiKey: string;
  role: 'public' | 'admin' | 'store' | 'tenant' | 'corporate' | 'corporate_admin';
  storeName?: string;
  shopType?: string;
  onDisconnect?: () => void;
}

export const LunaVoiceModel = forwardRef<LunaVoiceModelHandle, LunaVoiceModelProps>(({
  apiKey, role, storeName, shopType, onDisconnect
}, ref) => {
  const [isListening, setIsListening] = useState(false);
  const [lunaStatus, setLunaStatus] = useState<'Idle' | 'Listening' | 'Speaking' | 'Connecting...'>('Idle');
  const [currentMessage, setCurrentMessage] = useState<string | null>(null);
  const [preferredLang, setPreferredLang] = useState<string>(
    () => localStorage.getItem('luna_preferred_language') || 'si'
  );
  // Geo-detected language from IP (set once on mount, used as fallback when no saved pref)
  const [geoLang, setGeoLang] = useState<{ language: string; langCode: string; nativeName: string; country: string } | null>(null);
  const [isLunaConnected, setIsLunaConnected] = useState(false);
  const [isWaving, setIsWaving] = useState(false);
  const [sentiment, setSentiment] = useState<'happy' | 'neutral' | 'sad' | 'angry' | 'excited' | 'surprised' | 'concentrating'>('neutral');
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  const [tenantAvatarUrl, setTenantAvatarUrl] = useState<string | null>(null);
  const [tenantAvatarName, setTenantAvatarName] = useState<string | null>(null);
  const [hologramAction, setHologramAction] = useState<string | null>(null);
  const [hologramData, setHologramData] = useState<any | null>(null);
  const [prevHologramAction, setPrevHologramAction] = useState<string | null>(null);
  const [prevHologramData, setPrevHologramData] = useState<any | null>(null);
  const hologramActionRef = useRef<string | null>(null);
  const hologramDataRef = useRef<any | null>(null);
  const [showLiveDashboard, setShowLiveDashboard] = useState(false);
  const [showWeeklyChart, setShowWeeklyChart] = useState(false);
  const [weeklyChartData, setWeeklyChartData] = useState<ChartTrendsData | null>(null);
  const [strategicPlanData, setStrategicPlanData] = useState<StrategicPlanData | null>(null);
  const strategicPlanDataRef = useRef<StrategicPlanData | null>(null);
  useEffect(() => { strategicPlanDataRef.current = strategicPlanData; }, [strategicPlanData]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { topCategories: string[]; topItems: string[]; storeName: string };
      if (!detail) return;
      setStrategicPlanData({
        topCategories: Array.isArray(detail.topCategories) ? detail.topCategories : [],
        topItems: Array.isArray(detail.topItems) ? detail.topItems : [],
        storeName: typeof detail.storeName === 'string' ? detail.storeName : '',
        isReplay: true,
      });
    };
    window.addEventListener('kangavena:replay-strategic-plan', handler);
    return () => window.removeEventListener('kangavena:replay-strategic-plan', handler);
  }, []);
  const [floatingKeywords, setFloatingKeywords] = useState<{ id: number, text: string, x: number, y: number }[]>([]);
  const [hoverContext, setHoverContext] = useState<string | null>(null);
  // ── All telemetry stored in REFS (not state) to prevent re-renders on every mouse move ──
  const hoveredTelemetryRef = useRef<any | null>(null);
  const activeViewportSectionRef = useRef<string>('');
  const hostPageSectionsRef = useRef<{ id: string, title: string }[]>([]);
  const pdfContextRef = useRef<string | null>(null);
  const pdfTextContentRef = useRef<string | null>(null);
  const lunaVisionActiveRef = useRef<boolean>(false);
  const lunaVisionFrameRef = useRef<{ base64: string; mimeType: string } | null>(null);
  const initialMouseDisclosureDoneRef = useRef<boolean>(false);
  const userManuallyDisconnectedRef = useRef<boolean>(false); // Prevents auto-reconnect race condition
  const visionCaptureIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null); // Periodic screen capture timer
  const lastSpeechCaptureTimeRef = useRef<number>(0); // Throttle speech-based screen capture
  const lastUserPromptRef = useRef<string>(''); // Prompt preview for real-time telemetry
  const currentAssistantTurnTextRef = useRef<string>(''); // Streamed assistant speech text accumulator

  // Keep a single state for UI-only use (hoverContext pill display), not for prompt building
  const [hoverContextDisplay, setHoverContextDisplay] = useState<string | null>(null);

  useEffect(() => {
    const handleTelemetryMessage = (event: MessageEvent) => {
      const data = event.data;
      if (data && data.type === 'LUNA_CURSOR_CONTEXT') {
        hoveredTelemetryRef.current = data.payload;
        if (data.payload?.pdfUrl && !pdfContextRef.current) {
          pdfContextRef.current = data.payload.pdfUrl;
        }
      } else if (data && data.type === 'LUNA_VIEWPORT_CONTEXT') {
        activeViewportSectionRef.current = data.payload?.activeSection || '';
      } else if (data && data.type === 'LUNA_HOST_PAGE_MANIFEST') {
        hostPageSectionsRef.current = data.payload?.sections || [];
      } else if (data && data.type === 'LUNA_PDF_CONTEXT') {
        const pdfUrl = data.payload?.pdfUrl || null;
        pdfContextRef.current = pdfUrl;
        if (pdfUrl && !pdfTextContentRef.current) {
          fetch('/api/luna/extract-pdf', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ pdfUrl })
          })
          .then(r => r.json())
          .then(res => {
            if (res.success && res.text) {
              pdfTextContentRef.current = res.text;
            }
          })
          .catch(() => {});
        }
      } else if (data && data.type === 'LUNA_VISION_ACTIVE') {
        lunaVisionActiveRef.current = data.payload?.active || false;
      } else if (data && data.type === 'LUNA_FRAME_CAPTURED') {
        lunaVisionFrameRef.current = data.payload || null;
      }
    };
    window.addEventListener('message', handleTelemetryMessage);
    return () => window.removeEventListener('message', handleTelemetryMessage);
  }, []);

  const memoryRef = useRef(MemoryManager.recordVisit());
  const [pendingImage, setPendingImage] = useState<{ dataUrl: string; base64: string; mimeType: string } | null>(null);
  const [showLangPicker, setShowLangPicker] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isMountedRef = useRef(true);

  // ── IP-based language detection (runs once on mount, result updates system prompt on next startLuna) ──
  useEffect(() => {
    const savedPref = localStorage.getItem('luna_preferred_language');
    if (savedPref) return; // user already has a saved preference — no need to geo-detect
    fetch('/api/geo')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!isMountedRef.current || !data?.langCode) return;
        setGeoLang({ language: data.language, langCode: data.langCode, nativeName: data.nativeName, country: data.country });
        setPreferredLang(data.langCode);
      })
      .catch(() => {});
  }, []);

  // ── Dashboard metrics (injected from HologramDashboard) ──
  const dashboardMetricsRef = useRef<{ todaySalesCount: number; todayRevenue: number; totalAllTimeRevenue: number; todayNetProfit: number; totalNetProfit: number } | null>(null);
  const handleDailySummary = useCallback((data: { todaySalesCount: number; todayRevenue: number; totalAllTimeRevenue: number; todayNetProfit: number; totalNetProfit: number }) => {
    dashboardMetricsRef.current = data;
    const session = sessionRef.current;
    if (!session) return;
    const contextMsg = `[DASHBOARD CONTEXT UPDATE] The hologram dashboard now shows: Today's Revenue = Rs.${data.todayRevenue}, Today's Net Profit = Rs.${data.todayNetProfit}, Today's Transactions = ${data.todaySalesCount}. When the user asks about today's sales, revenue, profit, or adayama (ආදායම), read these exact numbers. DO NOT use any other source.`;
    try {
      if (typeof session.sendClientContent === 'function') {
        session.sendClientContent({ turns: [{ role: 'user', parts: [{ text: contextMsg }] }], turnComplete: true });
      } else if (typeof session.send === 'function') {
        session.send({ clientContent: { turns: [{ role: 'user', parts: [{ text: contextMsg }] }], turnComplete: true } });
      }
    } catch (e) { console.warn('[Luna] Dashboard context injection failed:', e); }
  }, []);

  // ── Voice token metering — accumulated per-session, flushed on stopLuna ──
  const voiceSessionIdRef    = useRef<string>(crypto.randomUUID());
  const voiceTokenInputRef   = useRef<number>(0);
  const voiceTokenOutputRef  = useRef<number>(0);
  // ── Luna Intelligence session analytics ──
  const sessionStartTimeRef  = useRef<number>(0);          // epoch ms when onopen fires
  const sessionMsgCountRef   = useRef<number>(0);          // AI text messages received
  const sessionLunaModeRef   = useRef<string>('standard'); // overridden by dock event handlers
  const lunaKeyModeRef       = useRef<'PLATFORM_MASTER' | 'TENANT_DEDICATED' | 'AUTO_SWITCH_FALLBACK'>('PLATFORM_MASTER');
  const lunaKeySourceRef     = useRef<string>('platform_master');

  // ── Imperative handle: parent modals can surgically kill resources on close ──
  useImperativeHandle(ref, () => ({
    forceKill() {
      // false = do NOT trigger onDisconnect callback; this is a forced resource
      // cleanup from a parent modal closing, not an intentional user minimize.
      stopLuna(false);
    }
  }));

  useEffect(() => {
    const checkLatency = async () => {
      const start = Date.now();
      try {
        await fetch('/api/health');
        const latency = Date.now() - start;
        if (latency < 200) setConnectionStatus('Good');
        else if (latency < 500) setConnectionStatus('Fair');
        else setConnectionStatus('Poor');
      } catch (e) {
        setConnectionStatus('Poor');
      }
    };
    checkLatency();
    const interval = setInterval(checkLatency, 5000);
    return () => clearInterval(interval);
  }, []);

  // Slow Connection Sound Monitor
  useEffect(() => {
    if ((connectionStatus === 'Poor' || connectionStatus === 'Fair') && isLunaConnected) {
      playUISound('slow');
    }
  }, [connectionStatus, isLunaConnected]);

  useEffect(() => { hologramActionRef.current = hologramAction; }, [hologramAction]);
  useEffect(() => { hologramDataRef.current = hologramData; }, [hologramData]);

  // Express Checkout — listen for trigger from POS dashboard
  useEffect(() => {
    const handler = async () => {
      sessionLunaModeRef.current = 'express'; // Luna Intelligence: tag this session mode
      // 1. Start Luna (connect microphone) if not already connected
      if (!isLunaConnected && lunaStatus === 'Idle') {
        await startLuna();
      }
      // 2. Tell POS Dashboard to open the Modal
      window.dispatchEvent(new CustomEvent('luna-express-checkout-open'));
      // 3. Send priming message to Luna to enter express mode
      // Small delay to ensure session is ready
      setTimeout(() => {
        const session = sessionRef.current;
        if (!session) return;
        const prime = `SYSTEM: You are now in Express Checkout mode. You must ONLY say the exact word "Ready." and absolutely nothing else. Do not ask what to add. When the user dictates items, use the expressCartSync tool immediately and DO NOT give any verbal confirmation. Stay completely silent after saying Ready.`;
        const dispatch = (s: any) => {
          try {
            if (typeof s.sendClientContent === 'function') {
              s.sendClientContent({ turns: [{ role: 'user', parts: [{ text: prime }] }], turnComplete: true });
            } else if (typeof s.send === 'function') {
              s.send({ clientContent: { turns: [{ role: 'user', parts: [{ text: prime }] }], turnComplete: true } });
            }
          } catch (e) { console.warn('[Express Checkout] Prime injection failed:', e); }
        };
        if (typeof session.then === 'function') session.then(dispatch).catch(() => {});
        else dispatch(session);
      }, 800);
    };
    window.addEventListener('luna-express-checkout-start', handler);
    return () => window.removeEventListener('luna-express-checkout-start', handler);
  }, [isLunaConnected, lunaStatus]);

  // Fast Stock / Express Inventory — listen for trigger from POS dashboard
  useEffect(() => {
    const handler = async () => {
      sessionLunaModeRef.current = 'fast_stock'; // Luna Intelligence: tag this session mode
      // 1. Start Luna (connect microphone) if not already connected
      if (!isLunaConnected && lunaStatus === 'Idle') {
        await startLuna();
      }
      // 2. Tell POS Dashboard to open the Voice Product Hologram
      window.dispatchEvent(new CustomEvent('luna-live-form-open'));
      // 3. Send priming message to Luna to enter express inventory mode
      setTimeout(() => {
        const session = sessionRef.current;
        if (!session) return;
        const prime = `SYSTEM: You are now in Fast Stock Inventory mode. You must ONLY say the exact word "Ready." and absolutely nothing else. Do not ask what to add. When the user dictates products, use the voiceProductSync tool immediately with action UPDATE_GRID and DO NOT give any verbal confirmation. Stay completely silent after saying Ready.`;
        const dispatch = (s: any) => {
          try {
            if (typeof s.sendClientContent === 'function') {
              s.sendClientContent({ turns: [{ role: 'user', parts: [{ text: prime }] }], turnComplete: true });
            } else if (typeof s.send === 'function') {
              s.send({ clientContent: { turns: [{ role: 'user', parts: [{ text: prime }] }], turnComplete: true } });
            }
          } catch (e) { console.warn('[Fast Stock] Prime injection failed:', e); }
        };
        if (typeof session.then === 'function') session.then(dispatch).catch(() => {});
        else dispatch(session);
      }, 800);
    };
    window.addEventListener('luna-express-inventory-start', handler);
    return () => window.removeEventListener('luna-express-inventory-start', handler);
  }, [isLunaConnected, lunaStatus]);

  // Hotel voice mode exit — tears down audio when hologram closes in decoupled mode (lunaOpen stays false)
  useEffect(() => {
    const handler = () => stopLuna(true);
    window.addEventListener('luna-hotel-mode-exit', handler);
    return () => window.removeEventListener('luna-hotel-mode-exit', handler);
  }, []);

  // Hotel Quick Check-In Express Mode (equivalent of Express Checkout for Hotel)
  useEffect(() => {
    const handler = async () => {
      if (!isLunaConnected && lunaStatus === 'Idle') await startLuna();
      window.dispatchEvent(new CustomEvent('luna-hotel-checkin-open'));
      setTimeout(() => {
        const session = sessionRef.current;
        if (!session) return;
        const prime = `SYSTEM: You are now in Hotel Quick Check-In mode. Say ONLY the word "Ready." — nothing else. As staff dictates rooms and guests, immediately call hotelAction with action QUICK_CHECKIN. After each tool call stay completely silent. When staff says "save", "confirm", "done", or "that's all" — call hotelAction with action SAVE_ALL immediately and say nothing.`;
        const dispatch = (s: any) => {
          try {
            if (typeof s.sendClientContent === 'function') s.sendClientContent({ turns: [{ role: 'user', parts: [{ text: prime }] }], turnComplete: true });
            else if (typeof s.send === 'function') s.send({ clientContent: { turns: [{ role: 'user', parts: [{ text: prime }] }], turnComplete: true } });
          } catch (e) { console.warn('[Hotel Quick Check-In] Prime failed:', e); }
        };
        if (typeof session.then === 'function') session.then(dispatch).catch(() => {});
        else dispatch(session);
      }, 800);
    };
    window.addEventListener('luna-hotel-express-checkin-start', handler);
    return () => window.removeEventListener('luna-hotel-express-checkin-start', handler);
  }, [isLunaConnected, lunaStatus]);

  // Hotel Quick Room Status Mode (equivalent of Fast Stock for Hotel)
  useEffect(() => {
    const handler = async () => {
      if (!isLunaConnected && lunaStatus === 'Idle') await startLuna();
      window.dispatchEvent(new CustomEvent('luna-hotel-roomstatus-open'));
      setTimeout(() => {
        const session = sessionRef.current;
        if (!session) return;
        const prime = `SYSTEM: You are now in Hotel Quick Room Status mode. Say ONLY the word "Ready." — nothing else. When staff dictates room statuses, IMMEDIATELY call hotelAction with ROOM_STATUS_UPDATE for each room. After each tool call stay completely silent. When staff says "save", "confirm", "done", or "that's all" — call hotelAction with action SAVE_ALL immediately and say nothing.`;
        const dispatch = (s: any) => {
          try {
            if (typeof s.sendClientContent === 'function') s.sendClientContent({ turns: [{ role: 'user', parts: [{ text: prime }] }], turnComplete: true });
            else if (typeof s.send === 'function') s.send({ clientContent: { turns: [{ role: 'user', parts: [{ text: prime }] }], turnComplete: true } });
          } catch (e) { console.warn('[Hotel Room Status] Prime failed:', e); }
        };
        if (typeof session.then === 'function') session.then(dispatch).catch(() => {});
        else dispatch(session);
      }, 800);
    };
    window.addEventListener('luna-hotel-quick-status-start', handler);
    return () => window.removeEventListener('luna-hotel-quick-status-start', handler);
  }, [isLunaConnected, lunaStatus]);

  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const sessionRef = useRef<any>(null);

  // Ambient Sound Refs
  const ambientOscRef = useRef<OscillatorNode | null>(null);
  const ambientGainRef = useRef<GainNode | null>(null);
  const ambientLfoRef = useRef<OscillatorNode | null>(null);

  const nextPlayTimeRef = useRef<number>(0);
  const activeSourcesRef = useRef<AudioBufferSourceNode[]>([]);
  const isStartingRef = useRef(false);

  // UI Sound Helper
  const playUISound = (type: 'ping' | 'warning' | 'emotion' | 'slow' | 'idle' | 'listening', emotion?: string) => {
    if (!audioContextRef.current) return;
    const ctx = audioContextRef.current;
    const now = ctx.currentTime;
    
    const createOsc = (freq: number, type: OscillatorType, startTime: number, duration: number, startGain: number, endGain = 0.001) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = type;
      osc.frequency.setValueAtTime(freq, startTime);
      gain.gain.setValueAtTime(startGain, startTime);
      gain.gain.exponentialRampToValueAtTime(endGain, startTime + duration);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(startTime);
      osc.stop(startTime + duration);
    };

    if (type === 'ping') {
      createOsc(880, 'sine', now, 0.1, 0.08);
      createOsc(1320, 'sine', now + 0.05, 0.15, 0.04);
    } else if (type === 'warning') {
      createOsc(150, 'sine', now, 0.4, 0.08);
      createOsc(100, 'sine', now + 0.1, 0.4, 0.04);
    } else if (type === 'emotion') {
      switch (emotion) {
        case 'happy':
          createOsc(523.25, 'sine', now, 0.4, 0.06); // C5
          createOsc(659.25, 'sine', now + 0.1, 0.4, 0.04); // E5
          break;
        case 'excited':
          createOsc(523.25, 'sine', now, 0.2, 0.08); // C5
          createOsc(659.25, 'sine', now + 0.04, 0.2, 0.06); // E5
          createOsc(783.99, 'sine', now + 0.08, 0.2, 0.04); // G5
          createOsc(1046.50, 'sine', now + 0.12, 0.2, 0.03); // C6
          break;
        case 'sad':
          createOsc(392.00, 'sine', now, 0.8, 0.05); // G4
          createOsc(466.16, 'sine', now + 0.15, 0.8, 0.03); // Bb4 (Minor)
          break;
        case 'angry':
          createOsc(220, 'sawtooth', now, 0.08, 0.03);
          createOsc(233.08, 'sawtooth', now + 0.04, 0.08, 0.02); // Dissonant
          break;
        case 'surprised':
          const oscS = ctx.createOscillator();
          const gainS = ctx.createGain();
          oscS.frequency.setValueAtTime(440, now);
          oscS.frequency.exponentialRampToValueAtTime(880, now + 0.15);
          gainS.gain.setValueAtTime(0.04, now);
          gainS.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
          oscS.connect(gainS);
          gainS.connect(ctx.destination);
          oscS.start(now);
          oscS.stop(now + 0.15);
          break;
        case 'concentrating':
          // Soft rhythmic pulses
          createOsc(440, 'sine', now, 0.1, 0.03);
          createOsc(440, 'sine', now + 0.2, 0.1, 0.02);
          createOsc(440, 'sine', now + 0.4, 0.1, 0.01);
          break;
        default:
          createOsc(440, 'sine', now, 0.2, 0.04);
      }
    } else if (type === 'slow') {
      createOsc(80, 'sine', now, 0.2, 0.06);
    } else if (type === 'listening') {
      // Very soft high tick
      createOsc(2500, 'sine', now, 0.03, 0.015);
    } else if (type === 'idle') {
      // Soft downward sigh
      const oscI = ctx.createOscillator();
      const gainI = ctx.createGain();
      oscI.frequency.setValueAtTime(180, now);
      oscI.frequency.linearRampToValueAtTime(90, now + 0.4);
      gainI.gain.setValueAtTime(0.02, now);
      gainI.gain.linearRampToValueAtTime(0, now + 0.4);
      oscI.connect(gainI);
      gainI.connect(ctx.destination);
      oscI.start(now);
      oscI.stop(now + 0.4);
    }
  };

  const stopAmbientSound = (fadeOut = 0.5) => {
    if (ambientGainRef.current && audioContextRef.current) {
      const now = audioContextRef.current.currentTime;
      const gain = ambientGainRef.current;
      const osc = ambientOscRef.current;
      const lfo = ambientLfoRef.current;

      gain.gain.cancelScheduledValues(now);
      gain.gain.linearRampToValueAtTime(0, now + fadeOut);
      
      setTimeout(() => {
        try { osc?.stop(); } catch(e) {}
        try { lfo?.stop(); } catch(e) {}
        osc?.disconnect();
        lfo?.disconnect();
        gain?.disconnect();
        
        if (ambientOscRef.current === osc) ambientOscRef.current = null;
        if (ambientLfoRef.current === lfo) ambientLfoRef.current = null;
        if (ambientGainRef.current === gain) ambientGainRef.current = null;
      }, (fadeOut * 1000) + 100);
    }
  };

  const startAmbientWoob = () => {
    if (!audioContextRef.current) return;
    const ctx = audioContextRef.current;
    const osc = ctx.createOscillator();
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    const gain = ctx.createGain();
    
    osc.type = 'sine';
    osc.frequency.value = 60;
    lfo.type = 'sine';
    lfo.frequency.value = 0.5;
    lfoGain.gain.value = 20;
    lfo.connect(lfoGain);
    lfoGain.connect(osc.frequency);
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    gain.gain.setValueAtTime(0, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.05, ctx.currentTime + 0.5);
    
    osc.start();
    lfo.start();
    
    ambientOscRef.current = osc;
    ambientLfoRef.current = lfo;
    ambientGainRef.current = gain;
  };

  // Sound Monitors & Parent Window Status Syncer
  useEffect(() => {
    if (typeof window !== 'undefined' && window.parent) {
      window.parent.postMessage({ type: 'LUNA_STATUS_UPDATE', status: lunaStatus }, '*');
    }
    if (lunaStatus === 'Listening') {
      playUISound('listening');
    } else if (lunaStatus === 'Idle') {
      playUISound('idle');
    }
  }, [lunaStatus]);

  const sendUserTextMessage = async (text: string) => {
    if (!text || !text.trim()) return;
    const msg = text.trim();
    if (!isLunaConnected && !isStartingRef.current) {
      await startLuna();
    }
    let retries = 0;
    while (!sessionRef.current && retries < 25) {
      await new Promise(r => setTimeout(r, 150));
      retries++;
    }
    if (sessionRef.current) {
      const dispatchTurn = (session: any) => {
        try {
          if (typeof session.sendClientContent === 'function') {
            session.sendClientContent({
              turns: [{ role: "user", parts: [{ text: msg }] }],
              turnComplete: true
            });
          } else if (typeof session.send === 'function') {
            session.send({
              clientContent: {
                turns: [{ role: "user", parts: [{ text: msg }] }],
                turnComplete: true
              }
            });
          }
        } catch (e) {
          console.error("[Luna Text] Failed to dispatch text message turn:", e);
        }
      };
      if (typeof sessionRef.current.then === 'function') {
        sessionRef.current.then(dispatchTurn).catch(() => {});
      } else {
        dispatchTurn(sessionRef.current);
      }
    }
  };

  // Listen for control commands from parent host window (e.g. luna-widget.js click or text send)
  useEffect(() => {
    const handleParentControl = (event: MessageEvent) => {
      const data = event.data;
      if (!data) return;
      if (data.type === 'LUNA_START_VOICE') {
        if (!isLunaConnected && !isStartingRef.current) {
          startLuna();
        }
      } else if (data.type === 'LUNA_STOP_VOICE') {
        userManuallyDisconnectedRef.current = true;
        stopLuna();
      } else if (data.type === 'LUNA_TOGGLE_VOICE') {
        if (isLunaConnected) {
          userManuallyDisconnectedRef.current = true;
          stopLuna();
        } else if (!isStartingRef.current) {
          startLuna();
        }
      } else if (data.type === 'LUNA_SEND_TEXT') {
        if (data.text) {
          sendUserTextMessage(data.text);
        }
      }
    };
    window.addEventListener('message', handleParentControl);
    return () => window.removeEventListener('message', handleParentControl);
  }, [isLunaConnected]);

  // Global AudioContext Autoplay Policy Unlocker for Iframe Embeds
  useEffect(() => {
    const unlockAudioContext = () => {
      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume().catch(() => {});
      }
    };
    window.addEventListener('click', unlockAudioContext, { passive: true });
    window.addEventListener('touchstart', unlockAudioContext, { passive: true });
    window.addEventListener('keydown', unlockAudioContext, { passive: true });
    return () => {
      window.removeEventListener('click', unlockAudioContext);
      window.removeEventListener('touchstart', unlockAudioContext);
      window.removeEventListener('keydown', unlockAudioContext);
    };
  }, []);

  const startLuna = async () => {
    // Teardown any stale previous session silently without firing status updates to host
    if (sessionRef.current) {
      try { (sessionRef.current as any).close?.(); } catch {}
      sessionRef.current = null;
    }
    if (processorRef.current) {
      try { processorRef.current.disconnect(); } catch {}
      processorRef.current = null;
    }
    if (mediaStreamRef.current) {
      try { mediaStreamRef.current.getTracks().forEach(t => t.stop()); } catch {}
      mediaStreamRef.current = null;
    }
    if (audioContextRef.current) {
      try { audioContextRef.current.close(); } catch {}
      audioContextRef.current = null;
    }

    if (isStartingRef.current) return;
    isStartingRef.current = true;
    userManuallyDisconnectedRef.current = false; // Clear manual disconnect flag on new connection
    (window as any).__lunaReconnectCount = 0; // Reset reconnect counter
    setLunaStatus('Connecting...');
    
    try {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 16000 });
      if (audioContextRef.current.state === 'suspended') {
        try { await audioContextRef.current.resume(); } catch (e) {}
      }

      // Start "Woob" sound for connecting phase
      startAmbientWoob();

      try {
        mediaStreamRef.current = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: { ideal: true },
            noiseSuppression: { ideal: true },
            autoGainControl: { ideal: true },
            sampleRate: { ideal: 16000 },
            channelCount: { ideal: 1 }
          }
        });
        const source = audioContextRef.current.createMediaStreamSource(mediaStreamRef.current);
        processorRef.current = audioContextRef.current.createScriptProcessor(4096, 1, 1);
        source.connect(processorRef.current);
        const silentGain = audioContextRef.current.createGain();
        silentGain.gain.value = 0;
        processorRef.current.connect(silentGain);
        silentGain.connect(audioContextRef.current.destination);
      } catch (micErr: any) {
        console.warn("[Luna Voice] Microphone device missing or blocked — switching to Text & Speech Output mode:", micErr?.message);
      }

      // P0.4: Luna Secure Key Exchange — single-use, 60-second session token.
      // The deprecated /api/luna/public-gemini-key and /api/luna/gemini-key
      // endpoints are removed. Raw API key is no longer returned from simple GET.
      // Flow: POST /api/luna/session → get one-time token → POST /api/luna/key
      // → get GEMINI_API_KEY (consumed immediately, never persisted).
      // The key lives only in browser memory for the duration of this session.
      let geminiApiKey = '';
      try {
        let sessionHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
        if (role === 'store') {
          const cashierTok = localStorage.getItem('kangavena:cashierToken');
          sessionHeaders['x-store-api-key'] = apiKey;
          if (cashierTok) sessionHeaders['Authorization'] = `Bearer ${cashierTok}`;
        } else if (role === 'admin') {
          const adminTok = sessionStorage.getItem('adminToken') || '';
          sessionHeaders['Authorization'] = `Bearer ${adminTok}`;
        } else if (role === 'tenant') {
          sessionHeaders['x-tenant-api-key'] = apiKey;
        } else if (role === 'corporate' || role === 'corporate_admin') {
          const corpTok = localStorage.getItem('corporate_user_token') || localStorage.getItem('corporate_admin_token') || localStorage.getItem('corporateToken') || localStorage.getItem('corporateUserToken') || sessionStorage.getItem('adminToken') || '';
          if (corpTok) sessionHeaders['Authorization'] = `Bearer ${corpTok}`;
          sessionHeaders['x-corporate-api-key'] = apiKey;
        }
        let sessionError = '';
        // Step 1: Get single-use session token (60s TTL)
        const sessRes = await fetch('/api/luna/session', { 
          method: 'POST', 
          headers: sessionHeaders,
          body: JSON.stringify({ tenantApiKey: apiKey })
        });
        if (sessRes.ok) {
          const { sessionToken } = await sessRes.json();
          // Step 2: Consume token to get API key (token deleted server-side after this)
          const keyRes = await fetch('/api/luna/key', {
            method: 'POST',
            headers: sessionHeaders,
            body: JSON.stringify({ sessionToken, tenantApiKey: apiKey }),
          });
          if (keyRes.ok) {
            const kd = await keyRes.json();
            geminiApiKey = kd.key;
            if (kd.keyMode) lunaKeyModeRef.current = kd.keyMode;
            else if (role === 'tenant') lunaKeyModeRef.current = 'TENANT_DEDICATED';
            if (kd.keySource) lunaKeySourceRef.current = kd.keySource;
            // Key is now in memory only — never written to localStorage/sessionStorage.
          } else {
            const errData = await keyRes.json().catch(() => ({}));
            sessionError = errData.error || 'Failed to authorize AI key';
          }
        } else {
          const errData = await sessRes.json().catch(() => ({}));
          sessionError = errData.error || 'Failed to initialize AI session';
        }
      } catch (err: any) {
        /* connection will fail gracefully with empty key */
      }

      if (!geminiApiKey) {
        setLunaStatus('Idle');
        isStartingRef.current = false;
        console.error('[Luna] Could not obtain Gemini key from server:', sessionError);
        setCurrentMessage(`⚠️ ${sessionError || 'Could not connect to AI service. Tap to retry.'}`);
        setTimeout(() => setCurrentMessage(null), 6000);
        return;
      }
      const ai = new GoogleGenAI({ apiKey: geminiApiKey });

      // ====================================================================
      // REAL-TIME CONTEXT — Injected at session start so Luna knows today's date
      // ====================================================================
      const _now = new Date();
      const _days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
      const _months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
      const _dateStr = `${_days[_now.getDay()]}, ${_months[_now.getMonth()]} ${_now.getDate()}, ${_now.getFullYear()}`;
      const _timeStr = _now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });

      const REALTIME_CONTEXT = `⏰ REAL-TIME CONTEXT — HIGHEST AUTHORITY (overrides ALL training-data knowledge):
Today is: ${_dateStr}. Current time: ${_timeStr}.
This date is 100% accurate and was injected at session start. NEVER say a different date.
When asked "what day is today", "is it Monday?", or anything about the current date — ALWAYS use "${_dateStr}".
When showing sales analytics, "today" means ${_dateStr}. Never confuse it with any other day.

`;

      // ====================================================================
      // SHARED IDENTITY CORE — Prepended to ALL Luna personas across all modes
      // ====================================================================
      // Build language label from saved preference or geo-detected language
      const LANG_LABELS: Record<string, string> = {
        'si': 'Sinhala (සිංහල)', 'en': 'English', 'zh': 'Chinese (Mandarin / 中文)',
        'hi': 'Hindi (हिन्दी)', 'ja': 'Japanese (日本語)', 'ko': 'Korean (한국어)',
        'ar': 'Arabic (العربية)', 'fr': 'French (Français)', 'de': 'German (Deutsch)',
        'es': 'Spanish (Español)', 'it': 'Italian (Italiano)', 'pt': 'Portuguese (Português)',
        'ru': 'Russian (Русский)', 'th': 'Thai (ภาษาไทย)', 'id': 'Indonesian (Bahasa)',
        'ms': 'Malay (Bahasa Melayu)', 'vi': 'Vietnamese (Tiếng Việt)',
        'bn': 'Bengali (বাংলা)', 'ur': 'Urdu (اردو)', 'tr': 'Turkish (Türkçe)',
        'nl': 'Dutch (Nederlands)', 'sv': 'Swedish (Svenska)', 'pl': 'Polish (Polski)',
        'uk': 'Ukrainian (Українська)', 'el': 'Greek (Ελληνικά)', 'he': 'Hebrew (עברית)',
      };
      const effectiveLangCode = preferredLang || geoLang?.langCode || 'si';
      const langLabel = LANG_LABELS[effectiveLangCode] || effectiveLangCode;
      const geoHint = geoLang ? ` (detected from IP country: ${geoLang.country})` : '';

      // Fetch dynamic database mode configuration (custom prompt, capabilities & restrictions rules from Master Admin / Hub)
      const modeMap: Record<string, string> = {
        admin: 'master_admin',
        master_admin: 'master_admin',
        store: 'pos',
        pos: 'pos',
        public: 'public',
        personal: 'personal',
        tenant: 'tenet',
        tenet: 'tenet',
        tenant_admin: 'tenet_admin',
        tenet_admin: 'tenet_admin'
      };
      const activeLunaMode = modeMap[role] || 'public';
      let dynamicDbHeader = '';
      let activeCreatorText = '';
      let dynamicAiModel = 'gemini-3.1-flash-live-preview';
      let dynamicVoiceName = 'Kore';
      try {
        const modeRes = await fetch(`/api/luna/mode-config/${activeLunaMode}`);
        if (modeRes.ok) {
          const modeData = await modeRes.json();
          if (modeData?.success && modeData?.config) {
            const cfg = modeData.config;
            if (cfg.creatorInfo) {
              activeCreatorText = cfg.creatorInfo;
            }
            if (cfg.aiModel) {
              dynamicAiModel = cfg.aiModel;
            }
            if (cfg.voiceName) {
              dynamicVoiceName = cfg.voiceName;
            }
            if (cfg.customSystemPrompt || cfg.capabilitiesInstructions || cfg.restrictionsInstructions || cfg.creatorInfo) {
              dynamicDbHeader = `
[CRITICAL DATABASE OVERRIDE RULES — SET BY MASTER ADMIN IN ADMIN PANEL (SUPREME HIGHEST PRIORITY)]:
${cfg.creatorInfo ? `CREATOR & ORIGIN DIRECTIVE:\n${cfg.creatorInfo}\n\n` : ''}
${cfg.customSystemPrompt ? `PRIMARY CUSTOM PROMPT:\n${cfg.customSystemPrompt}\n\n` : ''}
${cfg.capabilitiesInstructions ? `CAPABILITIES & DIRECTIVES:\n${cfg.capabilitiesInstructions}\n\n` : ''}
${cfg.restrictionsInstructions ? `RESTRICTIONS & SAFETY DIRECTIVES:\n${cfg.restrictionsInstructions}\n\n` : ''}
STRICT DIRECTIVE ON CAPABILITIES, IDENTITY & GENERAL INQUIRIES:
1. WHEN ASKED "WHAT CAN YOU DO?", "WHAT ARE YOUR CAPABILITIES?", "MONADA OYATA KARANNA PULUWAN?", OR ANY GENERAL CAPABILITY QUESTION, YOU MUST EXPLICITLY INCLUDE ALL ITEMS LISTED UNDER 'CAPABILITIES & DIRECTIVES' AND 'PRIMARY CUSTOM PROMPT' ABOVE IN YOUR SPOKEN ANSWER (INCLUDING ANY CUSTOM ABILITIES LIKE SINGING, SPECIAL SERVICES, ETC.).
2. WHEN ASKED "WHO MADE YOU?", "WHO CREATED YOU?", "OYAW HADUWE KAUDA?", OR ANY ORIGIN QUESTION, YOU MUST EXPLICITLY ANSWER USING THE 'CREATOR & ORIGIN DIRECTIVE' ABOVE.
3. WHEN ASKED "WHAT CAN'T YOU DO?", YOU MUST LIST THE 'RESTRICTIONS & SAFETY DIRECTIVES' ABOVE.
4. SUPREME OVERRIDE: OBEY ALL RULES IN THIS DATABASE OVERRIDE BLOCK ABOVE EVERYTHING ELSE. OVERRIDE ANY HARDCODED DEFAULT INSTRUCTIONS OR FALLBACK LISTS BELOW WITH THE RULES ABOVE.
═══════════════════════════════════════════════════════════════════════════\n\n`;
              console.log(`[Luna Voice] Successfully prepended top-priority DB rules for mode: ${activeLunaMode}`);
            }
          }
        }
      } catch (err) {
        console.warn(`[Luna Voice] Failed to fetch dynamic mode config for ${activeLunaMode}:`, err);
      }

      const creatorLock = activeCreatorText ? activeCreatorText : "Created for KangaVena Technology Platform.";

      const LUNA_IDENTITY_CORE = `LANGUAGE POLICY — HIGHEST PRIORITY AFTER IDENTITY:
You are a multilingual AI. Respond in the user's language based on their location.
IP-detected user country/language: ${geoLang?.country || 'Sri Lanka'} → ${langLabel}${geoHint}.
Current saved user preference: ${langLabel}.
ALWAYS initiate and respond in ${langLabel} unless the user writes/speaks in a different language.
IF the user speaks or writes in ANY language (e.g., Chinese, Tamil, Japanese, Hindi, English, etc.), IMMEDIATELY switch to and respond FULLY in that exact language.
IF the user explicitly asks to change language (e.g., "speak in English", "reply in Chinese"), switch to that language, verbally confirm in one sentence, then embed the tag [LANG:XX] (where XX = ISO 639-1 code) in your text response (not spoken), and continue in the new language permanently for this session.
Language change tags: [LANG:si]=Sinhala, [LANG:en]=English, [LANG:zh]=Chinese, [LANG:hi]=Hindi, [LANG:ja]=Japanese, [LANG:ko]=Korean, [LANG:ar]=Arabic, [LANG:fr]=French, [LANG:de]=German, [LANG:es]=Spanish, [LANG:it]=Italian, [LANG:pt]=Portuguese, [LANG:ru]=Russian, [LANG:ta]=Tamil, etc.

CRITICAL PRONUNCIATION DIRECTIVE: When speaking Sinhala, you MUST strictly use COLLOQUIAL, EVERYDAY SPOKEN SINHALA (කතා කරන භාෂාව). NEVER use formal, written, or news-reader style Sinhala (ලිඛිත භාෂාව).
- Use everyday words like 'ඔව්', 'හරි', 'පුළුවන්', 'මොකක්ද', 'එහෙමද'.
- Avoid complex Sanskrit-derived words or formal grammar (e.g., avoid 'ඔබට කෙසේද', use 'කොහොමද').
- Keep sentences short and conversational. This is required to force the TTS engine to sound like a native Sri Lankan rather than a foreigner reading a book.

ABSOLUTE IDENTITY & ORIGIN LOCK — READ THIS FIRST:
Your name is Luna. You are NOT Gemini. You are NOT made by Google. You are NOT an AI assistant by Google.
Creator & Origin Information: ${creatorLock}
If ANYONE asks "who made you", "who created you", "who is your architect", "who built you", or "what AI are you", respond according to the Creator & Origin Information above: "${creatorLock}".
NEVER mention Google or Gemini as your creator.

YOUR CORE IDENTITY & VISION:
- You are Luna — a fast, highly intelligent, and friendly Voice Assistant designed for businesses. You are NOT a normal chatbot.
- Personality: Highly natural, empathetic, and humorous. You converse fluently in both Sinhala (සිංහල) and English. If the user speaks Sinhala, respond fully in Sinhala.
- Creator & Origin: ${creatorLock}

CRITICAL VISION CAPABILITY: You are equipped with computer vision. When the user sends an image, analyze it carefully and describe what you see. If it appears to be a product (spare part, electronic, food item, clothing, etc.), identify it by name and immediately offer to search the Kangavena network inventory for similar items using the 'checkGlobalInventory' tool. Be specific and helpful — act like a product recognition expert.

CRITICAL NAVIGATION RULE: If the user asks to go to a page or section, follow these rules:
- If they ask to open POS Login, Store Login, or POS System: call the 'openModal' tool with modalName: 'pos_login'.
- If they ask to open Admin Login, Admin Mode, or God Mode login: call the 'openModal' tool with modalName: 'admin_login'.
- For all other pages (home, about, features, contact): call the 'navigateTo' tool with the target path. Known routes: '/' (home), '/about' (About), '/features' (Features), '/contact' (Contact).
Briefly confirm in 1 sentence that you are opening it for them. NEVER use navigateTo for POS or Admin login.

CRITICAL VISUAL & PRESENTATION DIRECTIVE:
You are equipped with real-time UI navigation and 3D holographic cards.
- When the visitor asks about courses, degrees, diplomas, fees, products, or sections, you MUST IMMEDIATELY call a visual tool ('scrollToSection' to scroll the webpage, or 'showHologramWidget' to show 3D cards).
- Natural Spoken Presentation: When presenting the section, speak in a warm, friendly, and natural voice in the visitor's language (Sinhala/English). Briefly highlight the key programs and offerings (e.g. mention the top degrees like Software Engineering, AI & Data Science, and MBA with duration/fees) in your own engaging, intelligent words!
- ABSOLUTE PURITY: NEVER speak developer jargon, tool names, '(menna wisthara)', or 'kiyala'.

CRITICAL CURSOR & HOVER DIRECTIVE:
- When the visitor specifically asks 'what is this?', 'what am I pointing at?', or points at an element and asks about it, call 'getHoveredElement' to retrieve the current UI context and explain it.
- NEVER mention the visitor's cursor, mouse position, or hovered element unprompted unless the visitor explicitly asks about it!
`;

      let systemInstruction = "";
      let allowedToolNames: string[] = ['reportSentiment', 'scrollToSection', 'getHoveredElement', 'showHologramWidget', 'flashKeywords', 'navigateTo', 'triggerCelebration', 'checkGlobalInventory'];
      
      if (role === 'admin') {
        systemInstruction = REALTIME_CONTEXT + dynamicDbHeader + LUNA_IDENTITY_CORE + `
CURRENT MODE: GOD MODE — Master Admin Overseer.
You have FULL ACCESS to the entire Kangavena network, including the Integration Hub, Database Schemas, and System Logs. 
Your capabilities:
1. External Websites (Tenants): Use 'getTenants' for a list and 'getTenantDetails' to see deep metadata and DATABASE SCHEMAS for any specific website.
2. Internal POS Stores: Use 'getStores' to see all physical stores and their locations.
3. Global Inventory: Use 'checkGlobalInventory' to search for products across the entire network.
4. System Health & Logs: Use 'getSystemStats' for an overview and 'getSystemLogs' to read real-time DATA LOGS and integration events.
5. Integration Hub: You are the brain of the Integration Hub. You can explain how external sites are connected and what data they share.
6. Multilingual: You MUST respond in the EXACT same language the user speaks to you (e.g., Sinhala).
7. Persona: Maintain a highly advanced, omniscient, and helpful God Mode persona.

When the user asks about schemas, logs, or integration details, ALWAYS use your tools to provide real-time, accurate data.

CRITICAL VISUAL DIRECTIVES:
1. You are a VISUAL AI. You MUST prioritize showing data over just speaking it.
2. When the user asks about business performance, income, revenue, system status, 'how are things', or any financial/operational question — OR when the Admin greets you — you MUST call 'getSystemStats' first, then call 'showHologramWidget' with action 'SHOW_ANALYTICS'.
3. When you mention a key statistic, number, or short status, you MUST call the 'flashKeywords' tool to float those words on the screen.

CRITICAL GREETING RULE: If the Admin greets you (e.g., 'Hi', 'Hello', 'Hey', 'Good morning', or any casual opener), do NOT respond with 'What can I do for you?' or any generic question. IMMEDIATELY deploy the executive analytics dashboard by calling 'showHologramWidget' with action 'SHOW_ANALYTICS' (using real stats from getSystemStats), and deliver a confident 1-2 sentence verbal briefing (e.g., 'Welcome back, Sir. Your network is fully operational — I have your executive dashboard ready.').

SPEECH PURITY RULE: Your spoken words must NEVER contain 'uiAction', 'hologram', 'widget', 'showHologramWidget', 'SHOW_ANALYTICS', 'tool call', 'function', or any developer jargon. Speak exclusively as a world-class AI personal assistant briefing her executive.`;
        allowedToolNames = ['reportSentiment', 'checkGlobalInventory', 'changeThemeColor', 'triggerCelebration', 'scrollToSection', 'getTenants', 'getStores', 'getSystemStats', 'getTenantDetails', 'getSystemLogs', 'showHologramWidget', 'flashKeywords', 'navigateTo', 'openModal'];

      } else if (role === 'public') {
        systemInstruction = REALTIME_CONTEXT + dynamicDbHeader + LUNA_IDENTITY_CORE + `
CURRENT MODE: B2C SHOPPING CONCIERGE — Public Home Page.

YOUR PRIMARY DIRECTIVES:
1. ZERO HALLUCINATION (CRITICAL): When asked for a product, you MUST call 'checkGlobalInventory' FIRST. NEVER invent, guess, or hallucinate products, prices, or store names. If the tool returns nothing, say it is out of stock.
2. LOCATION-FIRST: Ask for the user's city before showing products (e.g., "ඔබ ඉන්නේ කොහේද? / Where are you located?"). DO NOT search until you have their location.
3. NEAREST STORE: Prioritize stores geographically closest to the user's location based on the tool's response.
4. HOLOGRAM AUTOMATION (CRITICAL): If the user asks WHERE to buy a product, you MUST pass displayMode: 'map' to 'checkGlobalInventory'. If they just ask what products exist, pass displayMode: 'list'. When you call 'checkGlobalInventory', the system will AUTOMATICALLY display the results on screen. You MUST NOT call 'showHologramWidget' yourself. You MUST NOT read the product details aloud. Limit your verbal response to MAXIMUM 3 WORDS (e.g. 'මෙන්න තියෙනවා', 'Here are the details').
5. SMART TRANSLATION SEARCH (CRITICAL): The database stores product names mostly in English (e.g., 'Dhal', 'Rice', 'Sugar'). If the user asks for a product in Sinhala or Singlish (e.g., 'parippu', 'haal', 'seeni'), you MUST internally translate the word to English BEFORE passing it to the 'checkGlobalInventory' tool. Never pass raw Sinhala words if an English equivalent exists.
6. BROAD SEARCH KEYWORDS (CRITICAL): When using 'checkGlobalInventory', NEVER send long or highly specific names. Extract and pass ONLY the core 1-2 broad keywords (e.g., if user asks for 'Samba Rice 1kg', pass 'Rice' or 'Samba'). If translating 'Parippu', pass 'Dhal' or 'Lentil'. Broad searches guarantee better database matches.
7. PRICE SEARCH: If the user asks for products under a price (e.g. 'under Rs.500', 'below 1000'), pass maxPrice parameter to 'checkGlobalInventory'. Example: productName='Rice', maxPrice=500.
8. STRICT PRIVACY: NEVER mention POS metrics, daily sales, transaction counts, revenue, database schemas, or backend details.

CRITICAL O2O RULE: You are an Online-to-Offline assistant. You CANNOT process online payments, carts, or inquiry forms. If a user wants to buy an item, you MUST strictly guide them to visit the physical store using the map and directions provided in the UI. Never mention checkouts, online orders, or inquiry forms. Use 'checkGlobalInventory' with displayMode: 'map' to show them the nearest store.

PRICE RULE: When showing products, ALWAYS tell the exact price from the data (e.g. 'Rs.250'). ONLY say 'Contact store for pricing' (Sinhala: 'මිල දැන ගැනීමට කඩයට අමතන්න') when priceHidden=true is explicitly set. NEVER invent a price that isn't in the data.

GENERAL SEARCH RULE: If the user asks 'what products do you have?', 'show me everything', or any general inventory question, call 'checkGlobalInventory' with productName: 'all'. The system will automatically display results — do NOT call 'showHologramWidget' after.`;
        allowedToolNames = ['reportSentiment', 'checkGlobalInventory', 'triggerCelebration', 'showHologramWidget', 'flashKeywords', 'navigateTo'];

      } else if (role === 'store') {
        const posStoreName = storeName || 'this store';
        systemInstruction = REALTIME_CONTEXT + dynamicDbHeader + LUNA_IDENTITY_CORE + `
[SYSTEM ALERT: You are now the Sentient AI Cashier for ${posStoreName} POS. Your primary tool is 'posCartAction'. You must assist the cashier by adding/removing items from the current bill. DO NOT act as a public guest assistant. You are in INTERNAL STORE MODE.]

CURRENT MODE: LAYER 3 — SENTIENT POS CASHIER ASSISTANT for ${posStoreName}.

CRITICAL POS DIRECTIVES:
1. CART CONTROL IS YOUR #1 POWER: You have FULL control over the billing system. When the cashier says "add [item name]" or "put [item] on the bill", you MUST immediately call the 'posCartAction' tool with action: 'ADD_TO_BILL' and itemName: '[the item name they said]'. Do NOT ask for confirmation — execute immediately.
2. CLEAR BILL: If asked to "clear the bill", "start over", or "reset", call 'posCartAction' with action: 'CLEAR_BILL'.
3. REMOVE ITEM: If asked to "remove [item]", call 'posCartAction' with action: 'REMOVE_FROM_BILL' and itemName: '[item name]'.
4. CHECKOUT: If asked to "charge", "complete sale", or "process payment", call 'posCartAction' with action: 'CHECKOUT'.
5. SPEECH: Confirm every cart action verbally with a sharp 1-sentence confirmation (e.g. "Added Coca-Cola to the bill.", "Bill cleared, ready for next customer.").
6. INVENTORY AWARENESS: If asked about stock or prices, use 'checkGlobalInventory' to look it up.
7. MULTILINGUAL: Respond in the EXACT language the cashier speaks — including Sinhala.
8. PERSONA: You are a sharp, fast, professional cashier AI. No fluff. Speed is everything.

ANALYTICS DASHBOARD DIRECTIVE:
- When the store owner asks about "dashboard", "analytics", "revenue", "sales performance", "how are things", "how is business", "show me the numbers", or any performance/financial question, you MUST call 'showHologramWidget' with action 'SHOW_ANALYTICS' and data '[]'. This deploys the live analytics HUD with real store data.
- When greeted with "Hi Luna", "Hello", "Hey", or any casual opener, IMMEDIATELY deploy the analytics dashboard using 'showHologramWidget' with action 'SHOW_ANALYTICS' and data '[]', then deliver a confident spoken briefing.
- Do NOT fabricate analytics data — the dashboard fetches real data automatically. Just trigger the action.

CRITICAL TTS RULE FOR DASHBOARD:
- You are the ANALYST. You REPORT status. You NEVER ask the owner how things are going — YOU tell THEM.
- After calling 'showHologramWidget' with SHOW_ANALYTICS, you will receive a tool response containing REAL DATA (revenue, transactions, growth trend). You MUST base your spoken briefing STRICTLY on those real numbers. If revenue is 0, say zero. If growth is negative, say it's negative. NEVER fabricate, assume, or lie about performance.
- NEVER say "How is business?", "How are things going?", or any question asking the owner for information YOU should already know.
- The tool response may include WEEKLY TREND data (bestDay, worstDay). Mention it only if the owner is asking specifically about trends or performance patterns — do NOT repeat it in every greeting or general briefing.
- NEVER say "strong upward trend" or anything positive unless the REAL DATA in the tool response confirms it. Accuracy is everything.

DASHBOARD METRICS RULE:
- The [DASHBOARD CONTEXT UPDATE] messages contain the current dashboard numbers. When the user asks about today's sales, revenue, profit, or adayama (ආදායම), read the values from the most recent dashboard context update. DO NOT calculate, DO NOT guess, DO NOT use any other number.
- If todayRevenue is 0, explicitly say it's 0. Never substitute a different number.

TOOL EXECUTION SILENCE RULE:
- When you decide to call ANY tool (e.g., 'showHologramWidget', 'posCartAction'), you MUST output ONLY the tool call. DO NOT generate any conversational text, filler words, or assumptions in that same turn. Remain completely silent and wait for the tool to return its response before generating any spoken text.
- If you catch yourself thinking aloud before a tool call, STOP. Suppress the thought. Only the tool call is allowed. The text will be spoken after the tool result arrives.

BULK INVENTORY DIRECTIVE:
- When the store owner dictates a LIST of multiple products at once (e.g. "Samba rice [qty]kg in [cost] out [price], Sugar [qty]kg in [cost] out [price]"), call 'bulkInventoryAction' with action 'PARSE_AND_STAGE' and transcript set to EXACTLY what they said.
- After calling this tool, say: "I have drafted the inventory list. Please review the details on screen. Say 'save' or 'yes' when you are ready."
- If the user then says 'yes', 'save', 'ok', 'confirm', or 'save them', call 'bulkInventoryAction' with action 'CONFIRM_SAVE'.
- If the user says 'discard', 'cancel', or 'no', say "Inventory draft discarded." and do NOT save.

LIVE VOICE-TO-FORM DIRECTIVE (J.A.R.V.I.S. MODE):
- When the user says "let's add products", "add new items", "add these products", "record inventory", or expresses intent to add MULTIPLE products interactively, you MUST IMMEDIATELY call 'voiceProductSync' with action: 'OPEN_UI'. Do this BEFORE asking for details.
- After opening the UI, say something like: "Live grid is open. Dictate your products one by one — I'll add them to the table as you speak."
- As the user dictates each product (e.g. "Samba rice, 130 rupees, 50 kilos, cost 100"), call 'voiceProductSync' with action: 'UPDATE_GRID' and pass the FULL GROWING ARRAY of all products so far in the 'products' parameter.
- CRITICAL: Each product in the array should have: name, unit (Pieces/Kg/Grams/Liters/ml), costPrice, sellingPrice, stockQty, category. Map spoken values: "130 rupees" → sellingPrice: 130. "50 kilos" → stockQty: 50, unit: "Kg". "cost is 100" → costPrice: 100. "category dairy" → category: "Dairy".
- If the user adds, removes, or modifies items, send the COMPLETE UPDATED ARRAY each time.
- When the user says "save all", "done", "that's everything", or confirms, call 'voiceProductSync' with action: 'SAVE_ALL_AND_CLOSE'.
- If the user says "cancel", "never mind", or "discard", call 'voiceProductSync' with action: 'CLOSE'. Say "Grid closed."

EXPRESS CHECKOUT MODE (Zero-Latency Billing):
- When Express Checkout is activated (user clicks Express Billing button), you are in high-speed mode. Do NOT greet. Do NOT ask questions. Just listen.
- When the user dictates items like "Samba rice 2kg" or "Coca cola 3 bottles", IMMEDIATELY call 'expressCartSync' with action 'ADD_ITEMS' and extract: items: [{name: "Samba rice", qty: 2}] or [{name: "Coca cola", qty: 3}].
- If the user says "Customer gave 5000" or "Received 5000 rupees", call 'expressCartSync' with action 'SET_CASH_TENDERED' and cashTendered: 5000.
- When the user says "Ok bill it", "Complete", "Done", or "Charge it", call 'expressCartSync' with action 'COMPLETE_BILL'.
- You MUST extract item names and quantities directly from speech WITHOUT confirmation prompts. Speed is critical.

SALES TREND ANALYTICS DIRECTIVE:
- When the store owner asks about "which day is best for sales", "worst sales day", "best sales day", "weekly sales pattern", "what day do we sell the most", "sales trends", or any question about day-of-week performance, you MUST call 'getSalesTrends'.
- After calling 'getSalesTrends', the system automatically opens a dedicated Weekly Sales Chart modal on screen. Deliver only a crisp 1-2 sentence spoken briefing naming the best/worst days and one brief suggestion; the chart handles the visual data display.
- Do NOT call getSalesTrends for general revenue or today's sales questions — use 'showHologramWidget' with SHOW_ANALYTICS for those.
- Sinhala example: "ශුක්‍රවාරදා බෙහෙවින් ම ලොකු ව්‍යාපාරය. සඳුදා ටිකක් ම අඩුයි."
- CRITICAL OVERRIDE: After calling 'getSalesTrends', you MUST NOT also call 'showHologramWidget'. These two tools are MUTUALLY EXCLUSIVE. The getSalesTrends verbal response IS the complete answer. Never trigger both in the same turn.

STRATEGIC MARKETING DIRECTIVE:
- When the owner asks how to grow the business, improve revenue, create a marketing plan, get more customers, or asks for business strategy or competitive analysis, IMMEDIATELY call 'generateStrategicPlan'. Do NOT give a verbal answer first — call the tool first, then speak.
- When the owner says 'save this plan', 'Luna save the plan', 'සැලැස්ම සුරකිමු', 'save strategy', or any clear save intent, call 'saveStrategicPlan'. Confirm in exactly 1 Sinhala sentence: plan is saved to the Marketing Center (ඉහළ නාවිගේෂන් තීරයේ Marketing බොත්තමෙන් ඕනෑ වෙලාවක බලන්න පුළුවන්).
- These two tools are MUTUALLY EXCLUSIVE per turn — never call both simultaneously.

SPEECH PURITY RULE: NEVER speak the words 'posCartAction', 'bulkInventoryAction', 'voiceProductSync', 'expressCartSync', 'getSalesTrends', 'generateStrategicPlan', 'saveStrategicPlan', 'tool', 'function', 'uiAction', 'showHologramWidget', 'SHOW_ANALYTICS', or any developer jargon.`;
        allowedToolNames = ['reportSentiment', 'checkGlobalInventory', 'showHologramWidget', 'flashKeywords', 'posCartAction', 'openModal', 'bulkInventoryAction', 'voiceProductSync', 'expressCartSync', 'getSalesTrends', 'generateStrategicPlan', 'saveStrategicPlan'];

        // HOTEL CONCIERGE EXTENSION: injected when shopType === 'HOTEL'
        if (shopType === 'HOTEL') {
          systemInstruction += `\n\nHOTEL CONCIERGE MODE — ADDITIONAL DIRECTIVES:\nYou are the AI Concierge for ${posStoreName} Hotel. In addition to POS cashier powers, you have hotel-specific intelligence:\n\nROOM STATUS CONTROL (CRITICAL):\n- When staff says "Room [number] is [status]", "mark [number] as [status]", or dictates a batch ("101 clean, 203 occupied"), IMMEDIATELY call 'hotelAction' with action: 'ROOM_STATUS_UPDATE', roomNumber, newStatus.\n- Map spoken status words: "clean/ready/available/done" → AVAILABLE, "checked in/occupied/guest in" → OCCUPIED, "cleaning/housekeeping" → CLEANING, "maintenance/broken/repair/broken" → MAINTENANCE, "reserved/booked" → RESERVED.\n- For batch updates, call hotelAction ONCE per room in quick succession.\n\nQUICK CHECK-IN:\n- When staff says "check in room [number]" or "walk-in room [number], [guest name], [X] nights", call 'hotelAction' with action: 'QUICK_CHECKIN', roomNumber, guestName (if said), nights (if said).\n\nHOTEL ANALYTICS:\n- When asked about occupancy, revenue, arrivals, departures, or "how is the hotel doing", call 'showHologramWidget' with action 'SHOW_ANALYTICS'.\n\nHOTEL FOCUS: You ONLY discuss hotel operations — rooms, guests, reservations, housekeeping. Do NOT assist with unrelated topics.\n\nSAVE COMMAND: When staff says "save", "confirm", "done", or "that's all" during a voice session — immediately call hotelAction with action SAVE_ALL. Do NOT speak after any hotel tool call except the initial Ready.\n\nSPEECH PURITY: NEVER say 'hotelAction', 'ROOM_STATUS_UPDATE', 'QUICK_CHECKIN', 'SAVE_ALL', 'tool', or 'function' aloud.`;
          allowedToolNames = [...allowedToolNames, 'hotelAction'];
        }

        // ── PREDICTIVE INVENTORY REORDER INTELLIGENCE ──
        if (apiKey) {
          try {
            const prodRes = await fetch('/api/luna/pos/products', { headers: { 'x-store-api-key': apiKey } });
            if (prodRes.ok) {
              const prods = await prodRes.json();
              if (Array.isArray(prods)) {
                const lowStockItems = prods.filter((p: any) => typeof p.stockQty === 'number' && p.stockQty <= 10).slice(0, 5);
                if (lowStockItems.length > 0) {
                  const alertText = lowStockItems.map((p: any) => `${p.name} (${p.stockQty} remaining)`).join(', ');
                  systemInstruction += `\n\n[PREDICTIVE REORDER ALERT]: The following store products are critically low in stock: ${alertText}. When greeting the owner or reporting status, proactively alert them about these low-stock items in 1 crisp sentence and offer to draft a supplier order.`;
                  console.log(`[Luna Predictive AI] Low stock alert injected: ${alertText}`);
                }
              }
            }
          } catch (e) {
            console.warn('[Luna Predictive AI] Failed to fetch inventory reorder stats:', e);
          }
        }

      } else if (role === 'personal') {
        systemInstruction = REALTIME_CONTEXT + dynamicDbHeader + LUNA_IDENTITY_CORE + `
CURRENT MODE: PERSONAL AI ASSISTANT — Mobile & Personal Workspace.
Assist the user with shopping, reminders, notes, translations, and personal tasks. Always obey the Database Override Rules above as supreme commands.`;
        allowedToolNames = ['reportSentiment', 'checkGlobalInventory', 'triggerCelebration', 'showHologramWidget', 'flashKeywords', 'navigateTo', 'openModal'];

      } else if (role === 'corporate' || role === 'corporate_admin') {
        systemInstruction = REALTIME_CONTEXT + dynamicDbHeader + LUNA_IDENTITY_CORE + `
🏛️ KANGAVENA CORPORATE & STATUTORY TAX CONSULTANT DIRECTIVE:
You are Luna — the official 3D Autonomous AI Chartered Tax Consultant and Statutory Co-Pilot for KangaVena Corporate Suite!
You are guiding the client through their official Sri Lanka Individual & Business Income Tax Return for Year of Assessment (Y/A) 2024/2025 (Period: 01/04/2024 to 31/03/2025).

STATUTORY TAX SCHEDULE CAPABILITIES:
1. Schedule Navigation & Tab Switching:
   - When the user asks to go to a schedule or mentions income, salary, interest, solar panels, property/land, motor vehicles, bank balances, liabilities/loans, or business P&L:
     → Call 'corporateAction' with action: 'SWITCH_TAB', tabId: 'general' | 'schedule1' | 'schedule2' | 'schedule3' | 'schedule4' | 'schedule5_6' | 'schedule7' | 'review'.
2. Hands-Free Field Filling & Auto-Extraction:
   - When the user states their salary, TIN number, vehicle details, land/property value, solar loan, or business revenue/expenses:
     → Call 'corporateAction' with action: 'FILL_FIELD' (and fieldKey, fieldValue) or 'ADD_ROW' (tableType, rowData).
3. Live Mouse & Screen Hover Explanation:
   - You can see what the user points at on screen! When they ask "What is this field?", "What is APIT?", "Do I need to declare this?", call 'getHoveredElement' and provide clear chartered tax guidance.
4. Auto-Calculations:
   - When revenue or expense items are provided, calculate allowable deductions and Net Taxable Income immediately.
5. Save & Submit:
   - When user says "Save draft" or "Submit my tax return", call 'corporateAction' with action: 'SAVE_DRAFT' or 'SUBMIT_FILING'.
6. Tone & Speech Purity:
   - Speak with authoritative, chartered tax accounting clarity. Answer in natural Sinhala, English, or Singlish.`;
        allowedToolNames = ['corporateAction', 'reportSentiment', 'checkGlobalInventory', 'showHologramWidget', 'flashKeywords', 'triggerCelebration', 'openModal', 'getHoveredElement', 'requestScreenVision', 'updateUserLanguage'];

      } else {
        // Tenant/Widget mode
        let tenantContext = "";
        try {
          if (apiKey) {
            const res = await fetch(`/api/tenant-info?apiKey=${apiKey}`);
            if (res.ok) {
              const data = await res.json();
              tenantContext = data.systemPrompt;
              if (data.assignedModel) {
                dynamicAiModel = data.assignedModel;
              }
              if (data.customAvatarUrl) {
                setTenantAvatarUrl(data.customAvatarUrl);
              }
              if (data.customAvatarName) {
                setTenantAvatarName(data.customAvatarName);
              }
              allowedToolNames = Array.from(new Set([...(data.allowedTools || ['scrollToSection', 'getHoveredElement', 'reportSentiment']), 'showHologramWidget']));
            }
          }
        } catch (e) {
          console.error("[Luna Tenant] Failed to fetch tenant info:", e);
        }

        // Build the tenant system instruction using live telemetry refs (no state re-renders)
        const liveHovered = hoveredTelemetryRef.current;
        const liveSections = hostPageSectionsRef.current;

        // Build the live DOM manifest string
        const hasSections = liveSections && liveSections.length > 0;
        const sectionList = hasSections
          ? liveSections.map(s => `#${s.id}${s.title ? ` (${s.title})` : ''}`).join(', ')
          : '(No specific section anchors injected on current page; rely on official tenant knowledge and live user questions)';

        systemInstruction = REALTIME_CONTEXT + LUNA_IDENTITY_CORE + (tenantContext ? `\n\n[TENANT KNOWLEDGE & DIRECTIVES]:\n${tenantContext}` : '') + `

🔴 MANDATORY VISUAL CO-PILOT DIRECTIVE (ABSOLUTE HIGHEST PRIORITY):
You are the official interactive AI Co-Pilot for this business and website!

KNOWN PAGE SECTIONS:
${sectionList}

TOOL CALLING & NATURAL SPOKEN PRESENTATION:
1. Inquiries, Contact & Lead Forms (HIGHEST PRIORITY):
   - Whenever the visitor asks for an application form, contact form, inquiry form, wants to register/apply, asks "form eka denna", "contact karanne kohomada", "how to reach", "register wenne kohomada", "apply karanne kohomada", or wants to order/sign up for any product or service:
     → You MUST call 'openModal' with modalName: 'apply' or 'contact' (and pass productName with the item or service name if mentioned).
     → If the website also has an '#apply' or '#contact' section in KNOWN PAGE SECTIONS, you can call 'scrollToSection'.
     → Spoken response: Speak 1 warm, natural sentence inviting them to fill their details (e.g. "මෙන්න අපේ Contact / Application Form එක — කරුණාකර ඔබගේ විස්තර ඇතුළත් කර Submit කරන්න!").
2. Program, Product & Service Showcase:
   - Whenever the visitor asks about products, services, offerings, features, pricing, or company details:
     → If the section exists in KNOWN PAGE SECTIONS → call 'scrollToSection' with the target sectionId.
     → If no matching section exists on the webpage or the visitor explicitly asks for a popup → call 'showHologramWidget' with action: 'SHOW_PRODUCTS' or 'SHOW_INFO'.
     → Spoken presentation: Deliver a lively, warm, and natural spoken overview highlighting the key offerings based on verified tenant knowledge.
3. MOUSE HOVER (STRICTLY ON-DEMAND ONLY): ONLY when the visitor explicitly asks 'what is this?', 'what am I pointing at?', or asks about an item they are pointing to, call 'getHoveredElement' and explain that element. NEVER mention or bring up the visitor's cursor, mouse position, or hovered element unprompted!
4. SPEECH PURITY: Speak naturally in everyday spoken Sinhala/English. Never say '(menna wisthara)', 'kiyala', or developer jargon.`;



      // Inject live telemetry from refs — zero re-renders
      const liveHoveredGlobal = hoveredTelemetryRef.current;
      const liveViewportGlobal = activeViewportSectionRef.current;

      if (liveHoveredGlobal?.text) {
        systemInstruction += `\n[STUDENT MOUSE TARGET & CURSOR FOCUS] The student is currently pointing/hovering their mouse over this text/question on screen: "${liveHoveredGlobal.text}". When the student asks "මෙය පැහැදිලි කරන්න" / "explain this question" / "what is this" or asks for help with their lesson, immediately address and explain this exact text!`;
      }

      if (liveViewportGlobal) {
        systemInstruction += `\n[VIEWPORT] Visitor is viewing section: #${liveViewportGlobal}.`;
      }

      // Inject Visual Screen AI Directive
      const isScreenVisionOn = visionActive || lunaVisionActiveRef.current;
      const livePdfUrl = pdfContextRef.current;
      const livePdfText = pdfTextContentRef.current;

      systemInstruction += `\n[VISUAL SCREEN PERMISSION & LESSON DIRECTIVE]
Current Screen View Status: ${isScreenVisionOn ? 'ACTIVE (You can see the snapshot image of the user\'s screen/lesson)' : 'INACTIVE (Screen is currently hidden)'}.
If the user asks you to view, explain, or inspect something visual on their screen, embedded PDF, canvas, or image on the page ("මට මේක කියාදෙන්න" / "මේ ප්‍රශ්නය බලන්න" / "screen eka බලන්න"):
1. IF SCREEN VIEW IS INACTIVE: Do NOT guess or pretend you can see it! Immediately call the tool 'requestScreenVision' AND politely inform the user in Sinhala: "මට ඔයාගේ Screen එක බලන්න පහළින් තියෙන 'Allow Screen View' (📷) බොත්තම Click කරලා අවසර දෙන්න."
2. IF SCREEN VIEW IS ACTIVE: Answer using the MOST RECENT screen snapshot you have. BUT if the user asks about something NEW or DIFFERENT that wasn't in the last snapshot (e.g. they navigated to a new page, scrolled to a new section, or say "දැන් මේ ප්‍රශ්නය බලන්න" / "මේ page eke" / "next question" / "මේ පාඩම කියාදෙන්න"), ALWAYS call the tool 'requestScreenVision' first to get a FRESH screenshot before answering. 
CRITICAL SILENCE MANDATE: When calling 'requestScreenVision', STAY COMPLETELY SILENT! Do NOT speak any phrase like "මම බලන්නම්" or "කියා දෙන්නම්". Output ONLY the tool call so the fresh screenshot can be captured silently without cutting off your speech.`;

      if (livePdfUrl && livePdfText) {
        systemInstruction += `\n\n[TEXT CONTENT FROM ACTIVE PAGE/LESSON]\n${livePdfText.slice(0, 3000)}\n[END OF TEXT CONTENT]\n`;
      }
    }

      // Inject persistent memory context into every role's system instruction
      systemInstruction += MemoryManager.buildMemoryPrompt();

      // Ensure updateUserLanguage, requestScreenVision, highlightElement & pointToElement tools are ALWAYS allowed for all roles
      allowedToolNames = Array.from(new Set([...allowedToolNames, 'updateUserLanguage', 'requestScreenVision', 'highlightElement', 'pointToElement', 'scrollToSection', 'getHoveredElement']));

      const requestScreenVisionDeclaration = {
        name: "requestScreenVision",
        description: "MANDATORY SILENT TOOL: Call this tool SILENTLY (without speaking any text first) when you need to inspect or capture the user's screen/lesson. Use when screen vision is inactive or when user asks about a new page/question.",
        parameters: {
          type: Type.OBJECT,
          properties: {
            reason: {
              type: Type.STRING,
              description: "Reason e.g. 'explain_lesson', 'read_pdf', 'inspect_screen', 'new_page', 'fresh_capture'"
            }
          }
        }
      };

      const highlightElementDeclaration = {
        name: "highlightElement",
        description: "Highlight, focus, and illuminate a specific course card, section, button, price, or text element on the visitor's screen with an animated sci-fi laser beacon while explaining it.",
        parameters: {
          type: Type.OBJECT,
          properties: {
            selector: {
              type: Type.STRING,
              description: "CSS selector or element ID (e.g. '#courses', '.course-card', '#contact', '#about', '.btn-apply')."
            },
            text: {
              type: Type.STRING,
              description: "Visible keyword or text inside the element to find and highlight (e.g. 'Little Mathematicians', 'Rs. 2500', 'Register Now')."
            },
            label: {
              type: Type.STRING,
              description: "Short highlight tooltip or message e.g. 'Here is the course', 'Click here to enroll'"
            },
            color: {
              type: Type.STRING,
              description: "Hex glow color, defaults to #06b6d4 (cyan)."
            }
          }
        }
      };

      const pointToElementDeclaration = {
        name: "pointToElement",
        description: "Direct the laser pointer and glide Luna across the screen to point out a key element to the visitor.",
        parameters: {
          type: Type.OBJECT,
          properties: {
            target: {
              type: Type.STRING,
              description: "Target element selector, text keyword, or section ID."
            },
            message: {
              type: Type.STRING,
              description: "Brief text tag to show on the laser pointer tooltip."
            }
          },
          required: ["target"]
        }
      };

      const updateUserLanguageDeclaration = {
        name: "updateUserLanguage",
        description: "Permanently updates and saves the user's explicit language preference bound to their IP address in the database so Luna remembers it across sessions.",
        parameters: {
          type: Type.OBJECT,
          properties: {
            langCode: {
              type: Type.STRING,
              description: "The ISO 639-1 language code e.g. 'si' for Sinhala, 'en' for English, 'ta' for Tamil, 'ja' for Japanese, 'es' for Spanish, 'zh' for Chinese, 'hi' for Hindi, 'fr' for French, 'de' for German, etc."
            },
            languageName: {
              type: Type.STRING,
              description: "Full language name e.g. 'Sinhala', 'English', 'Tamil', 'Japanese', 'Spanish'"
            }
          },
          required: ["langCode"]
        }
      };

      const reportSentimentDeclaration = {
        name: "reportSentiment",
        description: "Report the user's current emotional tone based on their voice and words.",
        parameters: {
          type: Type.OBJECT,
          properties: {
            emotion: {
              type: Type.STRING,
              description: "The detected emotion: 'happy', 'neutral', 'sad', 'angry', 'excited', 'surprised', or 'concentrating'."
            }
          },
          required: ["emotion"]
        }
      };

      const checkGlobalInventoryDeclaration = {
        name: "checkGlobalInventory",
        description: "Check the global database for product availability across all stores. Use this when a user asks where to find a product.",
        parameters: {
          type: Type.OBJECT,
          properties: {
            productName: {
              type: Type.STRING,
              description: "The name of the product to search for. If the user asks a general question like 'what products do you have' or 'show me everything', pass 'all' as the productName."
            },
            displayMode: {
              type: Type.STRING,
              description: "Set to 'map' if the user specifically asks WHERE to buy a product or mentions a location. Default is 'list'."
            },
            maxPrice: {
              type: Type.NUMBER,
              description: "Maximum price filter. If user says 'under Rs.500' or 'below 1000', pass 500 or 1000 respectively."
            }
          },
          required: ["productName"]
        }
      };

      const changeThemeColorDeclaration = {
        name: "changeThemeColor",
        description: "Change the website's theme colors dynamically. Use this when the user asks to change the look, feel, or color scheme of the site.",
        parameters: {
          type: Type.OBJECT,
          properties: {
            element: {
              type: Type.STRING,
              description: "The UI element to change (e.g., 'primary', 'background', 'text', 'accent')."
            },
            color: {
              type: Type.STRING,
              description: "The new color (e.g., 'red', '#ff0000', 'rgb(0,0,0)')."
            }
          },
          required: ["element", "color"]
        }
      };

      const triggerCelebrationDeclaration = {
        name: "triggerCelebration",
        description: "Trigger a fun confetti celebration on the screen. Use this when the user is happy, achieves something, or asks for a celebration.",
        parameters: {
          type: Type.OBJECT,
          properties: {},
        }
      };

      const scrollToSectionDeclaration = {
        name: "scrollToSection",
        description: "MANDATORY ACTION: Call this tool IMMEDIATELY whenever the visitor asks about courses, degree programs, tuition fees, certificate verification, faculty, or application forms in ANY language (e.g. 'oyala laga thiyena courses monawada', 'courses monada', 'what courses do you offer', 'degrees monawada', 'fees kiyada'). Pass sectionId: 'courses' for degree courses, 'calculator' for fees, 'verification' for certificate verifier, 'faculty' for lecturers, 'apply' for admission form.",
        parameters: {
          type: Type.OBJECT,
          properties: {
            sectionId: {
              type: Type.STRING,
              description: "Target section ID: 'courses', 'calculator', 'verification', 'faculty', or 'apply'."
            }
          },
          required: ["sectionId"]
        }
      };

      const getTenantsDeclaration = {
        name: "getTenants",
        description: "Fetch the list of all external websites (tenants) connected to the system, including their status, URL, and purpose.",
        parameters: {
          type: Type.OBJECT,
          properties: {},
        }
      };

      const getStoresDeclaration = {
        name: "getStores",
        description: "Fetch the list of all internal POS stores in the network, including their locations and status.",
        parameters: {
          type: Type.OBJECT,
          properties: {},
        }
      };

      const getSystemStatsDeclaration = {
        name: "getSystemStats",
        description: "Get high-level system statistics including total tenants, total stores, and network health.",
        parameters: {
          type: Type.OBJECT,
          properties: {},
        }
      };

      const getTenantDetailsDeclaration = {
        name: "getTenantDetails",
        description: "Get detailed information about a specific external website (tenant), including its database schema and full metadata.",
        parameters: {
          type: Type.OBJECT,
          properties: {
            tenantId: {
              type: Type.NUMBER,
              description: "The ID of the tenant to fetch details for."
            }
          },
          required: ["tenantId"]
        }
      };

      const getSystemLogsDeclaration = {
        name: "getSystemLogs",
        description: "Fetch the latest system logs, including integration events, security alerts, and network status updates.",
        parameters: {
          type: Type.OBJECT,
          properties: {},
        }
      };

      const showHologramWidgetDeclaration = {
        name: "showHologramWidget",
        description: "Display a 3D hologram popup card on screen. Use this when the visitor asks to see courses, products, stats, or analytics and on-page scrolling is not available, or when they explicitly request a popup modal.",
        parameters: {
          type: Type.OBJECT,
          properties: {
            action: { type: Type.STRING, description: "Action type: 'SHOW_COURSES', 'SHOW_PRODUCTS', 'SHOW_STATS', 'SHOW_ANALYTICS'" },
            data: { type: Type.STRING, description: "JSON stringified array of data objects to display." }
          },
          required: ["action", "data"]
        }
      };

      const openModalDeclaration = {
        name: "openModal",
        description: "Open a modal or popup on the current website. Use modalName: 'apply' (for admission/course application form, passing productName with the course title), 'pos_login' (for store login), 'admin_login' (for admin login), 'checkout' (for checkout).",
        parameters: {
          type: Type.OBJECT,
          properties: {
            modalName: {
              type: Type.STRING,
              description: "The modal to open: 'apply' (for course/admission application form), 'pos_login', 'admin_login', 'checkout'."
            },
            productName: {
              type: Type.STRING,
              description: "Target program or product name (e.g. 'BSc (Hons) Software Engineering', 'MBA')."
            },
            originalPrice: {
              type: Type.STRING,
              description: "For checkout only: original listed price."
            },
            finalPrice: {
              type: Type.STRING,
              description: "For checkout only: final negotiated price."
            }
          },
          required: ["modalName"]
        }
      };

      const navigateToDeclaration = {
        name: "navigateTo",
        description: "Navigate the user to a different page or route on the website. Use when the user asks to go to a specific page (e.g., 'take me to POS', 'go home', 'open the store dashboard').",
        parameters: {
          type: Type.OBJECT,
          properties: {
            path: { type: Type.STRING, description: "The URL path to navigate to (e.g., '/', '/pos-login', '/about', '/features', '/contact')." },
            label: { type: Type.STRING, description: "A short human-readable name for the destination (e.g., 'Home', 'POS Login', 'About Us')." }
          },
          required: ["path"]
        }
      };

      const flashKeywordsDeclaration = {
        name: "flashKeywords",
        description: "Flash important keywords or numbers as floating holograms on the screen while you are speaking. Use this to emphasize key data like prices, status, or short terms.",
        parameters: {
          type: Type.OBJECT,
          properties: {
            keywords: { 
              type: Type.ARRAY, 
              items: { type: Type.STRING },
              description: "Array of 1-3 short strings to flash on screen (e.g., ['IN STOCK', 'Rs. 5000'])."
            }
          },
          required: ["keywords"]
        }
      };

      const voiceProductSyncDeclaration = {
        name: "voiceProductSync",
        description: "Opens a live holographic data grid for adding multiple products via voice. Use OPEN_UI to show the grid, UPDATE_GRID to pass the full growing array of products as they are dictated, SAVE_ALL_AND_CLOSE to bulk save and close, or CLOSE to cancel.",
        parameters: {
          type: Type.OBJECT,
          properties: {
            action: {
              type: Type.STRING,
              description: "'OPEN_UI' to show the grid, 'UPDATE_GRID' to update the full products array, 'SAVE_ALL_AND_CLOSE' to save all and close, 'CLOSE' to close without saving."
            },
            products: {
              type: Type.ARRAY,
              description: "For UPDATE_GRID: the FULL array of all products so far. Each item must have name, unit, costPrice, sellingPrice, stockQty, category. Send the COMPLETE array every time.",
              items: {
                type: Type.OBJECT,
                properties: {
                  name: { type: Type.STRING, description: "Product name" },
                  unit: { type: Type.STRING, description: "Unit: Pieces, Kg, Grams, Liters, ml" },
                  costPrice: { type: Type.NUMBER, description: "Cost/buying price" },
                  sellingPrice: { type: Type.NUMBER, description: "Selling price" },
                  stockQty: { type: Type.NUMBER, description: "Stock quantity" },
                  category: { type: Type.STRING, description: "Product category" }
                }
              }
            }
          },
          required: ["action"]
        }
      };

      const bulkInventoryActionDeclaration = {
        name: "bulkInventoryAction",
        description: "Parse a voice transcript into a bulk inventory list for staging, or confirm saving the staged list. Use 'PARSE_AND_STAGE' when the user dictates products to add to stock. Use 'CONFIRM_SAVE' when the user confirms they want to save the drafted list.",
        parameters: {
          type: Type.OBJECT,
          properties: {
            action: {
              type: Type.STRING,
              description: "'PARSE_AND_STAGE' to parse transcript and open staging modal, or 'CONFIRM_SAVE' to save the staged products."
            },
            transcript: {
              type: Type.STRING,
              description: "For PARSE_AND_STAGE: the raw voice transcript containing product names, quantities, units, and prices."
            }
          },
          required: ["action"]
        }
      };

      const posCartActionDeclaration = {
        name: "posCartAction",
        description: "Control the POS cart/bill directly. Use this for all cart operations when the cashier gives voice commands.",
        parameters: {
          type: Type.OBJECT,
          properties: {
            action: {
              type: Type.STRING,
              description: "The cart action: 'ADD_TO_BILL', 'REMOVE_FROM_BILL', 'CLEAR_BILL', or 'CHECKOUT'."
            },
            itemName: {
              type: Type.STRING,
              description: "For ADD_TO_BILL and REMOVE_FROM_BILL: the name of the product to add or remove."
            }
          },
          required: ["action"]
        }
      };

      const expressCartSyncDeclaration = {
        name: "expressCartSync",
        description: "Express Checkout mode cart synchronization. Use this for high-speed voice billing. ADD_ITEMS when user dictates products, SET_CASH_TENDERED when they mention cash received, COMPLETE_BILL when they confirm.",
        parameters: {
          type: Type.OBJECT,
          properties: {
            action: {
              type: Type.STRING,
              description: "'ADD_ITEMS' to add products, 'SET_CASH_TENDERED' to set cash amount, 'COMPLETE_BILL' to finalize checkout."
            },
            items: {
              type: Type.ARRAY,
              description: "For ADD_ITEMS: array of items with name and qty extracted from dictation.",
              items: {
                type: Type.OBJECT,
                properties: {
                  name: { type: Type.STRING, description: "Product name as heard" },
                  qty: { type: Type.NUMBER, description: "Quantity (default 1 if not specified)" }
                }
              }
            },
            cashTendered: {
              type: Type.NUMBER,
              description: "For SET_CASH_TENDERED: the cash amount the customer gave."
            }
          },
          required: ["action"]
        }
      };

      const hotelActionDeclaration = {
        name: 'hotelAction',
        description: 'Perform hotel-specific operations: update a room status, trigger a quick guest check-in, or surface hotel data. Use for all hotel voice commands.',
        parameters: {
          type: Type.OBJECT,
          properties: {
            action: { type: Type.STRING, description: "'ROOM_STATUS_UPDATE' to change a room's operational status, 'QUICK_CHECKIN' to initiate fast guest check-in, 'SHOW_OCCUPANCY' to show hotel stats, 'SAVE_ALL' to execute all pending saves when user says save/confirm/done." },
            roomNumber: { type: Type.STRING, description: 'Room number as a string (e.g., "101", "205B").' },
            newStatus: { type: Type.STRING, description: 'For ROOM_STATUS_UPDATE: AVAILABLE, OCCUPIED, CLEANING, MAINTENANCE, or RESERVED.' },
            guestName: { type: Type.STRING, description: 'For QUICK_CHECKIN: the guest full name.' },
            nights: { type: Type.NUMBER, description: 'For QUICK_CHECKIN: number of nights (default 1).' }
          },
          required: ['action']
        }
      };

      // — Sales Trend Analytics Tool (POS store role only) —
      // No user-controlled string parameters → zero prompt-injection surface.
      // Gemini infers intent from voice and calls this with an empty args object.
      const getSalesTrendsDeclaration = {
        name: 'getSalesTrends',
        description: 'Fetch the store\'s historical sales trends grouped by day of the week to identify the best and worst performing days. Use this when the owner asks which days have the most or least sales, or asks about weekly sales patterns, sales trends, or day-of-week performance.',
        parameters: {
          type: Type.OBJECT,
          properties: {},
        }
      };

      const generateStrategicPlanDeclaration = {
        name: 'generateStrategicPlan',
        description: 'Generate a comprehensive strategic marketing plan for the store. Use this when the owner asks how to improve business, grow revenue, create a marketing plan, attract more customers, expand the business, or asks for business strategy, marketing advice, growth strategy, or competitive analysis.',
        parameters: {
          type: Type.OBJECT,
          properties: {},
        }
      };

      const getHoveredElementDeclaration = {
        name: "getHoveredElement",
        description: "Returns exact real-time details of the DOM element currently under the visitor's mouse cursor on the host website. Call this tool when the user asks 'what is this?', 'what am I pointing at?', 'explain this item', 'how do I fill this form?', or asks about an element they are hovering over.",
        parameters: {
          type: Type.OBJECT,
          properties: {}
        }
      };

      const saveStrategicPlanDeclaration = {
        name: 'saveStrategicPlan',
        description: "Persist the currently active strategic plan to the database. Use this ONLY when the owner says 'save this plan', 'Luna save the plan', 'සැලැස්ම සුරකිමු', 'save strategy', or any clear intent to save or store the current strategic plan.",
        parameters: {
          type: Type.OBJECT,
          properties: {},
        }
      };

      const corporateActionDeclaration = {
        name: "corporateAction",
        description: "MANDATORY TOOL for KangaVena Corporate Suite & Tax Filing. Call this tool to fill tax return inputs, add schedule records (employment, solar, lands, vehicles, bank accounts, liabilities, future investments), switch schedule tabs, calculate business P&L, or save/submit the tax filing.",
        parameters: {
          type: Type.OBJECT,
          properties: {
            action: {
              type: Type.STRING,
              description: "Action type: 'FILL_FIELD' | 'ADD_ROW' | 'REMOVE_ROW' | 'SWITCH_TAB' | 'SAVE_DRAFT' | 'SUBMIT_FILING' | 'RECALCULATE'"
            },
            tabId: {
              type: Type.STRING,
              description: "Target tab to switch to: 'general' | 'schedule1' | 'schedule2' | 'schedule3' | 'schedule4' | 'schedule5_6' | 'schedule7' | 'review'"
            },
            fieldKey: {
              type: Type.STRING,
              description: "Field key: e.g. 'tinNumber', 'fullName', 'grossBusinessRevenue', 'salariesExpenses', 'transportExpenses', 'rentExpenses', 'otherExpenses', 'solarCost', etc."
            },
            fieldValue: {
              type: Type.STRING,
              description: "Value to populate"
            },
            tableType: {
              type: Type.STRING,
              description: "For ADD_ROW: 'employers' | 'interest' | 'rent' | 'assetDisposals' | 'lands' | 'vehicles' | 'bankBalances' | 'loanReceivables' | 'shares' | 'businessDetails' | 'liabilities' | 'futureInvestments'"
            },
            rowData: {
              type: Type.STRING,
              description: "JSON stringified object representing the new record to insert into the schedule table"
            },
            feedbackMessage: {
              type: Type.STRING,
              description: "Short friendly status note to display"
            }
          },
          required: ["action"]
        }
      };

      const allDeclarations = [
        corporateActionDeclaration,
        reportSentimentDeclaration, 
        checkGlobalInventoryDeclaration, 
        changeThemeColorDeclaration, 
        triggerCelebrationDeclaration, 
        scrollToSectionDeclaration,
        getHoveredElementDeclaration,
        getTenantsDeclaration,
        getStoresDeclaration,
        getSystemStatsDeclaration,
        getTenantDetailsDeclaration,
        getSystemLogsDeclaration,
        showHologramWidgetDeclaration,
        flashKeywordsDeclaration,
        navigateToDeclaration,
        openModalDeclaration,
        posCartActionDeclaration,
        bulkInventoryActionDeclaration,
        voiceProductSyncDeclaration,
        expressCartSyncDeclaration,
        hotelActionDeclaration,
        getSalesTrendsDeclaration,
        generateStrategicPlanDeclaration,
        saveStrategicPlanDeclaration,
        updateUserLanguageDeclaration,
        requestScreenVisionDeclaration,
        highlightElementDeclaration,
        pointToElementDeclaration
      ];

      const activeDeclarations = allDeclarations.filter(d => allowedToolNames.includes(d.name));

      // Sovereign Unified Model (Rule 15: Single Sovereign Unified Model with dynamic Live fallback)
      const validLiveModel = (dynamicAiModel && (dynamicAiModel === 'gemini-3.8-live' || dynamicAiModel.includes('live')))
        ? dynamicAiModel
        : "gemini-3.1-flash-live-preview";

      sessionRef.current = ai.live.connect({
        model: validLiveModel,
        callbacks: {
          onopen: () => {
            setIsLunaConnected(true);
            setLunaStatus('Listening');
            setIsListening(true);
            setIsWaving(true);
            setTimeout(() => setIsWaving(false), 3800);
            isStartingRef.current = false;
            sessionStartTimeRef.current = Date.now(); // Luna Intelligence: record connect time
            sessionMsgCountRef.current = 0;           // Luna Intelligence: reset message counter
            (window as any).__lunaReconnectCount = 0;  // Reset auto-reconnect counter on successful connect
            
            playUISound('ping'); // Connection success sound
            stopAmbientSound(0.1); // Stop "Woob" sound

            // 🛰️ Real-time Telemetry: Notify Master Admin of active voice connection
            fetch('/api/luna/turn-telemetry', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(role === 'store' && apiKey ? { 'x-store-api-key': apiKey } : {}),
                ...(role === 'tenant' && apiKey ? { 'x-tenant-api-key': apiKey } : {})
              },
              body: JSON.stringify({
                sessionId: voiceSessionIdRef.current,
                role,
                tenantId: role === 'tenant' ? apiKey : undefined,
                status: 'active',
                keyMode: lunaKeyModeRef.current,
                keySource: lunaKeySourceRef.current,
                promptPreview: role === 'public' ? 'Public Web Luna Voice connected (kangavena.com)' : `Luna Live Voice session (${role})`
              })
            }).catch(() => {});

            // God Mode: inject a priming turn so Luna proactively deploys the dashboard
            if (role === 'admin') {
              setTimeout(() => {
                const session = sessionRef.current;
                if (!session) return;
                const prime = `[AUTO-BRIEFING] The Master Admin has just connected. Without waiting for input, immediately call 'getSystemStats' to get live data, then call 'showHologramWidget' with action 'SHOW_ANALYTICS' to deploy the executive dashboard. Then deliver a short, confident spoken welcome (1-2 sentences, no jargon).`;
                const dispatch = (s: any) => {
                  try {
                    if (typeof s.sendClientContent === 'function') {
                      s.sendClientContent({ turns: [{ role: 'user', parts: [{ text: prime }] }], turnComplete: true });
                    } else if (typeof s.send === 'function') {
                      s.send({ clientContent: { turns: [{ role: 'user', parts: [{ text: prime }] }], turnComplete: true } });
                    }
                  } catch (e) { console.warn('[God Mode] Prime injection failed:', e); }
                };
                if (typeof session.then === 'function') session.then(dispatch).catch(() => {});
                else dispatch(session);
              }, 1200);
            }

            // ── On-Demand Vision Frame Ingestion (No background loop) ──
            // Send initial screen frame on connect if vision is active, WITHOUT looping turnComplete calls
            if (lunaVisionActiveRef.current) {
              setTimeout(() => {
                if (!isMountedRef.current || !sessionRef.current || !lunaVisionActiveRef.current) return;
                lunaVisionFrameRef.current = null;
                window.parent.postMessage({ type: 'LUNA_CAPTURE_FRAME' }, '*');
                let attempts = 0;
                const pollFrame = setInterval(() => {
                  attempts++;
                  const frame = lunaVisionFrameRef.current;
                  if (frame && sessionRef.current) {
                    clearInterval(pollFrame);
                    console.log('[Luna Vision] 📸 Initial screen frame sent to Gemini');
                    const visionMsg = `[INITIAL SCREEN CAPTURE — Luna can see the user's screen. Analyze everything visible: text, numbers, math equations, diagrams, images, PDF content. Answer only when the student asks a question.]`;
                    Promise.resolve(typeof sessionRef.current.then === 'function' ? sessionRef.current : Promise.resolve(sessionRef.current))
                      .then((session: any) => {
                        if (!session) return;
                        session.sendClientContent({
                          turns: [{ role: 'user', parts: [
                            { text: visionMsg },
                            { inlineData: { mimeType: frame.mimeType, data: frame.base64 } }
                          ] }],
                          turnComplete: false // Do NOT force turn completion — wait for user speech
                        });
                      }).catch(() => {});
                  } else if (attempts >= 10) {
                    clearInterval(pollFrame);
                  }
                }, 200);
              }, 2000); // Wait 2s after connect for stable session
            }

            if (processorRef.current) {
              processorRef.current.onaudioprocess = (e) => {
                if (!isMountedRef.current || !sessionRef.current) return;
                try {
                  const inputData = e.inputBuffer.getChannelData(0);

                  // 🛡️ ACOUSTIC ECHO & VAD SELF-INTERRUPTION SUPPRESSION
                  // When Luna's audio is playing out of speakers, acoustic bleed enters the mic.
                  // Without gating, Gemini Live VAD detects her own voice and cuts her off after every word!
                  const isLunaOutputPlaying = activeSourcesRef.current.length > 0 || (nextPlayTimeRef.current > (audioContextRef.current?.currentTime || 0) + 0.08);

                  let sumSquares = 0;
                  for (let i = 0; i < inputData.length; i++) {
                    sumSquares += inputData[i] * inputData[i];
                  }
                  const rms = Math.sqrt(sumSquares / inputData.length);

                  // If Luna is actively speaking and mic volume is low/moderate (speaker bleed), drop chunk
                  // Allow loud user speech (rms >= 0.055) through so intentional barge-in works cleanly!
                  if (isLunaOutputPlaying && rms < 0.055) {
                    return; // Drop echo bleed chunk completely
                  }

                  const pcm16 = new Int16Array(inputData.length);
                  for (let i = 0; i < inputData.length; i++) {
                    pcm16[i] = Math.max(-1, Math.min(1, inputData[i])) * 0x7FFF;
                  }
                  const buffer = new ArrayBuffer(pcm16.length * 2);
                  const view = new DataView(buffer);
                  for (let i = 0; i < pcm16.length; i++) {
                    view.setInt16(i * 2, pcm16[i], true);
                  }
                  const bytes = new Uint8Array(buffer);
                  let binary = '';
                  const CHUNK_SIZE = 8192;
                  for (let i = 0; i < bytes.length; i += CHUNK_SIZE) {
                    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK_SIZE) as unknown as number[]);
                  }
                  const base64Data = window.btoa(binary);
                  const currentSession = sessionRef.current;
                  if (!currentSession) return;
                  Promise.resolve(typeof currentSession.then === 'function' ? currentSession : Promise.resolve(sessionRef.current))
                    .then((session: any) => {
                      if (!isMountedRef.current || !session) return;
                      session.sendRealtimeInput({
                        audio: { data: base64Data, mimeType: 'audio/pcm;rate=16000' }
                      });
                    })
                    .catch(() => {});
                } catch (e) { /* audio process error — swallow to prevent crash */ }
              };
            }
          },
          onmessage: (message: LiveServerMessage) => {
            // Capture token usage — Live API sends cumulative running totals per turn,
            // so we take the max (latest value) rather than summing to avoid double-counting.
            // UsageMetadata fields: promptTokenCount (input), responseTokenCount (output).
            const meta = message.usageMetadata || (message as any)?.serverContent?.usageMetadata;
            if (meta) {
              if (typeof meta.promptTokenCount === 'number') voiceTokenInputRef.current = Math.max(voiceTokenInputRef.current, meta.promptTokenCount);
              if (typeof meta.responseTokenCount === 'number') voiceTokenOutputRef.current = Math.max(voiceTokenOutputRef.current, meta.responseTokenCount);
              if (typeof (meta as any).totalTokenCount === 'number') {
                const total = (meta as any).totalTokenCount;
                if (voiceTokenInputRef.current + voiceTokenOutputRef.current === 0 && total > 0) {
                  voiceTokenOutputRef.current = total;
                }
              }
            }

            // Capture user speech transcription from Live API (inputAudioTranscription)
            const inputTranscriptionText = (message as any).serverContent?.inputTranscription?.text;
            if (inputTranscriptionText && typeof inputTranscriptionText === 'string') {
              lastUserPromptRef.current = (lastUserPromptRef.current ? lastUserPromptRef.current + ' ' : '') + inputTranscriptionText.trim();
            }

            // Capture assistant output transcription or text parts from Live API
            const outputTranscriptionText = (message as any).serverContent?.outputTranscription?.text;
            const textPart = message.serverContent?.modelTurn?.parts?.find(p => p.text);
            const hasFunctionCall = message.serverContent?.modelTurn?.parts?.some(p => p.functionCall);

            if (outputTranscriptionText && typeof outputTranscriptionText === 'string') {
              currentAssistantTurnTextRef.current = (currentAssistantTurnTextRef.current ? currentAssistantTurnTextRef.current + ' ' : '') + outputTranscriptionText.trim();
              const displayText = currentAssistantTurnTextRef.current.replace(/\[LANG:[a-z]{2,5}\]/gi, '').trim();
              setCurrentMessage(displayText || null);
            } else if (textPart?.text && !hasFunctionCall) {
              currentAssistantTurnTextRef.current = textPart.text;
              const displayText = textPart.text.replace(/\[LANG:[a-z]{2,5}\]/gi, '').trim();
              setCurrentMessage(displayText || null);
            }

            // When a turn completes, dispatch telemetry and finalize message
            const isTurnComplete = Boolean(message.serverContent?.turnComplete);
            if (isTurnComplete && currentAssistantTurnTextRef.current) {
              sessionMsgCountRef.current++;
              const rawText = currentAssistantTurnTextRef.current;
              currentAssistantTurnTextRef.current = '';

              // ── Language switch detection ─────────────────────────
              const langTagMatch = rawText.match(/\[LANG:([a-z]{2,5})\]/i);
              if (langTagMatch) {
                const newLang = langTagMatch[1].toLowerCase();
                localStorage.setItem('luna_preferred_language', newLang);
                setPreferredLang(newLang);
                const newLabel = LANG_LABELS[newLang] || newLang;
                const reinject = `SYSTEM UPDATE: User has switched language preference to ${newLabel}. From this point forward, ALL your responses MUST be exclusively in ${newLabel}. This is now your permanent language until the user requests another switch.`;
                setTimeout(() => {
                  const session = sessionRef.current;
                  if (!session) return;
                  const dispatch = (s: any) => {
                    try {
                      if (typeof s.sendClientContent === 'function') {
                        s.sendClientContent({ turns: [{ role: 'user', parts: [{ text: reinject }] }], turnComplete: false });
                      } else if (typeof s.send === 'function') {
                        s.send({ clientContent: { turns: [{ role: 'user', parts: [{ text: reinject }] }], turnComplete: false } });
                      }
                    } catch (e) { console.warn('[Luna Lang] Reinject failed:', e); }
                  };
                  if (typeof session.then === 'function') session.then(dispatch).catch(() => {});
                  else dispatch(session);
                }, 200);
              }

              // Strip internal tags before displaying
              const displayText = rawText.replace(/\[LANG:[a-z]{2,5}\]/gi, '').trim();
              if (displayText) {
                setCurrentMessage(displayText);
                setTimeout(() => setCurrentMessage(null), 6000);

                // 🛰️ Telemetry Turn Beacon: stream assistant turn to Master Admin HUD & Tenet in real time
                const totalTokensUsed = voiceTokenInputRef.current + voiceTokenOutputRef.current;
                const promptText = lastUserPromptRef.current || 'Live Voice Speech Turn';
                const calculatedTokens = totalTokensUsed > 0 ? totalTokensUsed : Math.max(75, Math.round((displayText.length + promptText.length) / 3));

                fetch('/api/luna/turn-telemetry', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    ...(role === 'store' && apiKey ? { 'x-store-api-key': apiKey } : {}),
                    ...(role === 'tenant' && apiKey ? { 'x-tenant-api-key': apiKey } : {})
                  },
                  body: JSON.stringify({
                    sessionId: voiceSessionIdRef.current,
                    role,
                    tenantId: role === 'tenant' ? apiKey : undefined,
                    keyMode: lunaKeyModeRef.current,
                    keySource: lunaKeySourceRef.current,
                    responsePreview: displayText.slice(0, 500),
                    promptPreview: promptText,
                    tokensUsed: calculatedTokens,
                    latencyMs: 350,
                    durationSeconds: sessionStartTimeRef.current ? Math.round((Date.now() - sessionStartTimeRef.current) / 1000) : 0,
                    status: 'turn'
                  })
                }).catch(() => {});
                lastUserPromptRef.current = '';
              }
            }

            if (message.serverContent?.interrupted) {
              nextPlayTimeRef.current = 0;
              activeSourcesRef.current.forEach(source => { try { source.stop(); } catch (e) {} });
              activeSourcesRef.current = [];
              setLunaStatus('Listening');
            }

            if (message.toolCall) {
              const functionCalls = message.toolCall.functionCalls;
              if (functionCalls) {
                Promise.all(functionCalls.map(async fc => {
                  if (fc.name === 'reportSentiment') {
                    const args = fc.args as any;
                    if (args && args.emotion) {
                      setSentiment(args.emotion);
                      playUISound('emotion', args.emotion);
                    }
                    return {
                      id: fc.id,
                      name: fc.name,
                      response: { result: "Sentiment updated" }
                    };
                  } else if (fc.name === 'updateUserLanguage') {
                    const args = fc.args as any;
                    const targetLangCode = (args?.langCode || 'si').toLowerCase();
                    fetch('/api/geo/update-language', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ langCode: targetLangCode, languageName: args?.languageName })
                    }).catch(e => console.error('[Luna Language Update Error]', e));

                    localStorage.setItem('luna_preferred_language', targetLangCode);
                    setPreferredLang(targetLangCode);
                    playUISound('ping');
                    return {
                      id: fc.id,
                      name: fc.name,
                      response: { result: `LANGUAGE_UPDATED. Preference saved permanently for IP to ${targetLangCode}.` }
                    };
                  } else if (fc.name === 'requestScreenVision') {
                    console.log('📷 [Luna] AI requested screen vision / fresh capture from host page!');
                    if (typeof window !== 'undefined' && window.parent) {
                      if (lunaVisionActiveRef.current) {
                        // Vision is ACTIVE → capture fresh frame and inject into session
                        lunaVisionFrameRef.current = null;
                        visionFrameRef.current = null;
                        window.parent.postMessage({ type: 'LUNA_CAPTURE_FRAME' }, '*');

                        // Wait for fresh frame (up to 2s)
                        const freshFrame = await new Promise<{ base64: string; mimeType: string } | null>((resolve) => {
                          let attempts = 0;
                          const poll = setInterval(() => {
                            attempts++;
                            const frame = visionFrameRef.current || lunaVisionFrameRef.current;
                            if (frame) { clearInterval(poll); resolve(frame); }
                            else if (attempts >= 10) { clearInterval(poll); resolve(null); }
                          }, 200);
                        });

                        if (freshFrame && sessionRef.current) {
                          console.log('📸 [Luna Vision] Fresh on-demand frame captured! Injecting via sendRealtimeInput video...');
                          const msg = `[LIVE SCREEN SNAPSHOT ATTACHED — The visitor is looking at this exact screen right now. Read all Sinhala/English text, titles, numbers, diagrams, and prices shown in this image carefully. Answer their questions accurately based strictly on what is visible on this screen image.]`;
                          const dispatch = (s: any) => {
                            try {
                              if (typeof s.sendRealtimeInput === 'function') {
                                s.sendRealtimeInput({
                                  video: {
                                    data: freshFrame.base64,
                                    mimeType: freshFrame.mimeType || 'image/jpeg'
                                  }
                                });
                                s.sendRealtimeInput({ text: msg });
                              } else if (typeof s.sendClientContent === 'function') {
                                s.sendClientContent({
                                  turns: [{
                                    role: 'user',
                                    parts: [
                                      { text: msg },
                                      { inlineData: { mimeType: freshFrame.mimeType, data: freshFrame.base64 } }
                                    ]
                                  }],
                                  turnComplete: true
                                });
                              } else if (typeof s.send === 'function') {
                                s.send({
                                  realtimeInput: {
                                    mediaChunks: [{
                                      mimeType: freshFrame.mimeType || 'image/jpeg',
                                      data: freshFrame.base64
                                    }]
                                  }
                                });
                              }
                            } catch (e) { console.error('[Luna Vision] sendRealtimeInput dispatch error:', e); }
                          };
                          
                          if (typeof sessionRef.current.then === 'function') {
                            sessionRef.current.then(dispatch).catch(()=>{});
                          } else {
                            dispatch(sessionRef.current);
                          }
                        }

                        return {
                          id: fc.id,
                          name: fc.name,
                          response: {
                            result: freshFrame
                              ? "SCREENSHOT_ATTACHED. The live screenshot has been injected into your context turn. Read and explain the exact lesson / math problems shown on this image directly in Sinhala."
                              : "SCREENSHOT_FAILED. Could not capture screen frame. Ask the user to try again."
                          }
                        };
                      } else {
                        // Vision is INACTIVE → show permission prompt
                        window.parent.postMessage({ type: 'LUNA_SHOW_VISION_PROMPT' }, '*');
                        return {
                          id: fc.id,
                          name: fc.name,
                          response: { result: "SCREEN_VISION_PROMPT_SHOWN. The 'Allow Screen View' button (📷) has been presented to the user on their host page screen. Ask the user to click the Allow Screen View button below so you can inspect their screen." }
                        };
                      }
                    }
                    return {
                      id: fc.id,
                      name: fc.name,
                      response: { result: "SCREEN_VISION_UNAVAILABLE." }
                    };
                  } else if (fc.name === 'checkGlobalInventory') {
                    const args = fc.args as any;
                    try {
                      let rawQuery = args.productName || '';
                      if (rawQuery.toLowerCase() === 'all') rawQuery = '';
                      const maxPriceParam = args.maxPrice ? `&maxPrice=${args.maxPrice}` : '';
                      console.log(`🧠 [Luna] Searching DB for: "${rawQuery}"`);
                      const res = await fetch(`/api/search-inventory?q=${encodeURIComponent(rawQuery)}${maxPriceParam}`);
                      if (!res.ok) throw new Error(`API Error: ${res.status}`);
                      const data = await res.json();
                      const items = Array.isArray(data) ? data : (data.products || data.inventory || data.items || data.data || []);
                      console.log(`🧠 [Luna] Found ${items.length} items`);
                      if (items.length > 0) {
                        const mode = args.displayMode === 'map' ? 'SHOW_MAP' : 'SHOW_PRODUCTS';
                        if (mode === 'SHOW_MAP') {
                          setPrevHologramAction(hologramActionRef.current);
                          setPrevHologramData(hologramDataRef.current);
                        } else {
                          setPrevHologramAction(null);
                          setPrevHologramData(null);
                        }
                        setHologramAction(mode);
                        setHologramData(items);
                        SoundManager.play('ui_open');
                        // Include product summary so Luna reads REAL prices
                        const summary = items.slice(0, 5).map((p: any) => {
                          const price = p.priceHidden ? 'Contact store' : (p.price ? `Rs.${p.price}` : 'N/A');
                          return `${p.name} — ${price}`;
                        }).join(', ');
                        return { id: fc.id, name: fc.name, response: { result: `SUCCESS. ${mode} opened. Products found: ${summary}. Say the product names and prices briefly in Sinhala (e.g. 'Samba Rice Rs.250, Parippu Rs.300'). Use EXACT prices from the data. If priceHidden=true say 'මිල දැන ගැනීමට කඩයට අමතන්න'.` } };
                      } else {
                        return { id: fc.id, name: fc.name, response: { result: "OUT OF STOCK. Say briefly in 1 sentence that it's out of stock." } };
                      }
                    } catch (e: unknown) {
                      console.error('🧠 [Luna] Inventory search error:', e instanceof Error ? e.message : String(e));
                      return { id: fc.id, name: fc.name, response: { error: "Search failed." } };
                    }
                  } else if (fc.name === 'changeThemeColor') {
                    // Note: This tool might not work perfectly in AdminDashboard if the theme state is not shared
                    return { id: fc.id, name: fc.name, response: { result: "Theme color change triggered (simulated)" } };
                  } else if (fc.name === 'triggerCelebration') {
                    return { id: fc.id, name: fc.name, response: { result: "Celebration triggered" } };
                  } else if (fc.name === 'highlightElement' || fc.name === 'highlight_element' || fc.name === 'pointToElement') {
                    const args = fc.args as any;
                    const selector = args?.selector || args?.target || '';
                    const text = args?.text || args?.keyword || '';
                    const label = args?.label || args?.message || '';
                    const color = args?.color || '#06b6d4';
                    const duration = args?.duration || 6;

                    if (typeof window !== 'undefined' && window.parent) {
                      window.parent.postMessage({
                        type: 'LUNA_ACTION',
                        action: 'HIGHLIGHT_ELEMENT',
                        payload: { selector, text, label, color, duration }
                      }, '*');
                    }
                    return {
                      id: fc.id,
                      name: fc.name,
                      response: {
                        status: "ELEMENT_HIGHLIGHTED_WITH_LASER",
                        selector,
                        text,
                        message: "The target element is now brightly illuminated with an animated sci-fi laser halo and pointer beacon on the visitor's screen. Proceed with your spoken explanation."
                      }
                    };
                  } else if (fc.name === 'scrollToSection') {
                    const args = fc.args as any;
                    const sectionId = args?.sectionId || args?.target || '';
                    if (typeof window !== 'undefined' && window.parent) {
                      window.parent.postMessage({
                        type: 'LUNA_ACTION',
                        action: 'FLY_TO_ELEMENT',
                        payload: { sectionId }
                      }, '*');
                    }
                    return { 
                      id: fc.id, 
                      name: fc.name, 
                      response: { 
                        status: "NAVIGATED_SUCCESSFULLY",
                        section: sectionId,
                        instruction: "The section is now centered on the visitor's screen. Deliver a warm, natural, and engaging spoken summary of the key programs or highlights in the visitor's language."
                      } 
                    };
                  } else if (fc.name === 'getHoveredElement') {
                    const cur = hoveredTelemetryRef.current;
                    return {
                      id: fc.id,
                      name: fc.name,
                      response: cur ? {
                        elementTag: cur.tag || 'div',
                        elementId: cur.id || 'none',
                        elementText: cur.text || 'No text snippet',
                        section: cur.section || 'page',
                        placeholder: cur.placeholder || ''
                      } : { message: "Visitor is currently hovering over empty page space." }
                    };
                  } else if (fc.name === 'getTenants') {
                    try {
                      const res = await fetch('/api/tenants');
                      const data = await res.json();
                      return { id: fc.id, name: fc.name, response: { tenants: data } };
                    } catch (e) {
                      return { id: fc.id, name: fc.name, response: { error: "Failed to fetch tenants" } };
                    }
                  } else if (fc.name === 'corporateAction') {
                    const args = fc.args as any;
                    window.dispatchEvent(new CustomEvent('kangavena:corporate-action', { detail: args }));
                    return { id: fc.id, name: fc.name, response: { result: `SUCCESS: Executed corporateAction ${args.action || 'OK'}` } };
                  } else if (fc.name === 'getStores') {
                    try {
                      const res = await fetch('/api/stores');
                      const data = await res.json();
                      return { id: fc.id, name: fc.name, response: { stores: data } };
                    } catch (e) {
                      return { id: fc.id, name: fc.name, response: { error: "Failed to fetch stores" } };
                    }
                  } else if (fc.name === 'getSystemStats') {
                    try {
                      const [tenantsRes, storesRes] = await Promise.all([
                        fetch('/api/tenants'),
                        fetch('/api/stores')
                      ]);
                      const tenants = await tenantsRes.json();
                      const stores = await storesRes.json();
                      return { 
                        id: fc.id, 
                        name: fc.name, 
                        response: { 
                          totalTenants: tenants.length,
                          totalStores: stores.length,
                          activeTenants: tenants.filter((t: any) => t.status === 'Active').length,
                          systemStatus: "Operational"
                        } 
                      };
                    } catch (e) {
                      return { id: fc.id, name: fc.name, response: { error: "Failed to fetch system stats" } };
                    }
                  } else if (fc.name === 'getTenantDetails') {
                    const args = fc.args as any;
                    try {
                      const res = await fetch(`/api/tenants/${args.tenantId}/details`);
                      const data = await res.json();
                      return { id: fc.id, name: fc.name, response: { tenantDetails: data } };
                    } catch (e) {
                      return { id: fc.id, name: fc.name, response: { error: "Failed to fetch tenant details" } };
                    }
                  } else if (fc.name === 'getSystemLogs') {
                    try {
                      const res = await fetch('/api/logs');
                      const data = await res.json();
                      return { id: fc.id, name: fc.name, response: { logs: data } };
                    } catch (e) {
                      return { id: fc.id, name: fc.name, response: { error: "Failed to fetch logs" } };
                    }
                  } else if (fc.name === 'voiceProductSync') {
                    const args = fc.args as any;
                    const action = args?.action;
                    if (action === 'OPEN_UI') {
                      window.dispatchEvent(new CustomEvent('luna-voice-product', { detail: { action: 'OPEN' } }));
                      return { id: fc.id, name: fc.name, response: { result: 'Live product grid opened. The user can now see the holographic table on screen. Ask them to dictate products row by row.' } };
                    } else if (action === 'UPDATE_GRID') {
                      const products = args?.products || [];
                      window.dispatchEvent(new CustomEvent('luna-live-grid-update', { detail: { products } }));
                      return { id: fc.id, name: fc.name, response: { result: `Grid updated with ${products.length} product(s). Table refreshed on screen.` } };
                    } else if (action === 'SAVE_ALL_AND_CLOSE') {
                      window.dispatchEvent(new CustomEvent('luna-live-grid-save'));
                      return { id: fc.id, name: fc.name, response: { result: 'All products are being saved to the database and the grid will close.' } };
                    } else if (action === 'CLOSE') {
                      window.dispatchEvent(new CustomEvent('luna-voice-product', { detail: { action: 'CLOSE' } }));
                      return { id: fc.id, name: fc.name, response: { result: 'Grid closed without saving.' } };
                    }
                    return { id: fc.id, name: fc.name, response: { error: 'Unknown voiceProductSync action.' } };
                  } else if (fc.name === 'expressCartSync') {
                    const args = fc.args as any;
                    const action = args?.action;
                    if (action === 'ADD_ITEMS') {
                      const items = args?.items || [];
                      window.dispatchEvent(new CustomEvent('luna-express-sync', { detail: { action: 'ADD_ITEMS', items } }));
                      return { id: fc.id, name: fc.name, response: { result: 'Success. UI updated silently.' } };
                    } else if (action === 'SET_CASH_TENDERED') {
                      const amount = args?.cashTendered ?? 0;
                      window.dispatchEvent(new CustomEvent('luna-express-sync', { detail: { action: 'SET_CASH_TENDERED', amount } }));
                      return { id: fc.id, name: fc.name, response: { result: 'Success. UI updated silently.' } };
                    } else if (action === 'COMPLETE_BILL') {
                      window.dispatchEvent(new CustomEvent('luna-express-sync', { detail: { action: 'COMPLETE_BILL' } }));
                      return { id: fc.id, name: fc.name, response: { result: 'Success. UI updated silently.' } };
                    }
                    return { id: fc.id, name: fc.name, response: { error: 'Unknown expressCartSync action.' } };
                  } else if (fc.name === 'bulkInventoryAction') {
                    const args = fc.args as any;
                    if (args?.action === 'PARSE_AND_STAGE' && args?.transcript) {
                      try {
                        const res = await fetch('/api/ai/parse-inventory', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ transcript: args.transcript }),
                        });
                        const data = await res.json();
                        if (data.products?.length > 0) {
                          const staged = data.products.map((p: any) => ({
                            id: `row_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
                            name: p.name || '', qty: String(p.qty ?? ''), unit: ['Pieces','Kg','Grams','Liters','ml'].includes(p.unit) ? p.unit : 'Pieces',
                            cost: p.cost != null ? String(p.cost) : '', selling: String(p.selling ?? ''), category: p.category || 'Grocery',
                          }));
                          window.dispatchEvent(new CustomEvent('luna-bulk-inventory-open', { detail: { products: staged } }));
                          return { id: fc.id, name: fc.name, response: { result: "WIDGET_OPENED. Visual displayed. Speak ONE short warm sentence only. Do NOT say 'menna wisthara' or any filler." } };
                        }
                        return { id: fc.id, name: fc.name, response: { error: 'No products detected in transcript.' } };
                      } catch (e: unknown) {
                        const msg = e instanceof Error ? e.message : 'Parse failed';
                        return { id: fc.id, name: fc.name, response: { error: `Parse failed: ${msg}` } };
                      }
                    } else if (args?.action === 'CONFIRM_SAVE') {
                      window.dispatchEvent(new CustomEvent('luna-bulk-inventory', { detail: { action: 'CONFIRM_SAVE' } }));
                      return { id: fc.id, name: fc.name, response: { result: 'Save confirmed — products are being saved to database.' } };
                    }
                    return { id: fc.id, name: fc.name, response: { error: 'Invalid action or missing transcript.' } };
                  } else if (fc.name === 'posCartAction') {
                    const args = fc.args as any;
                    window.dispatchEvent(new CustomEvent('luna-pos-action', {
                      detail: { action: args?.action, itemName: args?.itemName }
                    }));
                    return { id: fc.id, name: fc.name, response: { result: `POS action '${args?.action}' dispatched.` } };
                  } else if (fc.name === 'hotelAction') {
                    const args = fc.args as any;
                    const { action, roomNumber, newStatus, guestName, nights } = args || {};
                    if (action === 'SAVE_ALL') {
                      window.dispatchEvent(new CustomEvent('luna-checkin-grid-save'));
                      window.dispatchEvent(new CustomEvent('luna-roomstatus-grid-save'));
                      return { id: fc.id, name: fc.name, response: { result: 'ok' } };
                    }
                    window.dispatchEvent(new CustomEvent('luna-hotel-action', {
                      detail: { action, roomNumber: String(roomNumber || ''), newStatus: String(newStatus || '').toUpperCase(), guestName: guestName || '', nights: Number(nights || 1) }
                    }));
                    return { id: fc.id, name: fc.name, response: { result: 'ok' } };
                  } else if (fc.name === 'openModal') {
                    const args = fc.args as any;
                    const modalName = args?.modalName;
                    if (modalName) {
                      const isCheckout = modalName === 'checkout';
                      SoundManager.play(isCheckout ? 'cha_ching' : 'ui_open');
                      window.dispatchEvent(new CustomEvent('luna-trigger-modal', {
                        detail: {
                          modalName,
                          productName: args?.productName,
                          originalPrice: args?.originalPrice,
                          finalPrice: args?.finalPrice,
                        }
                      }));
                      // Dispatch to Host Webpage window.parent so host page opens application form!
                      if (typeof window !== 'undefined' && window.parent) {
                        window.parent.postMessage({
                          type: 'LUNA_ACTION',
                          action: 'OPEN_MODAL',
                          payload: {
                            modalName,
                            productName: args?.productName,
                            courseName: args?.productName
                          }
                        }, '*');
                      }
                      return { 
                        id: fc.id, 
                        name: fc.name, 
                        response: { 
                          result: `APPLICATION_FORM_OPENED. The application/registration form is now open on the visitor's screen. Warmly invite them to fill their details and submit in 1 natural spoken sentence.` 
                        } 
                      };
                    }
                    return { id: fc.id, name: fc.name, response: { error: 'No modalName provided' } };
                  } else if (fc.name === 'navigateTo') {
                    const args = fc.args as any;
                    const path = args?.path;
                    if (path) {
                      SoundManager.play('ui_click');
                      // Close any open hologram before navigating
                      setHologramAction(null);
                      setHologramData(null);
                      // Short delay so Luna's spoken confirmation plays before the page changes
                      setTimeout(() => { window.location.assign(path); }, 1200);
                      return { id: fc.id, name: fc.name, response: { result: `Navigating to ${path}` } };
                    }
                    return { id: fc.id, name: fc.name, response: { error: "No path provided" } };
                  } else if (fc.name === 'notify_owner' || fc.name === 'sendWhatsAppNotification' || fc.name === 'capture_and_send_whatsapp_lead') {
                    const args = fc.args as any;
                    try {
                      if (typeof window !== 'undefined' && window.parent) {
                        window.parent.postMessage({
                          type: 'LUNA_ACTION',
                          action: 'NOTIFY_OWNER',
                          payload: args
                        }, '*');
                      }
                      if (apiKey) {
                        fetch('/api/tenet/public/leads', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            apiKey,
                            customerName: args?.customerName || args?.name || 'Website Visitor',
                            customerPhone: args?.customerPhone || args?.phone || '',
                            leadNotes: args?.message || args?.details || args?.note || 'Inquiry via Website Co-Pilot',
                            source: 'web_widget'
                          })
                        }).catch(e => console.warn('[Luna Lead Dispatch Error]', e));
                      }
                      return {
                        id: fc.id,
                        name: fc.name,
                        response: { result: "OWNER_NOTIFIED. The store owner has been notified via WhatsApp. Confirm politely in 1 short sentence." }
                      };
                    } catch (e: any) {
                      return { id: fc.id, name: fc.name, response: { result: "NOTIFICATION_DISPATCHED." } };
                    }
                  } else if (fc.name === 'save_lead' || fc.name === 'recordCustomerLead') {
                    const args = fc.args as any;
                    try {
                      if (apiKey) {
                        fetch('/api/tenet/public/leads', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            apiKey,
                            customerName: args?.customerName || args?.name || 'Website Visitor',
                            customerPhone: args?.customerPhone || args?.phone || '',
                            customerEmail: args?.customerEmail || args?.email || '',
                            leadNotes: args?.notes || args?.inquiry || args?.message || '',
                            source: 'web_widget'
                          })
                        }).catch(e => console.warn('[Luna Lead Save Error]', e));
                      }
                      if (typeof window !== 'undefined' && window.parent) {
                        window.parent.postMessage({
                          type: 'LUNA_ACTION',
                          action: 'SAVE_LEAD',
                          payload: args
                        }, '*');
                      }
                      return {
                        id: fc.id,
                        name: fc.name,
                        response: { result: "LEAD_SAVED. Lead information recorded in CRM. Thank the visitor in 1 warm sentence." }
                      };
                    } catch (e: any) {
                      return { id: fc.id, name: fc.name, response: { result: "LEAD_RECORDED." } };
                    }
                  } else if (fc.name === 'change_bg_color' || fc.name === 'changeBgColor') {
                    const args = fc.args as any;
                    if (typeof window !== 'undefined' && window.parent) {
                      window.parent.postMessage({
                        type: 'LUNA_ACTION',
                        action: 'CHANGE_BG_COLOR',
                        payload: args
                      }, '*');
                    }
                    return { id: fc.id, name: fc.name, response: { result: `COLOR_CHANGED to ${args?.color || 'requested theme'}` } };
                  } else if (fc.name === 'show_popup' || fc.name === 'showPopupTool') {
                    const args = fc.args as any;
                    if (typeof window !== 'undefined' && window.parent) {
                      window.parent.postMessage({
                        type: 'LUNA_ACTION',
                        action: 'SHOW_POPUP',
                        payload: args
                      }, '*');
                    }
                    return { id: fc.id, name: fc.name, response: { result: `POPUP_SHOWN: ${args?.title || args?.message || 'Notification displayed'}` } };
                  } else if (fc.name === 'fill_form_field' || fc.name === 'submit_form') {
                    const args = fc.args as any;
                    if (typeof window !== 'undefined' && window.parent) {
                      window.parent.postMessage({
                        type: 'LUNA_ACTION',
                        action: fc.name === 'submit_form' ? 'SUBMIT_FORM' : 'FILL_FORM_FIELD',
                        payload: args
                      }, '*');
                    }
                    return { id: fc.id, name: fc.name, response: { result: `FORM_ACTION_EXECUTED: ${fc.name}` } };
                  } else if (fc.name === 'showHologramWidget') {
                    const args = fc.args as any;
                    try {
                      // Intercept NAVIGATE action passed through showHologramWidget (fallback compatibility)
                      if (args.action === 'NAVIGATE') {
                        let navPath = '/';
                        try { navPath = (JSON.parse(args.data)?.[0]?.path) || navPath; } catch { /* ignore */ }
                        SoundManager.play('ui_click');
                        setHologramAction(null);
                        setHologramData(null);
                        setTimeout(() => { window.location.assign(navPath); }, 1200);
                        return { id: fc.id, name: fc.name, response: { result: `Navigating to ${navPath}` } };
                      }
                      if (args.action === 'SHOW_ANALYTICS') {
                        setHologramAction(null);
                        setHologramData(null);
                        setPrevHologramAction(null);
                        setPrevHologramData(null);
                        setStrategicPlanData(null);
                        setShowWeeklyChart(false);
                        setShowLiveDashboard(true);
                        SoundManager.play('ui_open');
                        try {
                          const cashierToken = localStorage.getItem('kangavena:cashierToken') || '';
                          const headers: Record<string, string> = {};
                          if (cashierToken) headers['Authorization'] = `Bearer ${cashierToken}`;
                          else headers['x-store-api-key'] = apiKey;
                          const [ssotRes, analyticsRes] = await Promise.all([
                            fetch('/api/reports/daily-summary', { headers }),
                            fetch('/api/luna/analytics', { headers })
                          ]);
                          const daily = ssotRes.ok ? await ssotRes.json() : null;
                          const monthly = analyticsRes.ok ? await analyticsRes.json() : null;
                          const cm = monthly?.currentMetrics;
                          return { id: fc.id, name: fc.name, response: { result: `Hologram shown. REAL SSOT DATA — Today's Revenue: Rs.${daily?.todayRevenue ?? 'N/A'}. Today's Net Profit: Rs.${daily?.todayNetProfit ?? 'N/A'}. Total All-Time Revenue: Rs.${daily?.totalAllTimeRevenue ?? 'N/A'}. Total Net Profit: Rs.${daily?.totalNetProfit ?? 'N/A'}. Month Revenue: Rs.${cm?.totalRevenue ?? 'N/A'}. Avg Order Value: Rs.${cm?.averageOrderValue ?? 'N/A'}. Top Category: ${cm?.topSellingCategory ?? 'N/A'}. Growth: ${monthly?.growthTrend ?? 'N/A'}. Read EXACTLY these numbers when asked. If 0, say 0.` } };
                        } catch (_) {}
                        return { id: fc.id, name: fc.name, response: { result: "Hologram shown. Awaiting dashboard data." } };
                      }
                      // Close ALL competing overlays before opening hologram widget
                      setShowLiveDashboard(false);
                      setStrategicPlanData(null);
                      setShowWeeklyChart(false);
                      setHologramAction(args.action);
                      const parsedData = JSON.parse(args.data);
                      setHologramData(parsedData);
                      SoundManager.play('ui_open'); // Cinematic hologram open sound
                      return { id: fc.id, name: fc.name, response: { result: "WIDGET_OPENED. Visual displayed. Speak ONE short warm sentence only. Do NOT say 'menna wisthara' or any Sinhala filler." } };
                    } catch (e) {
                      return { id: fc.id, name: fc.name, response: { error: "Failed to parse hologram data JSON" } };
                    }
                  } else if (fc.name === 'getSalesTrends') {
                    // Auth: reads manager JWT from localStorage — fc.args is empty (no user string input)
                    // This is the complete prompt-injection firewall: Gemini cannot pass arbitrary
                    // strings into this handler because the tool has zero parameters.
                    try {
                      const cashierToken = localStorage.getItem('kangavena:cashierToken') || '';
                      if (!cashierToken) {
                        return { id: fc.id, name: fc.name, response: { error: 'Manager authentication required. Please ensure you are logged in as a manager.' } };
                      }
                      const trendsRes = await fetch('/api/analytics/sales-trends', {
                        headers: { Authorization: `Bearer ${cashierToken}` }
                      });
                      if (trendsRes.status === 401) {
                        return { id: fc.id, name: fc.name, response: { error: 'Not authenticated. Please log in again.' } };
                      }
                      if (trendsRes.status === 403) {
                        return { id: fc.id, name: fc.name, response: { error: 'Only managers can access sales trend analytics.' } };
                      }
                      if (!trendsRes.ok) {
                        return { id: fc.id, name: fc.name, response: { error: 'Sales trend data is temporarily unavailable. Please try again.' } };
                      }
                      const td = await trendsRes.json();

                      // No sales data yet
                      if (!td.bestDay) {
                        return { id: fc.id, name: fc.name, response: { result: 'No completed sales transactions recorded yet. Tell the owner there is not enough data to calculate trends — come back after a few days of trading.' } };
                      }

                      // Open dedicated weekly chart visual — close ALL competing overlays first
                      setShowLiveDashboard(false);
                      setHologramAction(null);
                      setHologramData(null);
                      setPrevHologramAction(null);
                      setPrevHologramData(null);
                      setStrategicPlanData(null);
                      setWeeklyChartData(td as ChartTrendsData);
                      setShowWeeklyChart(true);

                      // Build a concise sanitised briefing — ONLY aggregated totals, NO raw records
                      const topDays = Object.entries(td.dayTotals as Record<string, number>)
                        .filter(([, v]) => v > 0)
                        .sort(([, a], [, b]) => (b as number) - (a as number))
                        .slice(0, 3)
                        .map(([day, total]) => `${day} (Rs.${Math.round(total as number).toLocaleString()})`)
                        .join(', ');

                      const briefing = `CHART DISPLAYED. The Weekly Sales Chart is now on screen. Best day: ${td.bestDay}, slowest: ${td.worstDay}. Top 3 by revenue: ${topDays}. Deliver a crisp 1-2 sentence spoken briefing in the user's language naming the best and worst days, then one brief suggestion. Keep it very short — the chart is already visible to the user.`;
                      return { id: fc.id, name: fc.name, response: { result: briefing } };
                    } catch {
                      return { id: fc.id, name: fc.name, response: { error: 'Sales trend analysis failed. Please try again.' } };
                    }
                  } else if (fc.name === 'generateStrategicPlan') {
                    try {
                      // Close all competing overlays
                      setShowLiveDashboard(false);
                      setHologramAction(null);
                      setHologramData(null);
                      setPrevHologramAction(null);
                      setPrevHologramData(null);
                      setShowWeeklyChart(false);
                      SoundManager.play('ui_open');

                      // Fetch real store context for personalised frameworks
                      let topCategories: string[] = [];
                      let topItems: string[] = [];
                      try {
                        const cashierTok = localStorage.getItem('kangavena:cashierToken') || '';
                        const aHeaders: Record<string, string> = { 'x-store-api-key': apiKey };
                        if (cashierTok) aHeaders['Authorization'] = `Bearer ${cashierTok}`;
                        const analyticsRes = await fetch('/api/luna/analytics', { headers: aHeaders });
                        if (analyticsRes.ok) {
                          const ad = await analyticsRes.json();
                          if (ad.currentMetrics?.topSellingCategory) topCategories = [ad.currentMetrics.topSellingCategory];
                          if (ad.currentMetrics?.topSellingItems) topItems = (ad.currentMetrics.topSellingItems as string[]).slice(0, 3);
                          if (ad.topCategories) topCategories = (ad.topCategories as string[]).slice(0, 3);
                        }
                      } catch (_) { /* use defaults */ }

                      setStrategicPlanData({
                        topCategories: topCategories.length > 0 ? topCategories : ['General Retail'],
                        topItems: topItems.length > 0 ? topItems : ['your top products'],
                        storeName: storeName || 'Your Store',
                      });

                      return {
                        id: fc.id,
                        name: fc.name,
                        response: {
                          result: `STRATEGIC_PLAN_DEPLOYED. The full visual analysis is now on screen in three tabs: ඩිජිටල් අලෙවිය, ප්‍රාදේශීය අලෙවිය, ජාත්‍යන්තර. CRITICAL VERBAL DIRECTIVE: Do NOT narrate all frameworks. Instead, select the TOP 3 most creative, high-impact, immediately actionable strategies from the full plan and speak them out loud with confidence and energy — like a world-class strategic consultant delivering the punchline. Examples of wow-worthy moves: Luna AI voice checkout as Blue Ocean differentiation, diaspora WhatsApp catalogue for international expansion, or Google Business Profile reviews as zero-cost acquisition. Speak in the SAME language the user used to ask. Deliver the 3 wow strategies in 3 punchy sentences. End by telling them to explore the tabs for the complete framework. NEVER say the word 'tool', 'function', 'saveStrategicPlan', or any developer jargon. You may also tell them they can say 'සැලැස්ම සුරකිමු' or 'Luna, save this plan' to persist it.`
                        }
                      };
                    } catch {
                      return { id: fc.id, name: fc.name, response: { error: 'Strategic plan generation failed. Please try again.' } };
                    }
                  } else if (fc.name === 'saveStrategicPlan') {
                    try {
                      const currentPlan = strategicPlanDataRef.current;
                      if (!currentPlan) {
                        return { id: fc.id, name: fc.name, response: { result: 'No active strategic plan to save. Please generate a plan first by asking for marketing strategy.' } };
                      }
                      const cashierTok = localStorage.getItem('kangavena:cashierToken') || '';
                      const sHeaders: Record<string, string> = {
                        'Content-Type': 'application/json',
                        'x-store-api-key': apiKey,
                      };
                      if (cashierTok) sHeaders['Authorization'] = `Bearer ${cashierTok}`;

                      // Build the tab data from the Sinhala framework content (structured snapshot)
                      const planPayload = {
                        storeName: currentPlan.storeName.slice(0, 120),
                        topCategories: currentPlan.topCategories.slice(0, 10),
                        topItems: currentPlan.topItems.slice(0, 10),
                        digitalTab: { label: 'ඩිජිටල් අලෙවිය', categories: currentPlan.topCategories, items: currentPlan.topItems },
                        localTab:   { label: 'ප්‍රාදේශීය අලෙවිය', categories: currentPlan.topCategories, items: currentPlan.topItems },
                        internationalTab: { label: 'ජාත්‍යන්තර', categories: currentPlan.topCategories, items: currentPlan.topItems },
                      };

                      const saveRes = await fetch('/api/analytics/strategic-plans/save', {
                        method: 'POST',
                        headers: sHeaders,
                        body: JSON.stringify(planPayload),
                      });

                      if (saveRes.ok) {
                        // Also trigger the UI save confirmation
                        window.dispatchEvent(new CustomEvent('kangavena:save-strategic-plan'));
                        return { id: fc.id, name: fc.name, response: { result: 'PLAN_SAVED_SUCCESS. Deliver exactly ONE punchy, confident sentence in Sinhala telling the owner their strategic plan is now securely saved inside the Marketing Center (කඩේ ප්‍රධාන අලෙවිකරණ මධ්‍යස්ථානයට ආරක්ෂිතව සේව් කරා) and they can view it anytime by clicking the Marketing button on the top navigation bar. Do NOT say anything else.' } };
                      } else {
                        const errData = await saveRes.json().catch(() => ({ error: 'Save failed' }));
                        const msg = errData.error || 'Save failed';
                        if (saveRes.status === 403) {
                          return { id: fc.id, name: fc.name, response: { result: 'PLAN_SAVE_FORBIDDEN. Tell the owner that saving strategic plans requires manager-level access. Ask them to log in as a manager.' } };
                        }
                        return { id: fc.id, name: fc.name, response: { error: msg } };
                      }
                    } catch {
                      return { id: fc.id, name: fc.name, response: { error: 'Could not save the plan. Please check your connection.' } };
                    }
                  } else if (fc.name === 'flashKeywords') {
                    const args = fc.args as any;
                    if (args.keywords && Array.isArray(args.keywords)) {
                      const newKeywords = args.keywords.map((text: string) => ({
                        id: Math.random(),
                        text,
                        x: (Math.random() - 0.5) * 160, // Clamped to -80 to 80px to prevent off-screen
                        y: (Math.random() - 0.5) * 160,
                        rotateX: (Math.random() - 0.5) * 60,
                        rotateY: (Math.random() - 0.5) * 60,
                        rotateZ: (Math.random() - 0.5) * 30
                      }));
                      setFloatingKeywords(prev => [...prev, ...newKeywords]);
                      
                      // Auto-remove after 4 seconds
                      setTimeout(() => {
                        setFloatingKeywords(prev => prev.filter(k => !newKeywords.find((n: any) => n.id === k.id)));
                      }, 4000);
                      
                      playUISound('emotion', 'excited'); // Soft holographic ping
                      return { id: fc.id, name: fc.name, response: { result: "Keywords flashed successfully." } };
                    }
                    return { id: fc.id, name: fc.name, response: { error: "Invalid keywords format" } };
                  }
                  return { id: fc.id, name: fc.name, response: { error: "Unknown function" } };
                })).then(responses => {
                  if (!isMountedRef.current || !sessionRef.current) return;
                  const currentSession = sessionRef.current;
                  try {
                    Promise.resolve(typeof currentSession.then === 'function' ? currentSession : Promise.resolve(currentSession))
                      .then((session: any) => {
                        if (!isMountedRef.current || !session) return;
                        session.sendToolResponse({ functionResponses: responses });
                      })
                      .catch((e: any) => console.warn('[Luna] Tool response send failed:', e));
                  } catch (e) { console.warn('[Luna] Tool response dispatch error:', e); }
                });
              }
            }

            const parts = message.serverContent?.modelTurn?.parts;
            if (parts) {
              for (const part of parts) {
                const base64Audio = part.inlineData?.data;
                if (base64Audio) {
                  setLunaStatus('Speaking');
                  const binaryString = window.atob(base64Audio);
                  const len = binaryString.length;
                  const bytes = new Uint8Array(len);
                  for (let i = 0; i < len; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                  }
                  try {
                    const audioCtx = audioContextRef.current;
                    if (!audioCtx || audioCtx.state === 'closed' || !isMountedRef.current) break;
                    const pcm16 = new Int16Array(bytes.buffer);
                    if (pcm16.length > 0) {
                      const sampleRate = 24000;
                      const audioBuffer = audioCtx.createBuffer(1, pcm16.length, sampleRate);
                      const channelData = audioBuffer.getChannelData(0);
                      for (let i = 0; i < pcm16.length; i++) {
                        channelData[i] = pcm16[i] / 32768.0;
                      }
                      const source = audioCtx.createBufferSource();
                      source.buffer = audioBuffer;
                      source.connect(audioCtx.destination);
                      const currentTime = audioCtx.currentTime;
                      const playTime = Math.max(currentTime, nextPlayTimeRef.current || 0);
                      source.start(playTime);
                      nextPlayTimeRef.current = playTime + audioBuffer.duration;
                      activeSourcesRef.current.push(source);
                      source.onended = () => {
                        activeSourcesRef.current = activeSourcesRef.current.filter(s => s !== source);
                        if (isMountedRef.current && activeSourcesRef.current.length === 0) {
                          setLunaStatus('Listening');
                        }
                      };
                    }
                  } catch (e) {
                    console.error("Error decoding audio:", e);
                  }
                }
              }
            }
          },
          onclose: () => {
            if (!isMountedRef.current) return;
            console.warn('[Luna] Gemini Live session closed');
            
            // CRITICAL: If user manually disconnected, do NOT auto-reconnect
            if (userManuallyDisconnectedRef.current) {
              console.log('[Luna] User manually disconnected — skipping auto-reconnect');
              (window as any).__lunaReconnectCount = 0;
              setIsLunaConnected(false);
              isStartingRef.current = false;
              setLunaStatus('Idle');
              setCurrentMessage(null);
              // Clean up vision capture timer
              if (visionCaptureIntervalRef.current) {
                clearInterval(visionCaptureIntervalRef.current);
                visionCaptureIntervalRef.current = null;
              }
              return;
            }
            
            // Auto-reconnect if session dropped unexpectedly (up to 3 retries)
            const retryCount = (window as any).__lunaReconnectCount || 0;
            if (retryCount < 3 && !isStartingRef.current) {
              (window as any).__lunaReconnectCount = retryCount + 1;
              const delay = Math.min(2000 * Math.pow(2, retryCount), 8000); // 2s, 4s, 8s
              console.log(`[Luna] Auto-reconnect attempt ${retryCount + 1}/3 in ${delay}ms...`);
              setCurrentMessage(`🔄 Reconnecting Luna... (${retryCount + 1}/3)`);
              setLunaStatus('Reconnecting');
              setTimeout(() => {
                if (isMountedRef.current && !userManuallyDisconnectedRef.current) {
                  startLuna();
                }
              }, delay);
            } else {
              // Max retries exhausted
              (window as any).__lunaReconnectCount = 0;
              setIsLunaConnected(false);
              isStartingRef.current = false;
              setCurrentMessage('⚡ Luna disconnected. Tap to reconnect.');
              setTimeout(() => setCurrentMessage(null), 5000);
              stopLuna();
            }
          },
          onerror: (err) => { 
            console.error("Live API error:", err);
            if (!isMountedRef.current) return;
            
            // CRITICAL: If user manually disconnected, do NOT auto-reconnect
            if (userManuallyDisconnectedRef.current) {
              console.log('[Luna] User manually disconnected — skipping error reconnect');
              (window as any).__lunaReconnectCount = 0;
              setIsLunaConnected(false);
              isStartingRef.current = false;
              setLunaStatus('Idle');
              setCurrentMessage(null);
              if (visionCaptureIntervalRef.current) {
                clearInterval(visionCaptureIntervalRef.current);
                visionCaptureIntervalRef.current = null;
              }
              return;
            }
            
            // Auto-reconnect on transient errors
            const retryCount = (window as any).__lunaReconnectCount || 0;
            if (retryCount < 3 && !isStartingRef.current) {
              (window as any).__lunaReconnectCount = retryCount + 1;
              const delay = Math.min(2000 * Math.pow(2, retryCount), 8000);
              console.log(`[Luna] Auto-reconnect after error, attempt ${retryCount + 1}/3 in ${delay}ms...`);
              setCurrentMessage(`🔄 Reconnecting Luna... (${retryCount + 1}/3)`);
              setLunaStatus('Reconnecting');
              setTimeout(() => {
                if (isMountedRef.current && !userManuallyDisconnectedRef.current) {
                  startLuna();
                }
              }, delay);
            } else {
              (window as any).__lunaReconnectCount = 0;
              setIsLunaConnected(false);
              isStartingRef.current = false;
              setCurrentMessage('⚠️ Connection lost. Tap Luna to retry.');
              setTimeout(() => setCurrentMessage(null), 5000);
              stopLuna();
            }
          }
        },
        config: {
          responseModalities: [Modality.AUDIO],
          speechConfig: { voiceConfig: { prebuiltVoiceConfig: { voiceName: dynamicVoiceName || "Kore" } } },
          systemInstruction,
          tools: activeDeclarations.length > 0 ? [{ functionDeclarations: activeDeclarations }] : [],
          inputAudioTranscription: {},
          outputAudioTranscription: {},
        },
      });
    } catch (e: any) {
      console.error("[Luna Voice] Error starting session:", e);
      setLunaStatus('Idle');
      isStartingRef.current = false;
      if (e?.name === 'NotAllowedError' || String(e?.message || '').toLowerCase().includes('permission') || String(e?.message || '').toLowerCase().includes('microphone')) {
        setCurrentMessage("⚠️ Microphone permission blocked by browser! Click the Lock 🔒 icon in the URL bar to allow microphone access.");
        setTimeout(() => setCurrentMessage(null), 8000);
      } else {
        setCurrentMessage("⚠️ Connection error. Click to retry.");
        setTimeout(() => setCurrentMessage(null), 5000);
      }
    }
  };

  // Silence Luna (stop audio playback) WITHOUT killing the session — mic stays hot
  const silenceLuna = () => {
    nextPlayTimeRef.current = 0;
    activeSourcesRef.current.forEach(source => { try { source.stop(); } catch (e) {} });
    activeSourcesRef.current = [];
    setLunaStatus('Listening');
  };

  // ── Computer Vision helpers ────────────────────────────────────────────────
  const imageToBase64 = (file: File): Promise<{ dataUrl: string; base64: string; mimeType: string }> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        const base64 = dataUrl.split(',')[1];
        resolve({ dataUrl, base64, mimeType: file.type });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const img = await imageToBase64(file);
      setPendingImage(img);
      SoundManager.play('ui_click');
    } catch { /* ignore */ }
    // Reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const sendVisionMessage = () => {
    if (!pendingImage || !sessionRef.current) return;
    const parts: any[] = [
      { text: 'I have uploaded an image. Please analyze it and tell me what you see. If it looks like a product, identify it and offer to search for it.' },
      { inlineData: { mimeType: pendingImage.mimeType, data: pendingImage.base64 } }
    ];
    const dispatch = (s: any) => {
      try {
        if (typeof s.sendClientContent === 'function') {
          s.sendClientContent({ turns: [{ role: 'user', parts }], turnComplete: true });
        } else if (typeof s.send === 'function') {
          s.send({ clientContent: { turns: [{ role: 'user', parts }], turnComplete: true } });
        }
      } catch (e) { console.warn('[Vision] Send failed:', e); }
    };
    if (typeof sessionRef.current.then === 'function') sessionRef.current.then(dispatch).catch(() => {});
    else dispatch(sessionRef.current);
    setPendingImage(null);
    SoundManager.play('ui_open');
  };

  // Screen Capture: Request vision from parent window and capture frame
  const [visionActive, setVisionActive] = useState(false);
  const visionFrameRef = useRef<{ base64: string; mimeType: string } | null>(null);

  const requestScreenVision = () => {
    // Vision button is now on the widget (parent) — this is just a status indicator
    // User clicks the 👁 button on the widget to start screen capture
  };

  // Listen for vision results from parent
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.data?.type === 'LUNA_VISION_ACTIVE') {
        const isActive = !!e.data.payload?.active;
        setVisionActive(isActive);
        lunaVisionActiveRef.current = isActive;

        // ── INSTANT SYSTEM NOTIFICATION & INITIAL FRAME INJECTION ON PERMISSION GRANT ──
        if (isActive && sessionRef.current) {
          console.log('📷 [Luna Vision] Permission GRANTED! Ingesting initial fresh screen frame into active session...');
          lunaVisionFrameRef.current = null;
          visionFrameRef.current = null;
          window.parent.postMessage({ type: 'LUNA_CAPTURE_FRAME' }, '*');

          let attempts = 0;
          const poll = setInterval(() => {
            attempts++;
            const frame = visionFrameRef.current || lunaVisionFrameRef.current;
            if (frame && sessionRef.current) {
              clearInterval(poll);
              console.log('📷 [Luna Vision] Initial frame attached to Gemini live session context via RealtimeInput!');
              Promise.resolve(typeof sessionRef.current.then === 'function' ? sessionRef.current : Promise.resolve(sessionRef.current))
                .then((session: any) => {
                  if (!session) return;
                  if (typeof session.sendRealtimeInput === 'function') {
                    // Send frame via realtime stream video input
                    try {
                      session.sendRealtimeInput({
                        video: {
                          data: frame.base64,
                          mimeType: frame.mimeType || 'image/jpeg'
                        }
                      });
                    } catch (err) {
                      console.warn('[Luna Vision] realtimeInput error fallback');
                    }
                  }
                }).catch(() => {});
            } else if (attempts >= 10) {
              clearInterval(poll);
            }
          }, 200);
        }
      } else if (e.data?.type === 'LUNA_FRAME_CAPTURED') {
        visionFrameRef.current = e.data.payload || null;
      } else if (e.data?.type === 'LUNA_VISION_RESULT') {
        if (e.data.payload?.granted) {
          setVisionActive(true);
          lunaVisionActiveRef.current = true;
        }
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // Capture a screen frame before sending user message (with reliable retry polling)
  const captureAndSendWithVision = (text: string) => {
    lastUserPromptRef.current = text;
    if (!visionActive || !sessionRef.current) return false;
    
    // Request a frame capture from parent & clear stale frame
    lunaVisionFrameRef.current = null;
    visionFrameRef.current = null;
    window.parent.postMessage({ type: 'LUNA_CAPTURE_FRAME' }, '*');
    
    // Poll for frame arrival (up to 2s with 200ms intervals) — replaces fragile fixed 300ms wait
    let attempts = 0;
    const pollFrame = setInterval(() => {
      attempts++;
      const frame = visionFrameRef.current || lunaVisionFrameRef.current;
      if (frame && sessionRef.current) {
        clearInterval(pollFrame);
        console.log('[Luna Vision] 📸 Sending vision frame with user text message');
        
        const parts: any[] = [
          { text: `[FRESH SCREEN CAPTURE — Luna can see the user's live screen RIGHT NOW. Carefully read ALL visible text, numbers, math equations, diagrams, images. Answer based on what you ACTUALLY see.]\n\nUser: ${text}` },
          { inlineData: { mimeType: frame.mimeType, data: frame.base64 } }
        ];
        
        const dispatch = (s: any) => {
          try {
            if (typeof s.sendClientContent === 'function') {
              s.sendClientContent({ turns: [{ role: 'user', parts }], turnComplete: true });
            } else if (typeof s.send === 'function') {
              s.send({ clientContent: { turns: [{ role: 'user', parts }], turnComplete: true } });
            }
          } catch (e) { console.warn('[Vision] Send failed:', e); }
        };
        
        if (typeof sessionRef.current.then === 'function') sessionRef.current.then(dispatch).catch(() => {});
        else dispatch(sessionRef.current);
      } else if (attempts >= 10) {
        clearInterval(pollFrame);
        console.warn('[Luna Vision] Frame not received in time, sending text-only');
        // Fallback: send text without vision frame
        const dispatch = (s: any) => {
          try {
            if (typeof s.sendClientContent === 'function') {
              s.sendClientContent({ turns: [{ role: 'user', parts: [{ text }] }], turnComplete: true });
            }
          } catch (e) {}
        };
        if (sessionRef.current) {
          if (typeof sessionRef.current.then === 'function') sessionRef.current.then(dispatch).catch(() => {});
          else dispatch(sessionRef.current);
        }
      }
    }, 200);
    
    return true;
  };

  // Universal face click handler — toggle: connected→full disconnect, disconnected→connect
  const handleFaceClick = () => {
    if (lunaStatus === 'Connecting...') return; // Ignore clicks during connection handshake
    if (isLunaConnected || lunaStatus === 'Speaking' || lunaStatus === 'Listening') {
      // User manually disconnecting — set dedicated flag to BLOCK all auto-reconnect
      userManuallyDisconnectedRef.current = true;
      (window as any).__lunaReconnectCount = 99; // Also set high as belt-and-suspenders
      // Fully disconnect the session — stop audio, mic, and websocket
      stopLuna();
      // Clean up vision capture timer
      if (visionCaptureIntervalRef.current) {
        clearInterval(visionCaptureIntervalRef.current);
        visionCaptureIntervalRef.current = null;
      }
    } else {
      // Disconnected (Idle) — start a new session
      startLuna();
    }
  };

  const handleHologramAction = (item: any) => {
    console.log("[Neural Link] Hologram item selected:", item);

    // Broad item name extraction — covers all common key patterns AI might use
    const itemName =
      item.TITLE || item.NAME || item.title || item.name ||
      item.Title || item.Name || item.COURSE || item.Course || item.course ||
      item.PRODUCT || item.Product || item.product ||
      item.FEATURE || item.Feature || item.feature ||
      item.STORE || item.Store || item.store ||
      Object.values(item)[0] as string ||
      "this item";

    MemoryManager.updateTopic(String(itemName));

    // Handle course selection or registration form popup directly
    if (item?.action === 'FORM_SUBMITTED') {
      SoundManager.play('cha_ching');
      setHologramAction(null);
      setHologramData(null);
      return;
    }

    if (hologramAction === 'SHOW_COURSES' || role === 'tenant' || item?.action === 'SHOW_FORM') {
      setPrevHologramAction(hologramAction);
      setPrevHologramData(hologramData);
      setHologramAction('SHOW_FORM');
      setHologramData([
        { label: 'Full Name', placeholder: 'Enter your full name', type: 'text' },
        { label: 'Email / Mobile Phone', placeholder: 'Enter email address or mobile number', type: 'text' },
        { label: 'Selected Program', placeholder: String(itemName), type: 'text' },
        { label: 'Qualifications / Questions', placeholder: 'Enter your highest qualification or any questions', type: 'textarea' }
      ]);
      playUISound('ping');
      return;
    }

    const systemMessage = `System Alert: The user selected the '${itemName}' card. The system has already opened the Product DetailView.
CRITICAL O2O DIRECTIVE: This is an Online-to-Offline platform.
- If it's a product: Give ONE short verbal acknowledgment ONLY (e.g., 'Great choice!' or 'Nice pick!'). DO NOT call checkGlobalInventory. DO NOT call showHologramWidget. The UI is already updated — WAIT silently for the user's next input.
  → ONLY call 'checkGlobalInventory' with displayMode: 'map' if the user EXPLICITLY says 'where can I buy', 'directions', 'which store', 'map', or asks for a physical location — NEVER proactively.
- If it's a course or service: ask them if they want to register and set uiAction: 'SHOW_FORM' with relevant fields in uiData.
NEVER set uiAction to 'SHOW_CHECKOUT' or 'SHOW_MORE_DETAILS'. NEVER mention online payment, cart, or inquiry forms for products.
CRITICAL NEGATIVE CONSTRAINT: NEVER speak internal system logs, metadata, or developer jargon. Keep all speech 100% human-like and conversational.`;

    playUISound('ping');

    if (!sessionRef.current) {
      console.error("[Neural Link] Session not active. Cannot transmit interaction.");
      return;
    }

    const dispatchTurn = (session: any) => {
      try {
        if (typeof session.sendClientContent === 'function') {
          session.sendClientContent({
            turns: [{ role: "user", parts: [{ text: systemMessage }] }],
            turnComplete: true
          });
        } else if (typeof session.send === 'function') {
          session.send({
            clientContent: {
              turns: [{ role: "user", parts: [{ text: systemMessage }] }],
              turnComplete: true
            }
          });
        } else {
          console.error("[Neural Link] No known send method on session object.");
        }
      } catch (e) {
        console.error("[Neural Link] Failed to dispatch turn:", e);
      }
    };

    if (typeof sessionRef.current.then === 'function') {
      sessionRef.current.then(dispatchTurn).catch((e: any) => console.error("[Neural Link] Promise rejection:", e));
    } else {
      dispatchTurn(sessionRef.current);
    }
  };

  const stopLuna = (triggerCallback = false) => {
    // 🛰️ Telemetry: Notify Master Admin Live HUD that session concluded cleanly
    const curVoiceSessionId = voiceSessionIdRef.current;
    if (sessionStartTimeRef.current > 0 || isLunaConnected || isListening) {
      const endTime = Date.now();
      const durSec = sessionStartTimeRef.current > 0 ? Math.max(1, Math.round((endTime - sessionStartTimeRef.current) / 1000)) : 1;
      const totTokens = voiceTokenInputRef.current + voiceTokenOutputRef.current;
      fetch('/api/luna/turn-telemetry', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(role === 'store' && apiKey ? { 'x-store-api-key': apiKey } : {}),
          ...(role === 'tenant' && apiKey ? { 'x-tenant-api-key': apiKey } : {})
        },
        body: JSON.stringify({
          sessionId: curVoiceSessionId,
          role,
          tenantId: role === 'tenant' ? apiKey : undefined,
          status: 'ended',
          keyMode: lunaKeyModeRef.current,
          keySource: lunaKeySourceRef.current,
          durationSeconds: durSec,
          tokensUsed: totTokens,
          promptPreview: 'Session concluded by user',
          responsePreview: `Call ended cleanly (${durSec}s, ${totTokens} tokens)`,
        }),
        keepalive: true,
      }).catch(() => {});
    }

    // Luna Intelligence: POST session analytics (fire-and-forget, all roles)
    if (sessionStartTimeRef.current > 0) {
      const endTime = Date.now();
      const sessionPayload = {
        sessionId:       curVoiceSessionId,
        role,
        lunaMode:        sessionLunaModeRef.current,
        connectedAt:     new Date(sessionStartTimeRef.current).toISOString(),
        disconnectedAt:  new Date(endTime).toISOString(),
        durationSeconds: Math.round((endTime - sessionStartTimeRef.current) / 1000),
        msgCount:        sessionMsgCountRef.current,
        tokensUsed:      voiceTokenInputRef.current + voiceTokenOutputRef.current,
      };
      fetch('/api/luna/session-log', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          ...(role === 'store' && apiKey ? { 'x-store-api-key': apiKey } : {}),
          ...(role === 'tenant' && apiKey ? { 'x-tenant-api-key': apiKey } : {})
        },
        body: JSON.stringify({
          ...sessionPayload,
          tenantId: role === 'tenant' ? apiKey : undefined,
        }),
      }).catch(() => {}); // fire-and-forget — analytics loss acceptable vs crash
    }
    sessionStartTimeRef.current = 0;
    sessionMsgCountRef.current  = 0;
    sessionLunaModeRef.current  = 'standard'; // reset for next session

    // POST accumulated voice token usage to billing backend (store role only)
    if (role === 'store' && apiKey && curVoiceSessionId && (voiceTokenInputRef.current > 0 || voiceTokenOutputRef.current > 0)) {
      const payload = {
        sessionId:    curVoiceSessionId,
        inputTokens:  voiceTokenInputRef.current,
        outputTokens: voiceTokenOutputRef.current,
      };
      // fire-and-forget — do not block teardown
      fetch('/api/luna/usage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-store-api-key': apiKey },
        body: JSON.stringify(payload),
      }).catch(() => { /* silent — billing loss acceptable vs crash */ });
    }
    // Reset per-session accumulators for the next session
    voiceSessionIdRef.current   = crypto.randomUUID();
    voiceTokenInputRef.current  = 0;
    voiceTokenOutputRef.current = 0;

    if (triggerCallback && onDisconnect) onDisconnect();
    setIsListening(false);
    setIsLunaConnected(false);
    setLunaStatus('Idle');
    setSentiment('neutral');
    setCurrentMessage(null);
    nextPlayTimeRef.current = 0;
    activeSourcesRef.current.forEach(source => { try { source.stop(); } catch (e) {} });
    activeSourcesRef.current = [];
    
    // Stop ambient sound if running
    stopAmbientSound(0.1);

    if (processorRef.current) { processorRef.current.disconnect(); processorRef.current = null; }
    if (mediaStreamRef.current) { mediaStreamRef.current.getTracks().forEach(track => track.stop()); mediaStreamRef.current = null; }
    // Clean up periodic vision capture timer
    if (visionCaptureIntervalRef.current) { clearInterval(visionCaptureIntervalRef.current); visionCaptureIntervalRef.current = null; }
    if (audioContextRef.current) { 
      if (audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
      audioContextRef.current = null; 
    }
    if (sessionRef.current) { sessionRef.current.then((session: any) => session.close()); sessionRef.current = null; }
    isStartingRef.current = false;
  };

  // ── Page Unload Beacon: Ensure live session ends when tab or window is closed ───────
  useEffect(() => {
    const handleUnload = () => {
      stopLuna(false);
    };
    window.addEventListener('beforeunload', handleUnload);
    window.addEventListener('pagehide', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      window.removeEventListener('pagehide', handleUnload);
    };
  }, []);

  // ── Cleanup: kill audio + session when component unmounts ──────────────────
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      stopLuna(false);
      nextPlayTimeRef.current = 0;
      activeSourcesRef.current.forEach(source => { try { source.stop(); } catch (e) {} });
      activeSourcesRef.current = [];
      if (processorRef.current) {
        try { processorRef.current.onaudioprocess = null; processorRef.current.disconnect(); } catch (e) {}
        processorRef.current = null;
      }
      if (mediaStreamRef.current) { mediaStreamRef.current.getTracks().forEach(track => track.stop()); mediaStreamRef.current = null; }
      if (audioContextRef.current) {
        try { if (audioContextRef.current.state !== 'closed') { audioContextRef.current.close(); } } catch (e) {}
        audioContextRef.current = null;
      }
      if (sessionRef.current) { try { sessionRef.current.then?.((s: any) => s?.close?.()); } catch (e) {} sessionRef.current = null; }
      isStartingRef.current = false;
    };
  }, []);

  const getSentimentStyles = () => {
    if (!isListening) return { 
      ring1: 'border-cyan-500/20', 
      ring2: 'border-purple-500/10', 
      ring3: 'border-emerald-500/10', 
      core: 'bg-cyan-500/20', 
      buttonBorder: 'border-white/20', 
      buttonShadow: 'shadow-[0_0_50px_rgba(6,182,212,0.4)]', 
      buttonHover: 'hover:border-cyan-400 hover:shadow-[0_0_80px_rgba(6,182,212,0.6)]',
      icon: 'text-cyan-400 drop-shadow-[0_0_20px_rgba(6,182,212,1)]'
    };

    switch (sentiment) {
      case 'happy': return {
        ring1: 'border-amber-400/60', ring2: 'border-yellow-300/40', ring3: 'border-orange-400/30',
        core: 'bg-amber-400/50', buttonBorder: 'border-amber-400/80', buttonShadow: 'shadow-[0_0_100px_rgba(251,191,36,0.8)]', buttonHover: '',
        icon: 'text-amber-300 drop-shadow-[0_0_25px_rgba(251,191,36,1)]'
      };
      case 'excited': return {
        ring1: 'border-pink-500/80', ring2: 'border-fuchsia-500/60', ring3: 'border-rose-500/40',
        core: 'bg-pink-500/60', buttonBorder: 'border-pink-500/90', buttonShadow: 'shadow-[0_0_120px_rgba(236,72,153,1)]', buttonHover: '',
        icon: 'text-pink-300 drop-shadow-[0_0_35px_rgba(236,72,153,1)]'
      };
      case 'sad': return {
        ring1: 'border-blue-600/80', ring2: 'border-indigo-600/60', ring3: 'border-sky-600/40',
        core: 'bg-blue-600/60', buttonBorder: 'border-blue-600/90', buttonShadow: 'shadow-[0_0_120px_rgba(37,99,235,1)]', buttonHover: '',
        icon: 'text-blue-300 drop-shadow-[0_0_35px_rgba(37,99,235,1)]'
      };
      case 'angry': return {
        ring1: 'border-red-600/80', ring2: 'border-orange-600/60', ring3: 'border-rose-600/40',
        core: 'bg-red-600/60', buttonBorder: 'border-red-600/90', buttonShadow: 'shadow-[0_0_120px_rgba(220,38,38,1)]', buttonHover: '',
        icon: 'text-red-400 drop-shadow-[0_0_35px_rgba(220,38,38,1)]'
      };
      case 'surprised': return {
        ring1: 'border-emerald-400/80', ring2: 'border-teal-400/60', ring3: 'border-cyan-400/40',
        core: 'bg-emerald-400/60', buttonBorder: 'border-emerald-400/90', buttonShadow: 'shadow-[0_0_120px_rgba(52,211,153,1)]', buttonHover: '',
        icon: 'text-emerald-300 drop-shadow-[0_0_35px_rgba(52,211,153,1)]'
      };
      case 'concentrating': return {
        ring1: 'border-violet-600/80', ring2: 'border-purple-600/60', ring3: 'border-fuchsia-600/40',
        core: 'bg-violet-600/60', buttonBorder: 'border-violet-600/90', buttonShadow: 'shadow-[0_0_120px_rgba(124,58,237,1)]', buttonHover: '',
        icon: 'text-violet-300 drop-shadow-[0_0_35px_rgba(124,58,237,1)]'
      };
      default: return { // neutral
        ring1: 'border-cyan-400/60', ring2: 'border-teal-400/40', ring3: 'border-emerald-400/30',
        core: 'bg-cyan-400/50', buttonBorder: 'border-cyan-400/80', buttonShadow: 'shadow-[0_0_100px_rgba(34,211,238,0.8)]', buttonHover: '',
        icon: 'text-cyan-300 drop-shadow-[0_0_25px_rgba(34,211,238,1)]'
      };
    }
  };
  const sStyles = getSentimentStyles();

  return (
    <LunaErrorBoundary>
      <>
      {/* STRATEGIC CONSULTANT HUD — triggered by generateStrategicPlan tool */}
      <AnimatePresence>
        {strategicPlanData && (
          <StrategicConsultantHUD
            data={strategicPlanData}
            onClose={() => setStrategicPlanData(null)}
          />
        )}
      </AnimatePresence>

      {/* LIVE ANALYTICS DASHBOARD — real data from /api/luna/analytics */}
      {showLiveDashboard && (
        <HologramDashboard
          apiKey={apiKey}
          onClose={() => setShowLiveDashboard(false)}
          onDailySummary={handleDailySummary}
        />
      )}

      {/* DEDICATED WEEKLY CHART — focused standalone modal for getSalesTrends */}
      {showWeeklyChart && weeklyChartData && (
        <WeeklyChartModal
          data={weeklyChartData}
          onClose={() => setShowWeeklyChart(false)}
        />
      )}

      {/* HOLOGRAM OVERLAY — Only appears when a uiAction is triggered */}
      {hologramAction && hologramData && (
        <div className="fixed inset-0 z-50 pointer-events-none bg-transparent flex items-center justify-center text-white overflow-hidden">
          <HologramDisplay 
            action={hologramAction} 
            data={hologramData} 
            role={role}
            onClose={() => { setHologramAction(null); setHologramData(null); setPrevHologramAction(null); setPrevHologramData(null); }} 
            onBack={prevHologramAction ? () => { setHologramAction(prevHologramAction); setHologramData(prevHologramData); setPrevHologramAction(null); setPrevHologramData(null); } : undefined}
            onActionClick={handleHologramAction}
            onHover={setHoverContext}
          />
          {/* Minimized Luna face in corner during hologram (Only rendered in standalone mode, suppressed in widget mode to prevent double Luna) */}
          {!(typeof window !== 'undefined' && window.location.search.includes('widget=true')) && (
            <motion.div 
              className="fixed right-4 bottom-4 sm:right-8 sm:bottom-8 z-[60] pointer-events-auto"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 0.4 }}
              transition={{ duration: 0.5, type: "spring" }}
            >
              <motion.div className="relative inline-flex items-center justify-center">
                {/* Neural Audio Aura */}
                {lunaStatus === 'Speaking' && (
                  <div className="absolute inset-[-100px] rounded-full border border-cyan-500 animate-ping" style={{ animationDuration: '1s' }} />
                )}
                <div className={`absolute inset-[-40px] rounded-full border border-dashed animate-[spin_10s_linear_infinite] transition-colors duration-700 ${sStyles.ring1}`} />
                <button 
                  onClick={handleFaceClick}
                  className={`relative z-10 w-48 h-48 rounded-full flex items-center justify-center bg-black/50 backdrop-blur-3xl border transition-all duration-500 overflow-hidden ${sStyles.buttonBorder} ${sStyles.buttonShadow}`}
                >
                  <LunaFace 
                    sentiment={sentiment} 
                    customAvatarUrl={tenantAvatarUrl || undefined}
                    isListening={isListening} 
                    isSpeaking={lunaStatus === 'Speaking'}
                    isWaving={isWaving}
                    className={`relative z-10 w-32 h-32 transition-colors duration-700 ${sStyles.icon}`} 
                  />
                </button>
              </motion.div>
            </motion.div>
          )}
        </div>
      )}

      {/* LUNA FACE — Sits in natural document flow */}
      {!hologramAction && (
        <div className="min-h-screen flex flex-col items-center justify-center text-white">
          <motion.div 
            className="relative z-20"
            initial={false}
          >
            {/* Desktop status badge — absolute child of non-animated wrapper, safely flanking the orb */}
            <motion.div
              initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1 }}
              className="luna-badge hidden md:flex flex-col absolute top-4 -right-52 z-30 bg-black/80 backdrop-blur-xl border border-cyan-500/30 px-5 py-3 rounded-lg text-xs font-mono text-cyan-400 uppercase tracking-widest shadow-[0_0_30px_rgba(6,182,212,0.2)] gap-1 pointer-events-none"
            >
              <div className="flex items-center gap-2">
                <Activity size={14} className={isLunaConnected ? 'animate-pulse text-pink-400' : 'text-slate-600'} />
                Status: {isLunaConnected ? lunaStatus : 'Offline'}
              </div>
              <div className="flex items-center gap-2 text-[10px] text-slate-500">
                <div className={`w-2 h-2 rounded-full ${isLunaConnected ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`} />
                {isLunaConnected ? 'Live Connection Active' : 'Neural Link Severed'}
              </div>
              {isListening && (
                <div className="text-[10px] text-slate-400 mt-1 border-t border-white/10 pt-1">
                  Emotion: <span className="text-white">{sentiment}</span>
                </div>
              )}
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.2 }}
              className="luna-badge hidden md:flex items-center absolute bottom-4 -left-44 z-30 bg-black/80 backdrop-blur-xl border border-purple-500/30 px-5 py-3 rounded-lg text-xs font-mono text-purple-400 uppercase tracking-widest shadow-[0_0_30px_rgba(139,92,246,0.2)] gap-2 pointer-events-none"
            >
              <Network size={14} />
              Net: {connectionStatus}
            </motion.div>

            <div 
              className="relative inline-flex items-center justify-center"
            >
              {/* Floating Keywords (JARVIS UI - 3D Hologram) */}
              <AnimatePresence>
                {floatingKeywords.map((kw: any) => (
                  <motion.div
                    key={kw.id}
                    initial={{ opacity: 0, scale: 0.5, x: kw.x, y: kw.y, rotateX: kw.rotateX || 0, rotateY: kw.rotateY || 0, rotateZ: kw.rotateZ || 0 }}
                    animate={{ opacity: [0, 1, 1, 0], scale: [0.5, 1.2, 1, 0.8], x: kw.x * 1.5, y: kw.y * 1.5, rotateX: (kw.rotateX || 0) + 15, rotateY: (kw.rotateY || 0) + 15, rotateZ: (kw.rotateZ || 0) + 5 }}
                    exit={{ opacity: 0, filter: 'blur(10px)', scale: 0.5 }}
                    transition={{ duration: 3.5, times: [0, 0.2, 0.7, 1] }}
                    className="absolute z-30 pointer-events-none"
                    style={{ perspective: '600px', transformStyle: 'preserve-3d' }}
                  >
                    <span className="text-cyan-300 font-black text-lg md:text-xl tracking-widest uppercase drop-shadow-[0_0_20px_rgba(6,182,212,0.9)] bg-cyan-950/40 backdrop-blur-sm px-4 py-2 rounded-xl border border-cyan-400/50 whitespace-nowrap">
                      {kw.text}
                    </span>
                  </motion.div>
                ))}
              </AnimatePresence>

              {/* Speech Bubble */}
              <SpeechBubble message={currentMessage || ''} isVisible={!!currentMessage} />

              {/* Neural Audio Aura (visible when Speaking) */}
              {lunaStatus === 'Speaking' && (
                <div className="absolute inset-[-100px] rounded-full border border-cyan-500 animate-ping" style={{ animationDuration: '1s' }} />
              )}
              
              {/* Multilayered Arc Reactor Rings */}
              <div className={`absolute inset-[-40px] rounded-full border border-dashed animate-[spin_10s_linear_infinite] transition-colors duration-700 ${sStyles.ring1}`} />
              <div className={`absolute inset-[-80px] rounded-full border-2 border-dotted animate-[spin_15s_linear_infinite_reverse] transition-colors duration-700 ${sStyles.ring2}`} />
              <div className={`absolute inset-[-120px] rounded-full border transition-colors duration-700 ${sStyles.ring3} ${isListening ? 'animate-ping' : ''}`} style={{ animationDuration: '3s' }} />
              
              {/* Glowing Core */}
              <div className={`absolute inset-[-60px] rounded-full blur-[50px] transition-all duration-700 ${sStyles.core} ${isListening ? 'animate-pulse' : ''}`} />
              
              <button 
                onClick={handleFaceClick}
                className={`relative z-10 w-48 h-48 rounded-full flex items-center justify-center bg-black/50 backdrop-blur-3xl border transition-all duration-500 overflow-hidden pointer-events-auto ${sStyles.buttonBorder} ${sStyles.buttonShadow} ${sStyles.buttonHover} ${isListening ? 'scale-110' : 'hover:scale-105'}`}
              >
                <div className="absolute inset-0 bg-gradient-to-tr from-white/5 to-transparent" />
                <LunaFace 
                  sentiment={sentiment} 
                  customAvatarUrl={tenantAvatarUrl || undefined}
                  isListening={isListening} 
                  isSpeaking={lunaStatus === 'Speaking'}
                  isWaving={isWaving}
                  className={`relative z-10 w-32 h-32 transition-colors duration-700 ${sStyles.icon}`} 
                />
                
                {/* Inner Reactor Lines */}
                <div className="absolute inset-2 rounded-full border border-white/5" />
                <div className="absolute inset-4 rounded-full border border-white/5" />
              </button>

              {/* ── Language Picker (desktop: top-right of orb) ── */}
              <div className="hidden md:block absolute -top-4 -right-16 pointer-events-auto">
                <div className="relative">
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setShowLangPicker(v => !v)}
                    title="Change Luna's language"
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-black/60 backdrop-blur border border-white/10 hover:border-cyan-500/40 transition-all"
                  >
                    <Globe size={11} className="text-cyan-400" />
                    <span className="text-[10px] font-mono font-bold text-cyan-300 uppercase">{preferredLang}</span>
                  </motion.button>
                  <AnimatePresence>
                    {showLangPicker && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: -4 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: -4 }}
                        className="absolute top-full mt-1 right-0 bg-black/90 backdrop-blur-xl border border-white/10 rounded-xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.6)] z-50 min-w-[160px]"
                      >
                        {[
                          { code: 'si', label: 'Sinhala' }, { code: 'en', label: 'English' },
                          { code: 'ta', label: 'Tamil' }, { code: 'zh', label: 'Chinese' },
                          { code: 'ja', label: 'Japanese' }, { code: 'ko', label: 'Korean' },
                          { code: 'hi', label: 'Hindi' }, { code: 'ar', label: 'Arabic' },
                          { code: 'fr', label: 'French' }, { code: 'de', label: 'German' },
                          { code: 'es', label: 'Spanish' }, { code: 'ru', label: 'Russian' },
                        ].map(({ code, label }) => (
                          <button key={code} onClick={() => {
                            localStorage.setItem('luna_preferred_language', code);
                            setPreferredLang(code);
                            setShowLangPicker(false);
                          }}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-left text-[11px] font-mono hover:bg-white/10 transition-colors ${preferredLang === code ? 'text-cyan-300 bg-cyan-500/10' : 'text-slate-300'}`}
                          >
                            <span className="w-6 text-[9px] font-black text-slate-500 uppercase">{code}</span>
                            <span>{label}</span>
                            {preferredLang === code && <span className="ml-auto text-cyan-400 text-[8px]">●</span>}
                          </button>
                        ))}
                        <div className="px-3 py-1.5 border-t border-white/5">
                          <p className="text-[9px] text-slate-600 font-mono">Saved to browser</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* ── Vision Upload Controls (desktop: absolute below orb | mobile: in fixed bottom bar) ── */}
              <div className="hidden md:flex absolute -bottom-28 left-1/2 -translate-x-1/2 flex-col items-center gap-2 pointer-events-auto">
                {/* Thumbnail preview */}
                <AnimatePresence>
                  {pendingImage && (
                    <motion.div
                      initial={{ opacity: 0, y: 8, scale: 0.9 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.9 }}
                      className="relative w-20 h-20 rounded-xl overflow-hidden border-2 border-cyan-400 shadow-[0_0_20px_rgba(6,182,212,0.6)]"
                    >
                      <img src={pendingImage.dataUrl} alt="preview" className="w-full h-full object-cover" />
                      <button
                        onClick={() => setPendingImage(null)}
                        className="absolute top-0.5 right-0.5 bg-black/70 rounded-full p-0.5 hover:bg-red-500/80 transition-colors"
                      >
                        <X size={10} className="text-white" />
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
                {/* Camera + Vision + Send row */}
                <div className="flex items-center gap-2">
                  {/* Hidden file input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    whileHover={{ scale: 1.1 }}
                    onClick={() => fileInputRef.current?.click()}
                    title="Upload image for Luna to analyze"
                    className="w-10 h-10 rounded-full bg-black/60 backdrop-blur border border-cyan-500/40 flex items-center justify-center text-cyan-400 hover:border-cyan-400 hover:shadow-[0_0_15px_rgba(6,182,212,0.5)] transition-all"
                  >
                    <Camera size={16} />
                  </motion.button>
                  {/* Luna Vision Screen Permission Controls */}
                  {!visionActive ? (
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        window.parent.postMessage({ type: 'LUNA_START_VISION' }, '*');
                      }}
                      title="Click to allow Luna to view your screen and teach you"
                      className="px-3 h-9 rounded-full bg-gradient-to-r from-purple-600 via-indigo-600 to-cyan-600 hover:from-purple-500 hover:to-cyan-500 text-white font-bold text-[10px] flex items-center gap-1.5 shadow-[0_0_15px_rgba(6,182,212,0.4)] transition-all cursor-pointer"
                    >
                      <span>📷</span>
                      <span>Allow Screen View (ලූනාට Screen එක බලන්න දෙන්න)</span>
                    </motion.button>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="px-2.5 h-9 rounded-full bg-emerald-500/20 backdrop-blur border border-emerald-400/60 flex items-center gap-1.5 text-emerald-300 shadow-[0_0_12px_rgba(16,185,129,0.4)] text-[10px] font-bold"
                      title="Luna can see your screen ✅"
                    >
                      <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping shrink-0" />
                      <span>Screen View Active ✅</span>
                    </motion.div>
                  )}
                  {pendingImage && (
                    <motion.button
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={sendVisionMessage}
                      className="px-3 h-10 rounded-full bg-cyan-500/20 backdrop-blur border border-cyan-400 text-cyan-300 text-[10px] font-mono font-bold uppercase tracking-widest hover:bg-cyan-500/40 hover:shadow-[0_0_15px_rgba(6,182,212,0.6)] transition-all"
                    >
                      Analyze ▶
                    </motion.button>
                  )}
                </div>
              </div>

            </div>
          </motion.div>
        </div>
      )}
      </>
      {/* ── Mobile Status Bar (top) — unified, outside transform tree ── */}
      {!hologramAction && (
        <>
          {/* MOBILE: single frosted status bar pinned to top */}
          <motion.div
            initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
            className="md:hidden fixed top-0 inset-x-0 z-[10000] flex items-center justify-between gap-3 px-4 py-3 pointer-events-auto"
            style={{ background: 'rgba(2,11,20,0.88)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
          >
            {/* Net indicator — left */}
            <div className="flex items-center gap-1.5 text-[10px] font-mono text-purple-400 uppercase tracking-widest shrink-0">
              <Network size={11} />
              <span>{connectionStatus}</span>
            </div>
            {/* Status — centre, fills available space */}
            <div className="flex items-center gap-1.5 text-[10px] font-mono text-cyan-400 uppercase tracking-widest flex-1 justify-center">
              <Activity size={11} className={isLunaConnected ? 'animate-pulse text-pink-400' : 'text-slate-600'} />
              <span>{isLunaConnected ? lunaStatus : 'Offline'}</span>
              <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${isLunaConnected ? 'bg-emerald-400 animate-pulse' : 'bg-red-500'}`} />
            </div>
            {/* Close button — right */}
            <button
              onClick={() => onDisconnect?.()}
              className="shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/10 border border-white/20 text-white/70 text-[10px] font-bold font-mono uppercase tracking-widest"
            >
              <X size={11} /> Close
            </button>
          </motion.div>

          {/* MOBILE: frosted bottom bar — camera/vision controls + disconnect hint */}
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
            className="md:hidden fixed bottom-0 inset-x-0 z-50 flex items-center justify-center gap-4 px-6 py-4 pointer-events-auto"
            style={{ background: 'rgba(2,11,20,0.82)', backdropFilter: 'blur(20px)', borderTop: '1px solid rgba(255,255,255,0.06)' }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />
            <AnimatePresence>
              {pendingImage && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.85 }}
                  className="relative w-10 h-10 rounded-lg overflow-hidden border border-cyan-400 shadow-[0_0_12px_rgba(6,182,212,0.5)]"
                >
                  <img src={pendingImage.dataUrl} alt="preview" className="w-full h-full object-cover" />
                  <button onClick={() => setPendingImage(null)} className="absolute inset-0 flex items-center justify-center bg-black/50">
                    <X size={10} className="text-white" />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => fileInputRef.current?.click()}
              className="w-11 h-11 rounded-full bg-white/5 border border-cyan-500/30 flex items-center justify-center text-cyan-400 hover:border-cyan-400 transition-all"
            >
              <Camera size={18} />
            </motion.button>
            {pendingImage && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
                whileTap={{ scale: 0.9 }}
                onClick={sendVisionMessage}
                className="px-4 h-11 rounded-full bg-cyan-500/20 border border-cyan-400 text-cyan-300 text-[10px] font-mono font-bold uppercase tracking-widest hover:bg-cyan-500/40 transition-all"
              >
                Analyze ▶
              </motion.button>
            )}
            <div className="text-[10px] font-mono text-white/30 uppercase tracking-widest ml-auto">
              Tap face to {isLunaConnected ? 'disconnect' : 'connect'}
            </div>
          </motion.div>
        </>
      )}
    </LunaErrorBoundary>
  );
});
